/**
 * FormPage Component
 * Main BRR form with 50+ fields organized in collapsible sections
 *
 * ERROR MESSAGE CONVENTION:
 * All error messages for missing/unavailable field data must follow the standardized format:
 *   "No {field name} found"
 * Examples: "No Marketing Authorisation found", "No API Name found", "No Valid To Date found"
 *
 * This pattern is detected by ValidatedFieldWithCompliance.isEmptyOrError() to automatically
 * mark fields as non-compliant when data is unavailable. New fields should follow this convention.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { CheckCircle, ChevronRight, X, ChevronDown, Shield, Building2, FileCheck, Package, Truck, AlertCircle, Calendar, FileText, AlertTriangle, CheckSquare, Square, ExternalLink, File, Upload, RefreshCw, Clock, Trash2, Database, Eye, Search, Loader2, Plus } from 'lucide-react';
import { AdditionalEvidenceItem, BRRData, BRRFormData, BRRLevel, FieldValue, FieldSource, FieldOverrides, ComplianceOverrides, BRRFormDataResponse, BRRReviewPayload, BoxArchiveInfo, BoxFileEntry, BoxFolderScanResult, BoxFolderInfo, BoxSubsectionKey, BoxSubsectionData, BoxLinkEntry, MaterialBoxFolders, ManualChangeControl, ManualQualityEvent, BRRFormDataChangeControl, STANDARD_DOC_SECTIONS, LEGACY_BOX_KEY_MAP, CustomDocumentSection, CP_INTERNAL_DOC_ITEMS, PackagingMaterialEntry, MaterialUsedEntry, MATERIAL_TYPE_OPTIONS, getComplianceOptionsForField, UserRole, isLegacyBoxData, migrateBoxSubsectionData, TraceLinkHierarchyLevel, ProgressStep } from '../services/types';
import { BRRApiClient, SessionAuditClient } from '../services';
import { useAutoSave, useBRRData, useValidatedFormData, useSession, useAuditLog, useQAACheck } from '../hooks';
import { ValidatedFieldWithCompliance, FormDisplayField, SearchableDropdown, ManufacturerDropdown, NOT_FOUND_VALUE, DeleteBRRButton } from '../components';
import { AddCardModal } from '../components/AddCardModal';
import { AddDocSectionModal } from '../components/AddDocSectionModal';
import { formatDate, formatDateTime, formatTime } from '../utils/formatDate';
import { formatNumber, formatBatchId } from '../utils/formatNumber';
import { SaveStatusIndicator } from '../components/SaveStatusIndicator';
import { useValidatedState } from '../contexts/ValidatedStateContext';
import { FEATURE_FLAGS } from '../config/featureFlags';

interface FormPageProps {
  batchId: string;
  inspectionLotId?: string | null;
  selectedLotIds?: string[];
  productName?: string | null;
  sessionId: string | null;
  userEmail: string;
  apiClient: BRRApiClient;
  sessionClient: SessionAuditClient;
  onNext: () => void;
  onCancel: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  fieldOverrides?: FieldOverrides;
  setFieldOverrides?: React.Dispatch<React.SetStateAction<FieldOverrides>>;
  complianceOverrides?: ComplianceOverrides;
  setComplianceOverrides?: React.Dispatch<React.SetStateAction<ComplianceOverrides>>;
  onDataChange?: (data: any) => void;
  useRealData?: boolean;
  additionalEvidenceItems?: AdditionalEvidenceItem[];
  setAdditionalEvidenceItems?: React.Dispatch<React.SetStateAction<AdditionalEvidenceItem[]>>;
  batchType?: 'direct' | 'indirect';
  priority?: 'low' | 'medium' | 'high';
  userRole?: UserRole;
  priorityLockedBy?: string | null;
  onPriorityChange?: (priority: 'low' | 'medium' | 'high') => void;
  brrLevel?: BRRLevel;
  startPageCountry?: string | null;
  orchestratorBoxArchive?: BoxArchiveInfo | null;
  isReadOnly?: boolean;
  onDeleteBRR?: () => Promise<void>;
  blockNavigation?: (message: string) => void;
  unblockNavigation?: (error?: string | null) => void;
  isNavigationBlocked?: boolean;
  startProgressOperation?: (operationId: string, steps: ProgressStep[], title?: string) => void;
  advanceProgressStep?: (stepLabel?: string) => void;
  isActive?: boolean;
}

interface Document {
  id: string;
  name: string;
  type?: string;
  size?: string;
  uploadTime?: string;
  blobName?: string;
  url?: string;
}

// File upload constraints
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// Progress tracking step definitions - MUST be outside component to avoid re-creation on every render
const FORM_PAGE_LOAD_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing...' },
  { name: 'getSavedReview', label: 'Loading saved review...', weight: 2 },
  { name: 'restoreState', label: 'Restoring form state...' },
  { name: 'complete', label: 'Finalizing...' },
];

const FORM_PAGE_SAVE_STEPS: ProgressStep[] = [
  { name: 'prepare', label: 'Preparing to save...' },
  { name: 'buildPayload', label: 'Building save data...' },
  { name: 'saveToDb', label: 'Saving to database...', weight: 3 },
  { name: 'updateStatus', label: 'Updating save status...' },
  { name: 'complete', label: 'Save complete' },
];

/**
 * Maps DocumentValidation extractionKeys to FormPage fieldIds.
 * DocumentValidation uses table-based structure: { COC: { destination_country: "Albania" } }
 * FormPage uses flat fieldId structure: { destinationCountry: { value: "Albania", ... } }
 */
const EXTRACTION_KEY_TO_FIELD_ID: Record<string, string> = {
  'product_name': 'productName',
  'destination_country': 'destinationCountry',
  'dosage_form': 'dosageForm',
  'strength': 'doseStrength',
  'package_size_and_type': 'packSize',
  'quantity': 'batchQuantity',
  'fp_batch_quantity': 'batchQuantity',
  'marketing_authorisation_number': 'marketAuthorisationNumber',
  'sap_material_number': 'materialSKU',
  'reported_shelf_life': 'reportedShelfLife',
  'storage_statement': 'storageStatement',
  'api_name': 'apiName',
  'authorisation_status': 'authorisationStatus',
  'validity_period_end_value': 'validityPeriodEndValue',
  'cmo_fp_batch_id': 'cmoFpBatchId',
  'cmo_bulk_batch_id': 'cmoBulkBatchId',
  'manufacturing_date': 'batchManufactureDate',
  'expiry_date': 'batchExpiryDate',
  'storage_location': 'targetStorageLocation',
};

/**
 * Extracts field values from DocumentValidation tablesSnapshot.
 * This captures the initial extracted values from documents (before any user edits).
 * These are the values displayed in the "BRR Field Value" column if no override exists.
 *
 * @param tablesSnapshot - Full validation tables snapshot from DocumentValidation
 * @returns Flat fieldOverrides structure { fieldId: { value, originalValue } }
 */
function extractFieldsFromTablesSnapshot(
  tablesSnapshot: import('../services/types').ValidationPageData
): FieldOverrides {
  const fieldOverrides: FieldOverrides = {};

  if (!tablesSnapshot?.tables) {
    return fieldOverrides;
  }

  for (const [tableKey, tableData] of Object.entries(tablesSnapshot.tables)) {
    if (!tableData || !tableData.rows) continue;

    for (const row of tableData.rows) {
      const extractionKey = row.extraction_key;
      const extractedValue = row.extracted_value;

      // Skip if no extracted value
      if (!extractedValue) continue;

      const fieldId = EXTRACTION_KEY_TO_FIELD_ID[extractionKey];
      if (fieldId && !fieldOverrides[fieldId]) {
        // Only set if not already set (first table's value wins)
        fieldOverrides[fieldId] = {
          value: extractedValue,
          originalValue: extractedValue, // Original is the same as extracted for non-overridden values
        };
      }
    }
  }

  return fieldOverrides;
}

/**
 * Converts DocumentValidation overrides to FormPage fieldOverrides format.
 * This ensures manual edits made on DocumentValidationPage persist when resuming a draft.
 *
 * @param docValidationOverrides - Table-based overrides from DocumentValidation { tableKey: { extractionKey: value } }
 * @returns Flat fieldOverrides structure { fieldId: { value, originalValue } }
 */
function convertDocValidationOverridesToFieldOverrides(
  docValidationOverrides: Record<string, Record<string, string>>
): FieldOverrides {
  const fieldOverrides: FieldOverrides = {};

  for (const tableKey of Object.keys(docValidationOverrides)) {
    const tableOverrides = docValidationOverrides[tableKey];
    for (const extractionKey of Object.keys(tableOverrides)) {
      const fieldId = EXTRACTION_KEY_TO_FIELD_ID[extractionKey];
      if (fieldId && !fieldOverrides[fieldId]) {
        // Only set if not already set (first table's value wins, or brrForm overrides later)
        fieldOverrides[fieldId] = {
          value: tableOverrides[extractionKey],
          originalValue: '', // Original value not available from DocumentValidation structure
        };
      }
    }
  }

  return fieldOverrides;
}

const FormPageComponent: React.FC<FormPageProps> = ({
  batchId,
  inspectionLotId,
  selectedLotIds,
  productName: productNameProp,
  sessionId,
  userEmail,
  apiClient,
  sessionClient,
  onNext,
  onCancel,
  onDirtyChange,
  fieldOverrides: fieldOverridesProp,
  setFieldOverrides: setFieldOverridesProp,
  complianceOverrides: complianceOverridesProp,
  setComplianceOverrides: setComplianceOverridesProp,
  onDataChange,
  useRealData = false,
  additionalEvidenceItems: additionalEvidenceItemsProp,
  setAdditionalEvidenceItems: setAdditionalEvidenceItemsProp,
  batchType = 'direct',
  priority = 'medium',
  userRole = 'viewer',
  priorityLockedBy = null,
  onPriorityChange,
  brrLevel: brrLevelProp = 'level1',
  startPageCountry = null,
  orchestratorBoxArchive = null,
  isReadOnly = false,
  onDeleteBRR,
  blockNavigation,
  unblockNavigation,
  isNavigationBlocked,
  startProgressOperation,
  advanceProgressStep,
  isActive,
}) => {
  // Feature flag: Controlled via centralized config (src/config/featureFlags.ts)
  const { ENABLE_SAVE_FUNCTIONALITY } = FEATURE_FLAGS;

  const isAdmin = userRole === 'admin';

  // Ref because the upload callbacks are declared above the autoPopulatedBRR useMemo that resolves this
  const boxFolderIdFromProps = orchestratorBoxArchive?.folder_id || null;
  const boxFolderIdRef = useRef<string | null>(boxFolderIdFromProps);

  // Load BRR data with validated state from Document Validation
  // This merges validated field values and source metadata into the API response
  // validatedFormData includes user edits from Document Validation page
  // sessionId enables session-scoped document filtering (only see documents from this BRR session)
  // selectedLotIds enables per-lot SAP quantities for multi-lot BRRs
  const { data: validatedFormData, loading: validatedLoading, error: validatedError } = useValidatedFormData({
    inspectionLotId: inspectionLotId ?? null,
    productName: productNameProp ?? undefined,
    sessionId: sessionId,
    selectedLotIds: selectedLotIds,
    apiClient,
    autoLoad: useRealData && !!inspectionLotId // Only load if we have an inspection lot ID and useRealData is true
  });

  // Load BRR data (skip API in test harness - will use mock data)
  const { data: brrData, loading, error, updateData } = useBRRData({
    batchId,
    apiClient,
    autoLoad: false // Disabled for test harness
  });

  // Session management
  const { updateLastSaved } = useSession({
    batchId,
    sessionClient,
    autoCreate: false // Session already created
  });

  // Auto-save (disabled in test harness)
  const { isSaving, lastSaved } = useAutoSave({
    batchId,
    formData: {} as any,
    apiClient,
    debounceMs: 2000,
    enabled: false, // Disabled for test harness
    onSaved: () => {
      void updateLastSaved();
    }
  });

  // Audit logging
  const { logSave } = useAuditLog({
    batchId,
    sessionId,
    sessionClient
  });

  // Validated state context for bidirectional navigation
  const { updateFields: updateValidatedFields } = useValidatedState();

  const brrLevel = brrLevelProp;

  // BRR Creation Basis state - multiple selection
  const [brrCreationBasis, setBrrCreationBasis] = useState<string[]>(['complete-sample']);

  // CP Internal Documents checklist
  const [cpInternalDocs, setCpInternalDocs] = useState<string[]>([]);

  // Save state tracking
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved'
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [hasSavedOnce, setHasSavedOnce] = useState(false);

  // Loading state for saved data from Neo4j
  const [savedStateLoading, setSavedStateLoading] = useState(true);

  // Non-validated fields dropdown state
  const [showNonValidatedDropdown, setShowNonValidatedDropdown] = useState(false);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);

  // Document state
  const [startBRRDocuments, setStartBRRDocuments] = useState<Document[] | null>(null);
  const [isDraggingEvidence, setIsDraggingEvidence] = useState<boolean>(false);

  // File upload state
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploadingEvidence, setIsUploadingEvidence] = useState<boolean>(false);
  const [uploadEvidenceError, setUploadEvidenceError] = useState<string>('');
  const [uploadEvidenceProgress, setUploadEvidenceProgress] = useState<number>(0);

  // Unified additional evidence items (PDF uploads + Box folder references)
  const [localAdditionalEvidenceItems, setLocalAdditionalEvidenceItems] = useState<AdditionalEvidenceItem[]>([]);
  const additionalEvidenceItems = additionalEvidenceItemsProp ?? localAdditionalEvidenceItems;
  const setAdditionalEvidenceItems = setAdditionalEvidenceItemsProp ?? setLocalAdditionalEvidenceItems;

  // Tab state for switching between PDF upload and Box link modes
  const [evidenceTab, setEvidenceTab] = useState<'pdf' | 'boxLink'>('pdf');

  // Form fields for adding/editing evidence items
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemBoxLink, setNewItemBoxLink] = useState('');
  const [itemError, setItemError] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // Pending PDF upload (staged before adding title/description)
  const [pendingPdfUpload, setPendingPdfUpload] = useState<{
    fileName: string;
    fileSize: string;
    uploadTime: string;
    boxFileId: string;
  } | null>(null);

  // Manual SAP entry confirmation
  const [manualSAPEntry, setManualSAPEntry] = useState<boolean>(false);

  // Country-specific requirements free text
  const [countrySpecificReqsText, setCountrySpecificReqsText] = useState<string>('');

  // GMP Annex 16 Batch Release Site confirmation
  const [batchReleaseSiteText, setBatchReleaseSiteText] = useState<string>('');
  const [batchReleaseSiteConfirmed, setBatchReleaseSiteConfirmed] = useState<boolean>(false);
  const [batchReleaseSiteCheplapharmConfirmed, setBatchReleaseSiteCheplapharmConfirmed] = useState<boolean>(false);
  const [selectedReleaseSites, setSelectedReleaseSites] = useState<string[]>([]);

  // Materials Used state (SAP lineage + manual entries)
  const [materialsUsed, setMaterialsUsed] = useState<MaterialUsedEntry[]>([]);
  const [showAddMaterialForm, setShowAddMaterialForm] = useState(false);
  const [newMaterialEntry, setNewMaterialEntry] = useState({ material: '', vendorBatchId: '' });

  // Packaging Materials (KAU) state
  const [packagingMaterials, setPackagingMaterials] = useState<PackagingMaterialEntry[]>([]);
  const [showAddPackagingForm, setShowAddPackagingForm] = useState(false);
  const [newPackagingEntry, setNewPackagingEntry] = useState({
    componentType: '',
    cpMaterialNumber: '',
    cmoMaterialNumber: '',
    versionNumber: '',
  });

  // Saved auto-populated data from Neo4j (for cloned BRRs that don't have live API data)
  const [savedAutoPopulatedData, setSavedAutoPopulatedData] = useState<BRRFormData | null>(null);

  // Track if this is a cloned session (affects data loading behavior)
  const [isClonedSession, setIsClonedSession] = useState(false);

  // Box folder link state — per-subsection (now supports multiple entries per section)
  interface BoxSectionState {
    entries: BoxLinkEntry[];           // Certified entries
    pendingUrl: string;                // URL being entered for new link
    pendingUrlError: string;
    pendingVersion: string;            // Version for new link
    pendingFreeText: string;           // FreeText for new link
    loading: boolean;
    scanResult: BoxFolderScanResult | null;
    folderInfo: BoxFolderInfo | null;
    showLargeWarning: boolean;
    scanElapsed: number;
    resolvedFolderId: string;
    showAddForm: boolean;              // Show the add new link form
    editingEntryId: string | null;     // Which entry is being edited (null = none)
    editingUrl: string;                // URL for entry being edited
    scanningEntryId: string | null;    // Which entry is being scanned for changes
  }
  const defaultBoxState: BoxSectionState = {
    entries: [],
    pendingUrl: '', pendingUrlError: '', pendingVersion: '', pendingFreeText: '',
    loading: false, scanResult: null,
    folderInfo: null, showLargeWarning: false, scanElapsed: 0,
    resolvedFolderId: '',
    showAddForm: false, editingEntryId: null, editingUrl: '',
    scanningEntryId: null,
  };
  const [boxStates, setBoxStates] = useState<Record<BoxSubsectionKey, BoxSectionState>>({
    specsApi: { ...defaultBoxState },
    primaryPackagingMaterials: { ...defaultBoxState },
    manufacturingInstructions: { ...defaultBoxState },
    packagingInstructions: { ...defaultBoxState },
    releaseTestInstructions: { ...defaultBoxState },
    releaseSpecifications: { ...defaultBoxState },
  });
  const scanTimerRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  // Document section data (version + freeText) - still used for custom sections and legacy compatibility
  const [documentSectionData, setDocumentSectionData] = useState<Record<string, { version: string; freeText: string }>>(() => {
    const initial: Record<string, { version: string; freeText: string }> = {};
    STANDARD_DOC_SECTIONS.forEach(({ key }) => { initial[key] = { version: '', freeText: '' }; });
    return initial;
  });
  const [customDocSections, setCustomDocSections] = useState<CustomDocumentSection[]>([]);
  const [showAddDocSectionModal, setShowAddDocSectionModal] = useState(false);

  // Form state
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'basicInfo',
    'materialsUsed',
    'supplierStatus',
    'approvalDetails',
    'changeControl',
    'batchDates',
    'serializationAggregation'
  ]);

  // Serialization & Aggregation section state
  const [serializationRequirement, setSerializationRequirement] = useState<string>('');
  const [aggregationRequirement, setAggregationRequirement] = useState<string>('');

  // TraceLink lot status state
  const [traceLinkData, setTraceLinkData] = useState<Record<string, string> | null>(null);
  const [traceLinkFound, setTraceLinkFound] = useState<boolean | null>(null);
  const [traceLinkNotFoundMessage, setTraceLinkNotFoundMessage] = useState<string | null>(null);
  const [traceLinkLoading, setTraceLinkLoading] = useState(false);
  const [traceLinkError, setTraceLinkError] = useState<string | null>(null);

  // TraceLink hierarchy levels state
  const [hierarchyLevels, setHierarchyLevels] = useState<TraceLinkHierarchyLevel[] | null>(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState<string | null>(null);

  // Use props if provided, otherwise use local state
  const [localFieldOverrides, setLocalFieldOverrides] = useState<FieldOverrides>({});
  const fieldOverrides = fieldOverridesProp ?? localFieldOverrides;
  const setFieldOverrides = setFieldOverridesProp ?? setLocalFieldOverrides;

  const [localComplianceOverrides, setLocalComplianceOverrides] = useState<ComplianceOverrides>({});
  const complianceOverrides = complianceOverridesProp ?? localComplianceOverrides;
  const setComplianceOverrides = setComplianceOverridesProp ?? setLocalComplianceOverrides;

  // Save Form state to validated context before unmounting (bidirectional navigation)
  useEffect(() => {
    return () => {
      // On unmount, save current form overrides to validated state
      // This ensures Form → Validation → Form preserves user edits
      if (fieldOverrides && Object.keys(fieldOverrides).length > 0) {
        const fieldsToSave: Record<string, import('../services/types').FieldValue> = {};

        Object.entries(fieldOverrides).forEach(([fieldId, override]) => {
          fieldsToSave[fieldId] = {
            value: override.value,
            source: 'Manual',
            override: true
          };
        });

        updateValidatedFields(fieldsToSave);
      }
    };
  }, [fieldOverrides, updateValidatedFields]);

  // Track previous sessionId to detect changes
  const previousSessionIdRef = useRef<string | null>(null);

  // Load saved state from Neo4j on mount or session change
  useEffect(() => {
    let isMounted = true;

    const loadSavedState = async () => {
      const effectiveSessionId = (!sessionId && !useRealData)
        ? null
        : sessionId;

      if (!effectiveSessionId || !batchId) {
        // Keep loading if we expect params to arrive (useRealData mode)
        if (!useRealData && isMounted) {
          setSavedStateLoading(false);
        }
        return;
      }

      // Set loading state to true when starting to load (ensures loading indicator shows
      // when navigating back to this page, not just on initial mount)
      if (isMounted) {
        setSavedStateLoading(true);
      }

      // Block navigation while loading saved state (with progress tracking)
      startProgressOperation?.('formPage_load', FORM_PAGE_LOAD_STEPS, 'Loading Batch Record Review');
      advanceProgressStep?.('Preparing...');
      blockNavigation?.('Loading Batch Record Review...');

      // Clear form state when session changes (prevents stale data)
      const sessionHasChanged =
        previousSessionIdRef.current &&
        previousSessionIdRef.current !== effectiveSessionId;

      if (sessionHasChanged && isMounted) {
        // Note: brrLevel is now controlled by parent (StartBRRPage), not cleared here
        setBrrCreationBasis(['complete-sample']);
        setManualSAPEntry(false);
        setNextPeriodicReview('');
        setChangeControlNotesArray([]);
        setIndividualCCNotes({});
        setCountrySpecificReqsText('');
        setBatchReleaseSiteText('');
        setBatchReleaseSiteCheplapharmConfirmed(false);
        setSelectedReleaseSites([]);
        setBoxStates({
          specsApi: { ...defaultBoxState },
          primaryPackagingMaterials: { ...defaultBoxState },
          manufacturingInstructions: { ...defaultBoxState },
          packagingInstructions: { ...defaultBoxState },
          releaseTestInstructions: { ...defaultBoxState },
          releaseSpecifications: { ...defaultBoxState },
        });
      }

      previousSessionIdRef.current = effectiveSessionId;

      try {
        advanceProgressStep?.('Loading saved review...');
        const savedReview = await apiClient.getSavedReview(batchId, effectiveSessionId);

        advanceProgressStep?.('Restoring form state...');

        if (!isMounted) return;

        // Detect if this is a cloned session (affects data loading behavior)
        if (savedReview?.clonedFromSessionId) {
          setIsClonedSession(true);
        }

        let restoredPackagingFromSave = false;
        let restoredMaterialsUsedFromSave = false;
        if (savedReview && savedReview.brrForm) {
          const pageData = savedReview.brrForm;

          // Note: brrLevel is now controlled by parent (StartBRRPage), not restored here

          // Restore BRR creation basis
          if (pageData.brrCreationBasis !== undefined) {
            setBrrCreationBasis(pageData.brrCreationBasis);
          }

          // Restore CP Internal Documents checklist
          if (pageData.cpInternalDocs !== undefined) {
            setCpInternalDocs(pageData.cpInternalDocs);
          }

          // Restore compliance overrides and sync section-level compliance states
          if (pageData.complianceOverrides !== undefined) {
            const co = pageData.complianceOverrides;
            setComplianceOverrides(co);

            // Sync section-level compliance states from complianceOverrides
            // BasicInfo section fields (productName, materialDescription, etc.)
            const basicInfoFields = ['productName', 'materialDescription', 'destinationCountry',
              'packSize', 'dosageForm', 'doseStrength', 'materialSKU', 'batchQuantity'];
            const basicInfoUpdates: Record<string, boolean> = {};
            basicInfoFields.forEach(field => {
              if (co[field] !== undefined) {
                basicInfoUpdates[field] = isCompliantValue(co[field]);
              }
            });
            if (Object.keys(basicInfoUpdates).length > 0) {
              setBasicInfoCompliance(prev => ({ ...prev, ...basicInfoUpdates }));
            }

            setCmoSupplierCompliance(prev => ({
              ...prev,
              cmoName: co.cmoName !== undefined ? isCompliantValue(co.cmoName) : prev.cmoName,
              cmoQualStatus: co.cmoQualStatus !== undefined ? isCompliantValue(co.cmoQualStatus) : prev.cmoQualStatus,
              supplierName: co.supplierName !== undefined ? isCompliantValue(co.supplierName) : prev.supplierName,
              cmoComplianceReviewDate: co.cmoComplianceReviewDate !== undefined ? isCompliantValue(co.cmoComplianceReviewDate) : prev.cmoComplianceReviewDate,
            }));

            setApprovalDetailsCompliance(prev => ({
              ...prev,
              marketAuthorisationNumber: co.marketAuthorisationNumber !== undefined ? isCompliantValue(co.marketAuthorisationNumber) : prev.marketAuthorisationNumber,
              apiName: co.apiName !== undefined ? isCompliantValue(co.apiName) : prev.apiName,
              apiManufacturer: co.apiManufacturer !== undefined ? isCompliantValue(co.apiManufacturer) : prev.apiManufacturer,
              primaryPackaging: co.primaryPackaging !== undefined ? isCompliantValue(co.primaryPackaging) : prev.primaryPackaging,
              bulkManufacturer: co.bulkManufacturer !== undefined ? isCompliantValue(co.bulkManufacturer) : prev.bulkManufacturer,
              qcTestingSite: co.qcTestingSite !== undefined ? isCompliantValue(co.qcTestingSite) : prev.qcTestingSite,
              primaryPackagingSite: co.primaryPackagingSite !== undefined ? isCompliantValue(co.primaryPackagingSite) : prev.primaryPackagingSite,
              secondaryPackagingSite: co.secondaryPackagingSite !== undefined ? isCompliantValue(co.secondaryPackagingSite) : prev.secondaryPackagingSite,
              releaseSite: co.releaseSite !== undefined ? isCompliantValue(co.releaseSite) : prev.releaseSite,
            }));

            setQaaStatusCompliance(prev => ({
              ...prev,
              qaaStatus: co.qaaStatus !== undefined ? isCompliantValue(co.qaaStatus) : prev.qaaStatus,
            }));

            setStorageInfoCompliance(prev => ({
              ...prev,
              reportedShelfLife: co.reportedShelfLife !== undefined ? isCompliantValue(co.reportedShelfLife) : prev.reportedShelfLife,
              storageStatement: co.storageStatement !== undefined ? isCompliantValue(co.storageStatement) : prev.storageStatement,
            }));

            setCountryRequirementsCompliance(prev => ({
              ...prev,
              targetCountry: co.targetCountry !== undefined ? isCompliantValue(co.targetCountry) : prev.targetCountry,
              mraACAA: co.mraACAA !== undefined ? isCompliantValue(co.mraACAA) : prev.mraACAA,
              countrySpecificReqs: co.countrySpecificReqs !== undefined ? isCompliantValue(co.countrySpecificReqs) : prev.countrySpecificReqs,
            }));

            setSampleDocsCompliance(prev => ({
              ...prev,
              specsApi: co.specsApi !== undefined ? isCompliantValue(co.specsApi) : (co.specifications !== undefined ? isCompliantValue(co.specifications) : prev.specsApi),
              primaryPackagingMaterials: co.primaryPackagingMaterials !== undefined ? isCompliantValue(co.primaryPackagingMaterials) : (co.packagingMaterialApproval !== undefined ? isCompliantValue(co.packagingMaterialApproval) : prev.primaryPackagingMaterials),
              manufacturingInstructions: co.manufacturingInstructions !== undefined ? isCompliantValue(co.manufacturingInstructions) : prev.manufacturingInstructions,
              packagingInstructions: co.packagingInstructions !== undefined ? isCompliantValue(co.packagingInstructions) : prev.packagingInstructions,
              releaseTestInstructions: co.releaseTestInstructions !== undefined ? isCompliantValue(co.releaseTestInstructions) : prev.releaseTestInstructions,
              releaseSpecifications: co.releaseSpecifications !== undefined ? isCompliantValue(co.releaseSpecifications) : prev.releaseSpecifications,
            }));

            setDeviationsOosCompliance(prev => ({
              ...prev,
              deviations: co.deviations !== undefined ? isCompliantValue(co.deviations) : prev.deviations,
            }));

            setBatchDatesCompliance(prev => ({
              ...prev,
              batchManufactureDate: co.batchManufactureDate !== undefined ? isCompliantValue(co.batchManufactureDate) : prev.batchManufactureDate,
              batchExpiryDate: co.batchExpiryDate !== undefined ? isCompliantValue(co.batchExpiryDate) : prev.batchExpiryDate,
              ...(co.calculatedExpiryDate !== undefined ? { calculatedExpiryDate: isCompliantValue(co.calculatedExpiryDate) } : {}),
            }));

            // Restore any dynamic change control compliance entries (cc_ prefix)
            const ccKeys = Object.keys(co).filter(k => k.startsWith('cc_'));
            if (ccKeys.length > 0) {
              const ccCompliance: Record<string, boolean> = {};
              ccKeys.forEach(k => {
                ccCompliance[k] = isCompliantValue(co[k]);
              });
              setChangeControlCompliance(ccCompliance);
            }

            // Restore any dynamic quality event compliance entries (qe_ prefix)
            const qeKeys = Object.keys(co).filter(k => k.startsWith('qe_'));
            if (qeKeys.length > 0) {
              const qeCompliance: Record<string, boolean> = {};
              qeKeys.forEach(k => {
                qeCompliance[k] = isCompliantValue(co[k]);
              });
              setDeviationsOosCompliance(qeCompliance);
            }

            // Restore any dynamic materials used compliance entries (materialsUsed_ prefix)
            const muKeys = Object.keys(co).filter(k => k.startsWith('materialsUsed_'));
            if (muKeys.length > 0) {
              const muCompliance: Record<string, boolean> = {};
              muKeys.forEach(k => {
                muCompliance[k] = isCompliantValue(co[k]);
              });
              setMaterialsUsedCompliance(muCompliance);
            }
          }

          // Restore field overrides with three-tier merge:
          // 1. Extract initial values from tablesSnapshot (lowest priority - raw extracted data)
          // 2. Apply DocumentValidation overrides (user edits from Document Validation page)
          // 3. Apply FormPage's own overrides (highest priority - user edits from Form page)
          let mergedFieldOverrides: FieldOverrides = {};

          // First, extract initial values from tablesSnapshot (if available)
          // This captures extracted values even when user didn't explicitly click to select them
          if (savedReview.documentValidation?.tablesSnapshot) {
            const extractedFields = extractFieldsFromTablesSnapshot(
              savedReview.documentValidation.tablesSnapshot
            );
            mergedFieldOverrides = { ...extractedFields };
          }

          // Second, apply DocumentValidation overrides (user edits - takes precedence over extracted)
          if (savedReview.documentValidation?.overrides) {
            const docValidationOverrides = convertDocValidationOverridesToFieldOverrides(
              savedReview.documentValidation.overrides
            );
            mergedFieldOverrides = { ...mergedFieldOverrides, ...docValidationOverrides };
          }

          // Third, apply FormPage's own overrides (highest priority)
          if (pageData.fieldOverrides !== undefined) {
            mergedFieldOverrides = { ...mergedFieldOverrides, ...pageData.fieldOverrides };
          }

          if (Object.keys(mergedFieldOverrides).length > 0) {
            setFieldOverrides(mergedFieldOverrides);
          }

          // Restore country-specific requirements text
          if (pageData.countrySpecificRequirements !== undefined) {
            setCountrySpecificReqsText(pageData.countrySpecificRequirements);
          }

          // Restore GMP Annex 16 batch release site confirmation
          if (pageData.batchReleaseSite !== undefined) {
            setBatchReleaseSiteText(pageData.batchReleaseSite);
          }
          if (pageData.batchReleaseSiteConfirmed !== undefined) {
            setBatchReleaseSiteConfirmed(pageData.batchReleaseSiteConfirmed);
          }
          if (pageData.batchReleaseSiteCheplapharmConfirmed !== undefined) {
            setBatchReleaseSiteCheplapharmConfirmed(pageData.batchReleaseSiteCheplapharmConfirmed);
          }
          if (pageData.selectedReleaseSites !== undefined) {
            setSelectedReleaseSites(pageData.selectedReleaseSites);
          }

          // Restore manual SAP entry
          if (pageData.manualSAPEntry !== undefined) {
            setManualSAPEntry(pageData.manualSAPEntry);
          }

          // Restore next periodic review
          if (pageData.nextPeriodicReview !== undefined) {
            setNextPeriodicReview(pageData.nextPeriodicReview);
          }

          // Restore change control notes
          if (pageData.changeControlNotes !== undefined) {
            setChangeControlNotesArray(pageData.changeControlNotes);
          }

          // Restore individual CC notes (keyed by stableId string)
          if (pageData.individualCCNotes !== undefined) {
            setIndividualCCNotes(pageData.individualCCNotes);
          }

          // Restore box folder URLs (now stored as BoxSubsectionData with entries array)
          if (pageData.boxFolderUrls !== undefined) {
            setBoxStates(prev => {
              const updated = { ...prev };
              (Object.keys(pageData.boxFolderUrls!) as BoxSubsectionKey[]).forEach(key => {
                if (updated[key]) {
                  const sectionData = pageData.boxFolderUrls![key];
                  if (sectionData && sectionData.entries) {
                    updated[key] = { ...updated[key], entries: sectionData.entries };
                  }
                }
              });
              return updated;
            });
          }

          // Restore auto-populated data from saved state (for cloned BRRs)
          if (pageData.autoPopulatedData !== undefined) {
            setSavedAutoPopulatedData(pageData.autoPopulatedData as BRRFormData);
          }

          // Restore manually added / removed change controls and quality events
          if (pageData.manuallyAddedCCs !== undefined) {
            setManuallyAddedCCs(pageData.manuallyAddedCCs);
          }
          if (pageData.manuallyAddedQEs !== undefined) {
            setManuallyAddedQEs(pageData.manuallyAddedQEs);
          }
          if (pageData.removedCCIds !== undefined) {
            setRemovedCCIds(new Set(pageData.removedCCIds));
          }
          if (pageData.removedQEIds !== undefined) {
            setRemovedQEIds(new Set(pageData.removedQEIds));
          }

          // Restore document section data (version + freeText per section)
          if (pageData.documentSectionData !== undefined) {
            setDocumentSectionData(prev => ({ ...prev, ...pageData.documentSectionData }));
          }

          // Restore custom document sections
          if (pageData.customDocumentSections !== undefined) {
            setCustomDocSections(pageData.customDocumentSections);
          }

          // Restore packaging materials (saved manual + extracted entries)
          // IMPORTANT: If DocumentValidation was saved more recently than BRRForm, we should
          // re-extract from DocumentValidation to pick up any user edits made after FormPage was saved.
          // This handles the case where user goes back to DocVal, edits packaging fields, then returns.
          const brrFormSavedAt = pageData.savedAt ? new Date(pageData.savedAt).getTime() : 0;
          const docValSavedAt = savedReview?.documentValidation?.validatedAt
            ? new Date(savedReview.documentValidation.validatedAt).getTime()
            : 0;
          const docValIsNewer = docValSavedAt > brrFormSavedAt;

          if (pageData.packagingMaterials && pageData.packagingMaterials.length > 0 && !docValIsNewer) {
            // Use saved BRRForm data only if DocumentValidation hasn't been updated since
            restoredPackagingFromSave = true;
            setPackagingMaterials(pageData.packagingMaterials);
          }

          // Restore materials used (saved extracted + manual entries)
          if (pageData.materialsUsed && pageData.materialsUsed.length > 0) {
            restoredMaterialsUsedFromSave = true;
            setMaterialsUsed(pageData.materialsUsed);
            // Set default compliance (compliant) for any materials without saved compliance
            const savedCompliance = pageData.complianceOverrides || {};
            const defaultMuCompliance: Record<string, boolean> = {};
            pageData.materialsUsed.forEach((mat: { id?: string }) => {
              const fieldId = `materialsUsed_${mat.id}`;
              if (savedCompliance[fieldId] === undefined) {
                defaultMuCompliance[fieldId] = true; // Default to compliant
              }
            });
            if (Object.keys(defaultMuCompliance).length > 0) {
              setMaterialsUsedCompliance(prev => ({ ...prev, ...defaultMuCompliance }));
            }
          }

          // Mark as saved if we loaded data and snapshot the restored state
          if (pageData.savedAt) {
            setLastSavedTime(new Date(pageData.savedAt));
            setHasSavedOnce(true);
            setLastSavedSnapshot(JSON.stringify({
              brrLevel: pageData.brrLevel ?? 'level1',
              brrCreationBasis: pageData.brrCreationBasis ?? ['complete-sample'],
              manualSAPEntry: pageData.manualSAPEntry ?? false,
              nextPeriodicReview: pageData.nextPeriodicReview ?? '',
              changeControlNotesArray: pageData.changeControlNotes ?? [],
              individualCCNotes: pageData.individualCCNotes ?? {},
              countrySpecificReqsText: pageData.countrySpecificRequirements ?? '',
              fieldOverrides: pageData.fieldOverrides ?? {},
              complianceOverrides: pageData.complianceOverrides ?? {},
              manuallyAddedCCs: pageData.manuallyAddedCCs ?? [],
              manuallyAddedQEs: pageData.manuallyAddedQEs ?? [],
              removedCCIdsArray: (pageData.removedCCIds ?? []).sort(),
              removedQEIdsArray: (pageData.removedQEIds ?? []).sort(),
              documentSectionData: pageData.documentSectionData ?? {},
              customDocumentSections: pageData.customDocumentSections ?? [],
              cpInternalDocs: pageData.cpInternalDocs ?? [],
              packagingMaterials: pageData.packagingMaterials ?? [],
              materialsUsed: pageData.materialsUsed ?? [],
              batchReleaseSiteConfirmed: pageData.batchReleaseSiteConfirmed ?? false,
              batchReleaseSiteCheplapharmConfirmed: pageData.batchReleaseSiteCheplapharmConfirmed ?? false,
              selectedReleaseSites: pageData.selectedReleaseSites ?? [],
              batchReleaseSiteText: pageData.batchReleaseSite ?? '',
            }));
          }
        } else if (savedReview?.documentValidation?.tablesSnapshot || savedReview?.documentValidation?.overrides) {
          // No brrForm saved yet, but DocumentValidation has data
          // Apply extracted values and overrides to ensure they persist
          let mergedOverrides: FieldOverrides = {};

          // First, extract initial values from tablesSnapshot
          if (savedReview.documentValidation?.tablesSnapshot) {
            const extractedFields = extractFieldsFromTablesSnapshot(
              savedReview.documentValidation.tablesSnapshot
            );
            mergedOverrides = { ...extractedFields };
          }

          // Then, apply user overrides (takes precedence)
          if (savedReview.documentValidation?.overrides) {
            const docValidationOverrides = convertDocValidationOverridesToFieldOverrides(
              savedReview.documentValidation.overrides
            );
            mergedOverrides = { ...mergedOverrides, ...docValidationOverrides };
          }

          if (Object.keys(mergedOverrides).length > 0) {
            setFieldOverrides(mergedOverrides);
          }
        }

        // Extract packaging materials from document validation:
        // 1. From tablesSnapshot (uploaded/extracted documents) - apply overrides for user edits
        // 2. From manualSections + overrides (Manual packaging scans)
        // Skip if we already restored saved data (which contains user edits)
        if (!restoredPackagingFromSave && savedReview?.documentValidation) {
          const extractedMaterials: PackagingMaterialEntry[] = [];
          const docVal = savedReview.documentValidation;
          const overrides = docVal.overrides || {};
          const SUBTYPE_LABELS: Record<string, string> = {
            LABEL: 'Labels', FOIL: 'Foils', LEAFLET: 'Leaflet',
            FOLDING_BOX: 'Folding Box', PRINTED_PACKAGING: 'Printed Packaging Materials',
            PACKAGING: 'Packaging',
          };

          // Helper to get value with override precedence
          const getValueWithOverride = (
            tableKey: string,
            rows: Array<Record<string, string | null>>,
            snakeKey: string,
            camelKey: string
          ): string => {
            // First check overrides (user edits take precedence)
            const tableOverrides = overrides[tableKey];
            if (tableOverrides) {
              if (tableOverrides[snakeKey] !== undefined) return tableOverrides[snakeKey];
              if (tableOverrides[camelKey] !== undefined) return tableOverrides[camelKey];
            }
            // Fall back to extracted value from rows
            for (const r of rows) {
              const rKey = r.extraction_key ?? r.extractionKey ?? '';
              if (rKey === snakeKey || rKey === camelKey) {
                return r.extracted_value ?? r.extractedValue ?? '';
              }
            }
            return '';
          };

          // 1. Extract from tablesSnapshot (uploaded/extracted documents)
          if (docVal.tablesSnapshot?.tables) {
            Object.entries(docVal.tablesSnapshot.tables).forEach(([tableKey, tableData]) => {
              if (tableKey.startsWith('PACKAGING_MATERIALS') && tableData) {
                const table = tableData as unknown as Record<string, unknown>;
                const rows = (table.rows || []) as Array<Record<string, string | null>>;
                const subtypeMatch = tableKey.match(/^PACKAGING_MATERIALS__(.+?)(?:_\d+)?$/);
                const subtypeFallback = subtypeMatch ? (SUBTYPE_LABELS[subtypeMatch[1]] || subtypeMatch[1]) : '';
                extractedMaterials.push({
                  id: `extracted_${tableKey}`,
                  componentType: getValueWithOverride(tableKey, rows, 'packaging_type', 'packagingType') || subtypeFallback,
                  cpMaterialNumber: getValueWithOverride(tableKey, rows, 'kau_material_number', 'kauMaterialNumber'),
                  cmoMaterialNumber: getValueWithOverride(tableKey, rows, 'cmo_material_number', 'cmoMaterialNumber'),
                  versionNumber: getValueWithOverride(tableKey, rows, 'kau_version', 'kauVersion'),
                  source: 'extracted' as const,
                  filename: (table.filename as string) || undefined,
                });
              }
            });
          }

          // 2. Extract from manualSections (Manual packaging scans)
          // Manual sections store their data in overrides[tableKey], not in tablesSnapshot
          if (docVal.manualSections) {
            docVal.manualSections.forEach(section => {
              // Check if this is a packaging materials manual section
              // Manual sections have tableKey like "MANUAL_PACKAGING_MATERIALS_1735670400000"
              if (section.documentType === 'PACKAGING_MATERIALS' || section.tableKey.includes('PACKAGING_MATERIALS')) {
                const tableOverrides = overrides[section.tableKey] || {};
                // Only add if there's at least some data entered
                const hasData = tableOverrides.kau_material_number || tableOverrides.kauMaterialNumber ||
                                tableOverrides.kau_version || tableOverrides.kauVersion ||
                                tableOverrides.cmo_material_number || tableOverrides.cmoMaterialNumber ||
                                tableOverrides.packaging_type || tableOverrides.packagingType;
                if (hasData) {
                  extractedMaterials.push({
                    id: `manual_${section.tableKey}`,
                    componentType: tableOverrides.packaging_type || tableOverrides.packagingType || '',
                    cpMaterialNumber: tableOverrides.kau_material_number || tableOverrides.kauMaterialNumber || '',
                    cmoMaterialNumber: tableOverrides.cmo_material_number || tableOverrides.cmoMaterialNumber || '',
                    versionNumber: tableOverrides.kau_version || tableOverrides.kauVersion || '',
                    source: 'extracted' as const, // Mark as extracted since it came from DocumentValidation
                  });
                }
              }
            });
          }

          if (extractedMaterials.length > 0) {
            setPackagingMaterials(prev => {
              const manualEntries = prev.filter(p => p.source === 'manual');
              const existingIds = new Set(extractedMaterials.map(e => e.id));
              const newManual = manualEntries.filter(m => !existingIds.has(m.id));
              return [...extractedMaterials, ...newManual];
            });
          }
        }

        // Restore serialization/aggregation data from documentValidation or brrForm
        if (savedReview?.documentValidation) {
          const docVal = savedReview.documentValidation;
          if (docVal.serializationRequirement) {
            setSerializationRequirement(docVal.serializationRequirement);
          }
          if (docVal.aggregationRequirement) {
            setAggregationRequirement(docVal.aggregationRequirement);
          }
          // Restore saved TraceLink data (avoids re-fetching)
          if (docVal.tracelinkGtin || docVal.tracelinkQuantity) {
            setTraceLinkData({
              GTIN: docVal.tracelinkGtin || '',
              Quantity: docVal.tracelinkQuantity || '',
            });
            setTraceLinkFound(true);
          }
          if (docVal.tracelinkHierarchyLevels?.length) {
            setHierarchyLevels(docVal.tracelinkHierarchyLevels);
          }
        } else if (savedReview?.brrForm) {
          // Fallback: load from brrForm if saved there
          const brrForm = savedReview.brrForm;
          if (brrForm.serializationRequirement) {
            setSerializationRequirement(brrForm.serializationRequirement);
          }
          if (brrForm.aggregationRequirement) {
            setAggregationRequirement(brrForm.aggregationRequirement);
          }
          if (brrForm.tracelinkGtin || brrForm.tracelinkQuantity) {
            setTraceLinkData({
              GTIN: brrForm.tracelinkGtin || '',
              Quantity: brrForm.tracelinkQuantity || '',
            });
            setTraceLinkFound(true);
          }
          if (brrForm.tracelinkHierarchyLevels?.length) {
            setHierarchyLevels(brrForm.tracelinkHierarchyLevels);
          }
        }

        // Backward compat: load from qpReview if no packaging materials from brrForm or extraction
        if (!restoredPackagingFromSave) {
          const qpPageData = savedReview?.brrForm;
          const qpPackagingMats = savedReview?.qpReview?.packagingMaterials as PackagingMaterialEntry[] | undefined;
          if ((!qpPageData?.packagingMaterials || qpPageData.packagingMaterials.length === 0)
              && qpPackagingMats && qpPackagingMats.length > 0) {
            const qpMaterials = qpPackagingMats.filter((m: PackagingMaterialEntry) => m != null);
            setPackagingMaterials(prev => {
              const extractedIds = new Set(prev.map(p => p.id));
              const newEntries = qpMaterials.filter(m => !extractedIds.has(m.id));
              return [...prev, ...newEntries];
            });
          }
        }
      } catch (error) {
        console.error('[FormPage] Failed to load saved state:', error);
      } finally {
        advanceProgressStep?.('Finalizing...');
        if (isMounted) {
          setSavedStateLoading(false);
          unblockNavigation?.();
        }
      }
    };

    void loadSavedState();

    return () => {
      isMounted = false;
    };
  }, [batchId, sessionId, useRealData, apiClient, setComplianceOverrides, setFieldOverrides, blockNavigation, unblockNavigation, startProgressOperation, advanceProgressStep]);

  // Track whether FormPage has become active (to detect navigation back from DocumentValidation)
  const wasActiveRef = useRef(isActive);

  // Refresh packaging materials when navigating BACK to FormPage from DocumentValidation
  // This ensures user edits on DocumentValidation are reflected when they click "Proceed to Batch Record Review"
  useEffect(() => {
    // Only trigger when page transitions from inactive to active
    const justBecameActive = isActive && !wasActiveRef.current;
    wasActiveRef.current = isActive;

    if (!justBecameActive || !sessionId || !batchId) return;

    const refreshPackagingMaterials = async () => {
      try {
        const savedReview = await apiClient.getSavedReview(batchId, sessionId);

        if (!savedReview?.documentValidation) {
          return;
        }

        const docVal = savedReview.documentValidation;
        const overrides = docVal.overrides || {};
        const SUBTYPE_LABELS: Record<string, string> = {
          LABEL: 'Labels', FOIL: 'Foils', LEAFLET: 'Leaflet',
          FOLDING_BOX: 'Folding Box', PRINTED_PACKAGING: 'Printed Packaging Materials',
          PACKAGING: 'Packaging',
        };

        // Helper to get value with override precedence
        const getValueWithOverride = (
          tableKey: string,
          rows: Array<Record<string, string | null>>,
          snakeKey: string,
          camelKey: string
        ): string => {
          const tableOverrides = overrides[tableKey];
          if (tableOverrides) {
            if (tableOverrides[snakeKey] !== undefined) return tableOverrides[snakeKey];
            if (tableOverrides[camelKey] !== undefined) return tableOverrides[camelKey];
          }
          for (const r of rows) {
            const rKey = r.extraction_key ?? r.extractionKey ?? '';
            if (rKey === snakeKey || rKey === camelKey) {
              return r.extracted_value ?? r.extractedValue ?? '';
            }
          }
          return '';
        };

        const extractedMaterials: PackagingMaterialEntry[] = [];

        // 1. Extract from tablesSnapshot (uploaded/extracted documents)
        if (docVal.tablesSnapshot?.tables) {
          Object.entries(docVal.tablesSnapshot.tables).forEach(([tableKey, tableData]) => {
            if (tableKey.startsWith('PACKAGING_MATERIALS') && tableData) {
              const table = tableData as unknown as Record<string, unknown>;
              const rows = (table.rows || []) as Array<Record<string, string | null>>;
              const subtypeMatch = tableKey.match(/^PACKAGING_MATERIALS__(.+?)(?:_\d+)?$/);
              const subtypeFallback = subtypeMatch ? (SUBTYPE_LABELS[subtypeMatch[1]] || subtypeMatch[1]) : '';
              extractedMaterials.push({
                id: `extracted_${tableKey}`,
                componentType: getValueWithOverride(tableKey, rows, 'packaging_type', 'packagingType') || subtypeFallback,
                cpMaterialNumber: getValueWithOverride(tableKey, rows, 'kau_material_number', 'kauMaterialNumber'),
                cmoMaterialNumber: getValueWithOverride(tableKey, rows, 'cmo_material_number', 'cmoMaterialNumber'),
                versionNumber: getValueWithOverride(tableKey, rows, 'kau_version', 'kauVersion'),
                source: 'extracted' as const,
                filename: (table.filename as string) || undefined,
              });
            }
          });
        }

        // 2. Extract from manualSections (Manual packaging scans)
        if (docVal.manualSections) {
          docVal.manualSections.forEach((section: { documentType?: string; tableKey: string }) => {
            if (section.documentType === 'PACKAGING_MATERIALS' || section.tableKey.includes('PACKAGING_MATERIALS')) {
              const tableOverrides = overrides[section.tableKey] || {};
              const hasData = tableOverrides.kau_material_number || tableOverrides.kauMaterialNumber ||
                              tableOverrides.kau_version || tableOverrides.kauVersion ||
                              tableOverrides.cmo_material_number || tableOverrides.cmoMaterialNumber ||
                              tableOverrides.packaging_type || tableOverrides.packagingType;
              if (hasData) {
                extractedMaterials.push({
                  id: `manual_${section.tableKey}`,
                  componentType: tableOverrides.packaging_type || tableOverrides.packagingType || '',
                  cpMaterialNumber: tableOverrides.kau_material_number || tableOverrides.kauMaterialNumber || '',
                  cmoMaterialNumber: tableOverrides.cmo_material_number || tableOverrides.cmoMaterialNumber || '',
                  versionNumber: tableOverrides.kau_version || tableOverrides.kauVersion || '',
                  source: 'extracted' as const,
                });
              }
            }
          });
        }

        if (extractedMaterials.length > 0) {
          setPackagingMaterials(prev => {
            const manualEntries = prev.filter(p => p.source === 'manual');
            const existingIds = new Set(extractedMaterials.map(e => e.id));
            const newManual = manualEntries.filter(m => !existingIds.has(m.id));
            return [...extractedMaterials, ...newManual];
          });
        }
      } catch (error) {
        console.error('[FormPage] Error refreshing packaging materials:', error);
      }
    };

    void refreshPackagingMaterials();
  }, [isActive, batchId, sessionId, apiClient]);

  // Timeout loading state if params never become valid (safety net to prevent infinite loading)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (savedStateLoading && (!sessionId || !batchId)) {
        setSavedStateLoading(false);
      }
    }, 5000);
    return () => clearTimeout(timeout);
  }, [sessionId, batchId, savedStateLoading]);

  // Block navigation during initial data load
  useEffect(() => {
    if (validatedLoading) {
      blockNavigation?.('Loading Batch Record Review...');
    } else {
      unblockNavigation?.();
    }
  }, [validatedLoading, blockNavigation, unblockNavigation]);

  // Next Periodic Review date
  const [nextPeriodicReview, setNextPeriodicReview] = useState<string>(
    () => fieldOverrides.nextPeriodicReview?.value as string || ''
  );

  // Change control validation notes (for overall "no change controls found" section)
  const [changeControlNotesArray, setChangeControlNotesArray] = useState<Array<{ text: string; timestamp: string }>>(
    () => fieldOverrides.changeControls?.validationNotesArray || []
  );
  const [changeControlNoteText, setChangeControlNoteText] = useState('');
  const [showChangeControlNotes, setShowChangeControlNotes] = useState(false);

  // Track compliance status for each section
  // Helper to convert string compliance to boolean for UI toggle logic
  // When value is undefined, defaults to false (non-compliant) to match original behavior
  const isCompliantValue = (value: string | undefined, defaultValue: boolean = false): boolean => {
    if (value === undefined || value === null || value === '') return defaultValue;
    const lowerVal = value.toLowerCase();
    return lowerVal.includes('compliant') || lowerVal.includes('complies') || lowerVal === 'current' || lowerVal === 'closed' || lowerVal === 'created' || lowerVal.includes('qp_authorised_complies');
  };

  const [basicInfoCompliance, setBasicInfoCompliance] = useState(() => ({}));
  const [materialsUsedCompliance, setMaterialsUsedCompliance] = useState<Record<string, boolean | string>>(() => ({}));
  const [cmoSupplierCompliance, setCmoSupplierCompliance] = useState(() => ({
    cmoName: isCompliantValue(complianceOverrides.cmoName),
    cmoQualStatus: isCompliantValue(complianceOverrides.cmoQualStatus),
    supplierName: isCompliantValue(complianceOverrides.supplierName),
    cmoComplianceReviewDate: isCompliantValue(complianceOverrides.cmoComplianceReviewDate)
  }));
  const [approvalDetailsCompliance, setApprovalDetailsCompliance] = useState(() => ({
    marketAuthorisationNumber: isCompliantValue(complianceOverrides.marketAuthorisationNumber),
    apiName: isCompliantValue(complianceOverrides.apiName),
    apiManufacturer: isCompliantValue(complianceOverrides.apiManufacturer),
    primaryPackaging: isCompliantValue(complianceOverrides.primaryPackaging),
    bulkManufacturer: isCompliantValue(complianceOverrides.bulkManufacturer),
    qcTestingSite: isCompliantValue(complianceOverrides.qcTestingSite),
    primaryPackagingSite: isCompliantValue(complianceOverrides.primaryPackagingSite),
    secondaryPackagingSite: isCompliantValue(complianceOverrides.secondaryPackagingSite),
    releaseSite: isCompliantValue(complianceOverrides.releaseSite)
  }));
  const [qaaStatusCompliance, setQaaStatusCompliance] = useState(() => ({
    qaaStatus: isCompliantValue(complianceOverrides.qaaStatus)
  }));
  const [storageInfoCompliance, setStorageInfoCompliance] = useState(() => ({
    reportedShelfLife: isCompliantValue(complianceOverrides.reportedShelfLife),
    storageStatement: isCompliantValue(complianceOverrides.storageStatement),
    targetStorageLocation: isCompliantValue(complianceOverrides.targetStorageLocation)
  }));
  const [countryRequirementsCompliance, setCountryRequirementsCompliance] = useState(() => ({
    targetCountry: isCompliantValue(complianceOverrides.targetCountry),
    mraACAA: isCompliantValue(complianceOverrides.mraACAA),
    countrySpecificReqs: isCompliantValue(complianceOverrides.countrySpecificReqs)
  }));
  const [changeControlCompliance, setChangeControlCompliance] = useState<Record<string, boolean>>(() => ({}));
  const [expandedChangeControlActions, setExpandedChangeControlActions] = useState<Set<number>>(new Set());
  const [actionStatusFilter, setActionStatusFilter] = useState<Record<number, string>>(() => ({}));
  // Individual change control validation notes (keyed by change control stableId)
  const [individualCCNotes, setIndividualCCNotes] = useState<Record<string, Array<{ text: string; timestamp: string }>>>(() => ({}));
  const [individualCCNoteText, setIndividualCCNoteText] = useState<Record<string, string>>(() => ({}));
  const [showIndividualCCNoteForm, setShowIndividualCCNoteForm] = useState<Record<string, boolean>>(() => ({}));
  const [sampleDocsCompliance, setSampleDocsCompliance] = useState<Record<string, boolean>>(() => ({
    // specsApi defaults to false (non-compliant) unless explicitly set
    specsApi: isCompliantValue(complianceOverrides.specsApi, false) || isCompliantValue(complianceOverrides.specifications, false),
    // Other sample docs default to true (compliant) unless explicitly set otherwise
    primaryPackagingMaterials: isCompliantValue(complianceOverrides.primaryPackagingMaterials, true) || isCompliantValue(complianceOverrides.packagingMaterialApproval, true),
    manufacturingInstructions: isCompliantValue(complianceOverrides.manufacturingInstructions, true),
    packagingInstructions: isCompliantValue(complianceOverrides.packagingInstructions, true),
    releaseTestInstructions: isCompliantValue(complianceOverrides.releaseTestInstructions, true),
    releaseSpecifications: isCompliantValue(complianceOverrides.releaseSpecifications, true),
  }));
  const [deviationsOosCompliance, setDeviationsOosCompliance] = useState<Record<string, boolean>>(() => ({
    deviations: isCompliantValue(complianceOverrides.deviations)
  }));
  const [batchDatesCompliance, setBatchDatesCompliance] = useState<Record<string, boolean>>(() => ({
    batchManufactureDate: isCompliantValue(complianceOverrides.batchManufactureDate),
    batchExpiryDate: isCompliantValue(complianceOverrides.batchExpiryDate),
  }));

  // Manually added and removed change controls / quality events
  const [manuallyAddedCCs, setManuallyAddedCCs] = useState<ManualChangeControl[]>([]);
  const [manuallyAddedQEs, setManuallyAddedQEs] = useState<ManualQualityEvent[]>([]);
  const [removedCCIds, setRemovedCCIds] = useState<Set<string>>(new Set());
  const [removedQEIds, setRemovedQEIds] = useState<Set<string>>(new Set());
  const [showAddCCModal, setShowAddCCModal] = useState(false);
  const [showAddQEModal, setShowAddQEModal] = useState(false);

  const [showCcSearch, setShowCcSearch] = useState(false);
  const [ccSearchQuery, setCcSearchQuery] = useState('');
  const [ccSearchResults, setCcSearchResults] = useState<BRRFormDataChangeControl[]>([]);
  const [ccSearchLoading, setCcSearchLoading] = useState(false);
  const [ccSearchError, setCcSearchError] = useState<string | null>(null);
  const [ccSearchCapped, setCcSearchCapped] = useState(false);

  const handleCcSearch = useCallback(async () => {
    const trimmed = ccSearchQuery.trim();
    if (trimmed.length < 2) return;

    setCcSearchLoading(true);
    setCcSearchError(null);
    try {
      const response = await apiClient.searchChangeControls(trimmed, 50);
      setCcSearchResults(response.results);
      setCcSearchCapped(response.count >= 50);
    } catch (err) {
      setCcSearchError(err instanceof Error ? err.message : 'Search failed');
      setCcSearchResults([]);
      setCcSearchCapped(false);
    } finally {
      setCcSearchLoading(false);
    }
  }, [ccSearchQuery, apiClient]);

  const handleAddCcFromSearch = useCallback((cc: BRRFormDataChangeControl) => {
    const stableId = `search_cc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const newCC: ManualChangeControl = {
      stableId,
      actionId: cc.name || '',
      text: cc.titleValue || cc.description || '',
      status: cc.status || 'Unknown',
      source: 'CARA Search',
      actions: cc.actions || [],
    };
    setManuallyAddedCCs(prev => [...prev, newCC]);
    setCcSearchResults(prev => prev.filter(r => r.name !== cc.name));
  }, []);

  // Snapshot of last-saved state for dirty comparison (useState so updates trigger re-render)
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string | null>(null);

  // Serialize removedIds for snapshot comparison
  const removedCCIdsArray = useMemo(() => Array.from(removedCCIds).sort(), [removedCCIds]);
  const removedQEIdsArray = useMemo(() => Array.from(removedQEIds).sort(), [removedQEIds]);

  // Track unsaved changes by comparing current state to last-saved snapshot
  const currentSnapshot = useMemo(() => JSON.stringify({
    brrLevel, brrCreationBasis, manualSAPEntry, nextPeriodicReview,
    changeControlNotesArray, individualCCNotes, countrySpecificReqsText, fieldOverrides,
    complianceOverrides, manuallyAddedCCs, manuallyAddedQEs, removedCCIdsArray, removedQEIdsArray,
    documentSectionData, customDocumentSections: customDocSections, cpInternalDocs, packagingMaterials, materialsUsed,
    batchReleaseSiteConfirmed, batchReleaseSiteCheplapharmConfirmed, selectedReleaseSites, batchReleaseSiteText,
    serializationRequirement, aggregationRequirement,
  }), [brrLevel, brrCreationBasis, manualSAPEntry, nextPeriodicReview, changeControlNotesArray, individualCCNotes, countrySpecificReqsText, fieldOverrides, complianceOverrides, manuallyAddedCCs, manuallyAddedQEs, removedCCIdsArray, removedQEIdsArray, documentSectionData, customDocSections, cpInternalDocs, packagingMaterials, materialsUsed, batchReleaseSiteConfirmed, batchReleaseSiteCheplapharmConfirmed, selectedReleaseSites, batchReleaseSiteText, serializationRequirement, aggregationRequirement]);

  const hasUnsavedChanges = useMemo(() => {
    if (!hasSavedOnce) {
      return brrLevel !== 'level1' ||
        brrCreationBasis.length !== 1 || brrCreationBasis[0] !== 'complete-sample' ||
        manualSAPEntry ||
        nextPeriodicReview !== '' ||
        changeControlNotesArray.length > 0 ||
        Object.keys(individualCCNotes).length > 0 ||
        countrySpecificReqsText !== '' ||
        Object.keys(fieldOverrides).length > 0 ||
        manuallyAddedCCs.length > 0 ||
        manuallyAddedQEs.length > 0 ||
        removedCCIds.size > 0 ||
        removedQEIds.size > 0 ||
        cpInternalDocs.length > 0 ||
        packagingMaterials.length > 0 ||
        materialsUsed.length > 0 ||
        batchReleaseSiteConfirmed ||
        batchReleaseSiteCheplapharmConfirmed ||
        selectedReleaseSites.length > 0 ||
        batchReleaseSiteText !== '' ||
        customDocSections.length > 0 ||
        serializationRequirement !== '' ||
        aggregationRequirement !== '';
    }
    return currentSnapshot !== lastSavedSnapshot;
  }, [hasSavedOnce, brrLevel, brrCreationBasis, manualSAPEntry, nextPeriodicReview, changeControlNotesArray, individualCCNotes, countrySpecificReqsText, fieldOverrides, currentSnapshot, manuallyAddedCCs, manuallyAddedQEs, removedCCIds, removedQEIds, cpInternalDocs, packagingMaterials, materialsUsed, batchReleaseSiteConfirmed, batchReleaseSiteCheplapharmConfirmed, selectedReleaseSites, batchReleaseSiteText, customDocSections, lastSavedSnapshot, serializationRequirement, aggregationRequirement]);

  // Notify parent of dirty state
  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  // Browser beforeunload warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && ENABLE_SAVE_FUNCTIONALITY) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, ENABLE_SAVE_FUNCTIONALITY]);

  // Ref to track the latest handleSave function for auto-save
  const handleSaveRef = useRef<(silent?: boolean) => Promise<boolean>>();

  // Auto-save: debounced save when form data changes
  // This ensures autoPopulatedData is persisted to Neo4j for cloning
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAutoSavingRef = useRef(false);

  // Track when button save completes to prevent redundant unmount save
  const buttonSaveCompletedRef = useRef(false);

  useEffect(() => {
    if (!ENABLE_SAVE_FUNCTIONALITY || isReadOnly) return;
    if (!batchId || !sessionId) return;

    // Clear any pending auto-save
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce auto-save by 3 seconds
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (handleSaveRef.current && !isAutoSavingRef.current) {
        isAutoSavingRef.current = true;
        handleSaveRef.current(true).finally(() => {
          isAutoSavingRef.current = false;
        });
      }
    }, 3000);

    // Save immediately on unmount
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      // Skip unmount save if button already saved (prevents race condition)
      if (buttonSaveCompletedRef.current) {
        return;
      }
      // Fire-and-forget save on unmount
      if (handleSaveRef.current && !isAutoSavingRef.current) {
        void handleSaveRef.current(true);
      }
    };
  }, [ENABLE_SAVE_FUNCTIONALITY, isReadOnly, batchId, sessionId, brrLevel, brrCreationBasis, fieldOverrides, complianceOverrides, countrySpecificReqsText, manualSAPEntry, nextPeriodicReview, changeControlNotesArray, individualCCNotes, boxStates, cpInternalDocs, batchReleaseSiteConfirmed, batchReleaseSiteCheplapharmConfirmed, selectedReleaseSites, batchReleaseSiteText, manuallyAddedCCs, manuallyAddedQEs, removedCCIdsArray, removedQEIdsArray, documentSectionData, customDocSections, packagingMaterials, materialsUsed]);

  // Fields with compliance dropdowns for each section
  // These define which fields count toward the "X/Y validated" metric
  const BASIC_INFO_FIELDS = ['productName', 'productNameGlobal', 'destinationCountry', 'packSize', 'dosageForm', 'doseStrength', 'materialSKU', 'batchQuantity'];
  const CMO_SUPPLIER_FIELDS = ['cmoName', 'cmoQualStatus', 'supplierName', 'cmoComplianceReviewDate'];
  const APPROVAL_DETAILS_FIELDS = ['marketAuthorisationNumber', 'authorisationStatus', 'validityPeriodEndValue', 'apiName', 'apiManufacturer', 'primaryPackaging', 'bulkManufacturer', 'qcTestingSite', 'primaryPackagingSite', 'secondaryPackagingSite', 'releaseSite'];
  const QAA_STATUS_FIELDS = ['qaaStatus'];
  const STORAGE_INFO_FIELDS = ['reportedShelfLife', 'storageStatement', 'targetStorageLocation'];
  // Country Requirements section has no compliance dropdowns (notes only) - excluded from validation count
  const BATCH_DATES_FIELDS = ['batchManufactureDate', 'batchExpiryDate'];

  // Helper to check if a value represents "compliant" status
  const isCompliantStatus = (v: boolean | string | undefined): boolean =>
    v === true || v === 'compliant' || v === 'current' || v === 'closed' || v === 'created';

  // Calculate validation counts based on explicitly set compliance values
  // Only counts fields that have been explicitly validated (exist in complianceObj)
  // Returns total=0 when no fields have been validated yet
  const getValidationCount = (complianceObj: Record<string, boolean | string>, expectedFields?: string[]) => {
    if (expectedFields) {
      // Count only fields that have been explicitly set in complianceObj
      const validatedFields = expectedFields.filter(f => complianceObj[f] !== undefined);
      const total = validatedFields.length;
      const validated = validatedFields.filter(f => isCompliantStatus(complianceObj[f])).length;
      return { validated, total };
    }
    // Fallback for dynamic sections (change controls, materials used, etc.)
    const total = Object.keys(complianceObj).length;
    const validated = Object.values(complianceObj).filter(isCompliantStatus).length;
    return { validated, total };
  };

  const basicInfoCount = getValidationCount(basicInfoCompliance, BASIC_INFO_FIELDS);
  const materialsUsedCount = getValidationCount(materialsUsedCompliance); // Dynamic - fields depend on materials
  const cmoSupplierCount = getValidationCount(cmoSupplierCompliance, CMO_SUPPLIER_FIELDS);
  const approvalDetailsCount = getValidationCount(approvalDetailsCompliance, APPROVAL_DETAILS_FIELDS);
  const qaaStatusCount = getValidationCount(qaaStatusCompliance, QAA_STATUS_FIELDS);
  const storageInfoCount = getValidationCount(storageInfoCompliance, STORAGE_INFO_FIELDS);
  // Country Requirements section has no compliance dropdowns - don't show validation count
  const changeControlCount = getValidationCount(changeControlCompliance); // Dynamic - fields depend on CCs
  const sampleDocsCount = getValidationCount(sampleDocsCompliance); // Uses section keys, not field IDs
  const deviationsOosCount = getValidationCount(deviationsOosCompliance); // Dynamic - fields depend on QEs
  const batchDatesCount = getValidationCount(batchDatesCompliance, BATCH_DATES_FIELDS);

  // Handle field compliance changes - uses field-specific compliance options
  const handleComplianceChange = useCallback((fieldId: string, isCompliant: boolean) => {
    // Get field-specific compliance options (e.g., 'complies'/'does_not_comply' for Technical Docs)
    const options = getComplianceOptionsForField(fieldId);
    // First option is positive (compliant/complies), second is negative (non_compliant/does_not_comply)
    const complianceValue = isCompliant ? options[0].value : options[1].value;
    setComplianceOverrides(prev => ({
      ...prev,
      [fieldId]: complianceValue
    }));
    onDirtyChange?.(true);
  }, [onDirtyChange]);

  // Compliance change handlers for each section
  const handleBasicInfoComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setBasicInfoCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleMaterialsUsedComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setMaterialsUsedCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleCmoSupplierComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setCmoSupplierCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleApprovalDetailsComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setApprovalDetailsCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleQaaStatusComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setQaaStatusCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleStorageInfoComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setStorageInfoCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleCountryRequirementsComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setCountryRequirementsCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleChangeControlComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setChangeControlCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleSampleDocsComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setSampleDocsCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleDeviationsOosComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setDeviationsOosCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  const handleBatchDatesComplianceChange = (fieldId: string, isCompliant: boolean) => {
    setBatchDatesCompliance(prev => ({ ...prev, [fieldId]: isCompliant }));
    handleComplianceChange(fieldId, isCompliant);
  };

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Evidence file upload handler - stages file for title/description entry
  const handleEvidenceFileUpload = useCallback(async (files: File[], boxFolderId: string | null) => {
    // Only handle one file at a time for the unified flow
    const file = files[0];
    if (!file) return;

    // Validate PDF only
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setItemError('Only PDF files are supported');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setItemError(`File must be under ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    if (!boxFolderId) {
      setItemError('Box folder not ready. Please wait for document processing to complete before uploading additional evidence.');
      return;
    }

    if (!inspectionLotId) {
      setItemError('Inspection lot not available. Please reload the BRR before uploading additional evidence.');
      return;
    }

    setIsUploadingEvidence(true);
    setItemError('');
    setUploadEvidenceProgress(0);

    try {
      const response = await apiClient.uploadFileToBox(
        boxFolderId,
        inspectionLotId,
        file,
        (progress: number) => {
          setUploadEvidenceProgress(progress);
        }
      );

      // Stage the uploaded file - user will add title/description
      setPendingPdfUpload({
        fileName: response.file_name || file.name,
        fileSize: formatFileSize(file.size),
        uploadTime: formatTime(new Date()),
        boxFileId: response.file_id,
      });

      // Auto-fill title with filename (without extension)
      const titleFromFilename = file.name.replace(/\.pdf$/i, '');
      setNewItemTitle(titleFromFilename);
      setNewItemDescription('');

    } catch (error) {
      console.error('Upload error:', error);
      setItemError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploadingEvidence(false);
      setUploadEvidenceProgress(0);
    }
  }, [apiClient, inspectionLotId]);

  // Document handlers
  const handleEvidenceDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingEvidence(true);
  }, []);

  const handleEvidenceDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingEvidence(false);
  }, []);

  const handleEvidenceDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingEvidence(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await handleEvidenceFileUpload(files, boxFolderIdRef.current);
    }
  }, [handleEvidenceFileUpload]);

  const handleEvidenceFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target; // Capture target reference before async operation
    const files = target.files ? Array.from(target.files) : [];
    if (files.length > 0) {
      await handleEvidenceFileUpload(files, boxFolderIdRef.current);
    }
    // Reset input so same file can be selected again
    if (target) {
      target.value = '';
    }
  }, [handleEvidenceFileUpload]);

  // Evidence uploaded before evidence moved to Box exists only in blob storage, so removing it
  // has to delete it there. Box-hosted files stay in the batch archive.
  const isLegacyBlobEvidence = (item: AdditionalEvidenceItem): item is AdditionalEvidenceItem & { blobName: string } =>
    item.type === 'pdf' && !!item.blobName && !item.boxFileId;

  // Remove evidence item (unified - handles both PDF and Box link types)
  const removeEvidenceItem = async (itemId: string): Promise<void> => {
    const item = additionalEvidenceItems.find(i => i.id === itemId);

    if (!item) {
      console.error(`Item with id ${itemId} not found`);
      return;
    }

    if (isLegacyBlobEvidence(item)) {
      try {
        await apiClient.deleteBlobDocument(item.blobName);
      } catch (error) {
        console.error(`Failed to delete ${item.blobName} from blob storage:`, error);
        setItemError(`Warning: Failed to delete ${item.fileName} from storage. ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Remove from local state
    setAdditionalEvidenceItems(prev => prev.filter(i => i.id !== itemId));

    // Cancel edit mode if editing the removed item
    if (editingItemId === itemId) {
      handleCancelEdit();
    }
  };

  // Validate Box link URL
  const validateBoxLink = (url: string): boolean => {
    if (!url.trim()) return false;
    const folderMatch = url.match(/box\.com\/folder\/(\d+)/);
    const sharedMatch = url.match(/box\.com\/s\/[\w]+/);
    return !!(folderMatch || sharedMatch);
  };

  // An edit rebuilds the item from the staged upload, which carries no blob fields — dropping them
  // would orphan the file in blob storage and kill its "View" link.
  const preserveLegacyBlobFields = (
    edited: AdditionalEvidenceItem,
    existing: AdditionalEvidenceItem
  ): AdditionalEvidenceItem =>
    edited.boxFileId ? edited : { ...edited, blobName: existing.blobName, url: existing.url };

  // Confirm pending PDF upload and add to unified list
  const handleConfirmPdfUpload = () => {
    if (!pendingPdfUpload) return;

    if (!newItemTitle.trim()) {
      setItemError('Title is required');
      return;
    }

    const newItem: AdditionalEvidenceItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'pdf',
      title: newItemTitle.trim(),
      description: newItemDescription.trim(),
      createdAt: new Date().toISOString(),
      fileName: pendingPdfUpload.fileName,
      fileSize: pendingPdfUpload.fileSize,
      uploadTime: pendingPdfUpload.uploadTime,
      boxFileId: pendingPdfUpload.boxFileId,
    };

    if (editingItemId) {
      setAdditionalEvidenceItems(prev => prev.map(item =>
        item.id === editingItemId
          ? preserveLegacyBlobFields({ ...newItem, id: editingItemId, createdAt: item.createdAt }, item)
          : item
      ));
      setEditingItemId(null);
    } else {
      setAdditionalEvidenceItems(prev => [...prev, newItem]);
    }

    // Reset form
    setPendingPdfUpload(null);
    setNewItemTitle('');
    setNewItemDescription('');
    setItemError('');
  };

  // Cancel pending PDF upload. No cleanup: the file is already part of the batch's Box archive.
  const handleCancelPdfUpload = () => {
    setPendingPdfUpload(null);
    setNewItemTitle('');
    setNewItemDescription('');
    setItemError('');
  };

  // Add Box link item
  const handleAddBoxLinkItem = () => {
    if (!newItemTitle.trim()) {
      setItemError('Title is required');
      return;
    }
    if (!newItemBoxLink.trim()) {
      setItemError('Box link is required');
      return;
    }
    if (!validateBoxLink(newItemBoxLink)) {
      setItemError('URL must be a Box folder link (box.com/folder/...) or shared link (box.com/s/...)');
      return;
    }

    const newItem: AdditionalEvidenceItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'boxLink',
      title: newItemTitle.trim(),
      description: newItemDescription.trim(),
      createdAt: new Date().toISOString(),
      boxLink: newItemBoxLink.trim(),
    };

    if (editingItemId) {
      setAdditionalEvidenceItems(prev => prev.map(item =>
        item.id === editingItemId ? { ...newItem, id: editingItemId, createdAt: item.createdAt } : item
      ));
      setEditingItemId(null);
    } else {
      setAdditionalEvidenceItems(prev => [...prev, newItem]);
    }

    setNewItemTitle('');
    setNewItemDescription('');
    setNewItemBoxLink('');
    setItemError('');
  };

  // Edit existing item
  const handleEditItem = (item: AdditionalEvidenceItem) => {
    setNewItemTitle(item.title);
    setNewItemDescription(item.description);
    setEditingItemId(item.id);
    setItemError('');

    if (item.type === 'pdf') {
      setEvidenceTab('pdf');
      setPendingPdfUpload({
        fileName: item.fileName || '',
        fileSize: item.fileSize || '',
        uploadTime: item.uploadTime || '',
        boxFileId: item.boxFileId || '',
      });
    } else {
      setEvidenceTab('boxLink');
      setNewItemBoxLink(item.boxLink || '');
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setNewItemTitle('');
    setNewItemDescription('');
    setNewItemBoxLink('');
    setPendingPdfUpload(null);
    setEditingItemId(null);
    setItemError('');
  };

  // Scroll to Additional Evidence section
  const scrollToAdditionalEvidence = () => {
    const element = document.getElementById('additional-evidence-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Save handler — writes full form snapshot to Neo4j
  // silent: if true, don't update UI status (for auto-save)
  const handleSave = async (silent = false): Promise<boolean> => {
    // When useRealData=false there is no backing session, so we generate a
    // throwaway ID. With useRealData=true a missing sessionId is a hard failure.
    const effectiveSessionId = (!sessionId && !useRealData)
      ? `local-dev-${Date.now()}`
      : sessionId;

    if (!effectiveSessionId) {
      console.error('[FormPage] Cannot save BRR review: no sessionId');
      return false;
    }

    // Validate batchId is present and numeric (backend requires integer)
    if (!batchId || batchId.trim() === '' || isNaN(parseInt(batchId, 10))) {
      console.error('[FormPage] Cannot save BRR review: batchId missing or non-numeric:', batchId);
      return false;
    }

    if (!silent) {
      startProgressOperation?.('formPage_save', FORM_PAGE_SAVE_STEPS, 'Saving Batch Record Review');
      advanceProgressStep?.('Preparing to save...');
      blockNavigation?.('Saving Batch Record Review...');
      setSaveStatus('saving');
    }

    try {
      if (!silent) advanceProgressStep?.('Building save data...');
      // Build box folder URLs from boxStates (now stores entries array per section)
      const boxFolderUrls: Record<string, BoxSubsectionData> = {};
      (Object.keys(boxStates) as BoxSubsectionKey[]).forEach(key => {
        const entries = boxStates[key].entries;
        if (entries.length > 0) {
          boxFolderUrls[key] = { entries };
        }
      });

      // Convert individualCCNotes from Record<number, Array> to Record<string, Array> for JSON
      const individualCCNotesForSave: Record<string, Array<{ text: string; timestamp: string }>> = {};
      Object.entries(individualCCNotes).forEach(([key, notes]) => {
        if (notes && notes.length > 0) {
          individualCCNotesForSave[key] = notes;
        }
      });

      const payload: BRRReviewPayload = {
        batchId: batchId,
        sessionId: effectiveSessionId,
        pageName: 'brr_form',
        pageData: {
          brrLevel: brrLevel as 'level1' | 'level2' | 'na',
          brrCreationBasis: brrCreationBasis,
          complianceOverrides: complianceOverrides,
          fieldOverrides: fieldOverrides,
          autoPopulatedData: autoPopulatedBRRWithQAA ?? autoPopulatedBRR ?? {},
          countrySpecificRequirements: countrySpecificReqsText || undefined,
          batchReleaseSite: batchReleaseSiteText || undefined,
          batchReleaseSiteConfirmed: batchReleaseSiteConfirmed,
          batchReleaseSiteCheplapharmConfirmed: batchReleaseSiteCheplapharmConfirmed,
          selectedReleaseSites: selectedReleaseSites.length > 0 ? selectedReleaseSites : undefined,
          manualSAPEntry: manualSAPEntry,
          nextPeriodicReview: nextPeriodicReview || undefined,
          changeControlNotes: changeControlNotesArray.length > 0 ? changeControlNotesArray : undefined,
          individualCCNotes: Object.keys(individualCCNotesForSave).length > 0 ? individualCCNotesForSave : undefined,
          boxFolderUrls: Object.keys(boxFolderUrls).length > 0 ? boxFolderUrls : undefined,
          manuallyAddedCCs: manuallyAddedCCs,
          manuallyAddedQEs: manuallyAddedQEs,
          removedCCIds: Array.from(removedCCIds),
          removedQEIds: Array.from(removedQEIds),
          documentSectionData: documentSectionData,
          customDocumentSections: customDocSections.length > 0 ? customDocSections : undefined,
          cpInternalDocs: cpInternalDocs.length > 0 ? cpInternalDocs : undefined,
          packagingMaterials: packagingMaterials.length > 0 ? packagingMaterials : undefined,
          materialsUsed: materialsUsed.length > 0 ? materialsUsed : undefined,
          // Serialization & Aggregation data
          serializationRequirement: serializationRequirement || undefined,
          aggregationRequirement: aggregationRequirement || undefined,
          tracelinkGtin: traceLinkData?.['GTIN'] || undefined,
          tracelinkQuantity: traceLinkData?.['Quantity'] || undefined,
          tracelinkHierarchyLevels: hierarchyLevels || undefined,
          savedAt: new Date().toISOString(),
        },
        batchType: batchType,
        priority: priority,
        isAdmin: isAdmin,
        status: 'draft',
      };

      if (!silent) advanceProgressStep?.('Saving to database...');
      const response = await apiClient.saveReview(payload);

      // Handle backend validation warnings
      if (response.warnings && response.warnings.length > 0) {
        console.warn('[FormPage] Backend validation warnings:', response.warnings);
      }

      if (!silent) advanceProgressStep?.('Updating save status...');
      const now = new Date();
      setLastSavedTime(now);
      setHasSavedOnce(true);
      setLastSavedSnapshot(currentSnapshot);

      if (!silent) {
        advanceProgressStep?.('Save complete');
        setSaveStatus('saved');
        // Clear "saved" status after 3 seconds
        setTimeout(() => {
          setSaveStatus('');
        }, 3000);
      }

      unblockNavigation?.();
      return true;
    } catch (err) {
      // Extract detailed error message for debugging
      let errorMessage = 'Unknown error';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      console.error('[FormPage] Failed to save BRR review:', {
        error: err,
        errorMessage,
        batchId,
        sessionId: effectiveSessionId,
      });
      if (!silent) {
        setSaveStatus('');
        unblockNavigation?.(`Save failed: ${errorMessage}`);
      }
      return false;
    }
  };

  // Update ref for auto-save to use
  handleSaveRef.current = handleSave;

  // Form data is loaded via useValidatedFormData hook (above) which merges
  // Document Validation edits with API data. We alias it here for clarity.
  const apiFormData = validatedFormData;
  const formLoading = validatedLoading;
  const formError = validatedError;

  // Helper to safely extract field value with fallback

  const safeField = useCallback((field: any, defaultSource: string = 'Unknown'): { value: string; source: string } => {
    if (!field) return { value: 'No Data Extracted', source: defaultSource };
    if (typeof field === 'object' && 'value' in field && 'source' in field) {
      return {
        value: field.value ?? 'No Data Extracted',
        source: field.source ?? defaultSource
      };
    }
    return { value: 'No Data Extracted', source: defaultSource };
  }, []);

  // Helper to safely extract array field
  const safeArrayField = useCallback((field: any, defaultSource: string = 'Unknown'): { value: string; source: string } => {
    if (!field || !Array.isArray(field) || field.length === 0) {
      return { value: 'No Data Extracted', source: defaultSource };
    }
    const firstItem = field[0];
    if (firstItem && typeof firstItem === 'object' && 'value' in firstItem) {
      return {
        value: firstItem.value ?? 'No Data Extracted',
        source: firstItem.source ?? defaultSource
      };
    }
    return { value: 'No Data Extracted', source: defaultSource };
  }, []);

  // Helper function to map API response to component structure
  const mapFormDataToComponent = useCallback((data: BRRFormDataResponse): BRRFormData => {
    try {
      return {
      sapIdentityBatch: {
        sapInternalBatch: data.sapIdentityBatch.sapInternalBatch,
        inspectionLotNumber: data.sapIdentityBatch.inspectionLotNumber,
        poNumber: data.sapIdentityBatch.poNumber,
        poLineItem: data.sapIdentityBatch.poLineItem,
        batchQuantity: data.sapIdentityBatch.batchQuantity,
        deliveryType: { value: batchType === 'indirect' ? 'Indirect Delivery' : 'Direct Delivery', source: 'BRR Session' },
        targetStorageLocation: { value: '', source: 'SAP' },
        cmoFpBatchId: data.sapIdentityBatch.cmoFpBatchId || { value: '', source: '' },
        cmoExternalBatchSAP: data.sapIdentityBatch.cmoExternalBatchSAP || { value: '', source: '' },
        cmoBulkBatchId: data.sapIdentityBatch.cmoBulkBatchId || { value: '', source: '' }
      },
      productMarketData: {
        productNameGlobal: data.productMarketData.productNameGlobal,
        productName: data.productMarketData.productName,
        packSize: data.productMarketData.packSize,
        dosageForm: data.productMarketData.dosageForm,
        materialSKU: data.productMarketData.materialSKU,
        targetCountry: startPageCountry
          ? { value: startPageCountry, source: 'User Selection' }
          : data.productMarketData.targetCountry,
        shelfLife: data.productMarketData.shelfLife,
        storageStatement: data.productMarketData.storageStatement
      },
      cmoSupplierQual: {
        cmoName: fieldOverrides.supplierName
          ? { value: String(fieldOverrides.supplierName.value), source: 'User Selection' }
          : data.cmoSupplierQual.cmoName,
        cmoQualStatus: { value: '', source: 'SAP' }, // Not yet in backend
        qaaStatus: { value: qaaData?.found ? [qaaData.matched_firma, qaaData.signing_date, qaaData.review_period, qaaData.review_date].filter(Boolean).join(' | ') : '', source: 'QAA Repository (Status Verträge.xlsx)' },
        qaaValidity: { value: '', source: 'QAA Repository' },
        cmoComplianceReviewDate: { value: '', source: 'SAP' } // Not yet in backend
      },
      cmoTechnicalDocs: {
        specsApi: { value: '', source: 'CMC-Q File Server' },
        primaryPackagingMaterials: { value: '', source: 'CMC-Q File Server' },
        manufacturingInstructions: { value: '', source: 'Artworks / KAU' },
        packagingInstructions: { value: '', source: 'Artworks / KAU' },
        releaseTestInstructions: { value: '', source: 'CMC-Q File Server' },
        releaseSpecifications: { value: '', source: 'CMC-Q File Server' }
      },
      artworkPackaging: {
        cmoMaterialNumber: { value: '', source: 'KAU/Artworks' },
        versionNumber: { value: '', source: 'KAU/Artworks' },
        packagingMaterialApproval: [],
        printApprovalVersion: { value: '', source: 'Artwork Repository' },
        bomAlignment: { value: 'Aligned', source: 'CMO Documentation' }
      },
      logisticsDocs: {
        deliveryNote: { value: false, source: 'Box CMO Folders' },
        transportReport: { value: false, source: 'Box CMO Folders' },
        inboundProtocol: { value: false, source: 'SAP MIGO' }
      },
      regulatoryQMS: {
        deviations: {
          value: [
            ...data.qualityEvents.deviations,
            ...data.qualityEvents.outOfSpecifications,
            ...data.qualityEvents.complaints,
            ...data.qualityEvents.marketComplaints,
            ...data.qualityEvents.outOfTrend
          ].map(qe => ({
            id: qe.name || 'Unknown',
            eventType: qe.eventType || 'Unknown',
            description: qe.description || '',
            titleValue: qe.titleValue || '',
            affectedProducts: qe.affectedProducts || '',
            affectedBatchNumber: qe.affectedBatchNumber || '',
            finalApprovalDate: qe.finalApprovalDate || '',
            finalApprovalCompleted: qe.finalApprovalCompleted,
            targetCompletionDate: qe.targetCompletionDate || '',
            matchedVia: Array.isArray(qe.matchedVia) ? qe.matchedVia.join(', ') : (qe.matchedVia || '')
          })),
          source: 'CARA Quality Events',
          validationStatus: 'Compliant'
        },
        changeControls: {
          value: data.changeControls.map(cc => ({
            actionId: cc.name || 'Unknown',
            text: cc.titleValue || cc.description || '',
            status: cc.status || 'Open',
            source: 'CARA Change Control Data',
            matchedVia: Array.isArray(cc.matchedVia) ? cc.matchedVia.join(', ') : (cc.matchedVia || ''),
            actions: cc.actions || []
          })),
          source: 'CARA Change Control Data'
        },
        countryValidationStatus: { value: 'Compliant', source: 'CARA Regulatory Dossier' }
      },
      approvalDetails: {
        marketAuthorisationNumber: (() => {
          const authNum = data.approvalDetails.marketAuthorisationNumber as any;
          // Handle array
          if (Array.isArray(authNum)) {
            return authNum.length > 0 && authNum[0]?.value
              ? authNum
              : [{ value: 'No Marketing Authorisation found', source: 'CARA' }];
          }
          // Handle single object - if value is null/empty, use default message
          if (authNum && authNum.value) {
            return [authNum];
          }
          return [{ value: 'No Marketing Authorisation found', source: 'CARA' }];
        })(),
        authorisationStatus: data.approvalDetails.authorisationStatus || { value: '', source: 'CARA' },
        validityPeriodEndValue: data.approvalDetails.validityPeriodEndValue || { value: '', source: 'CARA' },
        apiName: data.approvalDetails.apiName,
        apiManufacturer: data.approvalDetails.apiManufacturer,
        reportedShelfLife: data.approvalDetails.reportedShelfLife,
        primaryPackaging: { value: '', source: 'CARA Registrations' }, // Not yet in backend
        bulkManufacturer: data.approvalDetails.bulkManufacturer,
        qcTestingSite: data.approvalDetails.qcTestingSite,
        primaryPackagingSite: data.approvalDetails.primaryPackagingSite || [],
        secondaryPackagingSite: data.approvalDetails.secondaryPackagingSite || [],
        releaseSite: data.approvalDetails.releaseSite || []
      }
    };
    } catch (error) {
      console.error('Error mapping form data:', error);
      // Return safe default structure for all fields on error
      const noDataField: FieldValue = { value: '', source: 'Not Found' };
      return {
        sapIdentityBatch: {
          sapInternalBatch: noDataField,
          inspectionLotNumber: noDataField,
          poNumber: noDataField,
          poLineItem: noDataField,
          batchQuantity: noDataField,
          deliveryType: noDataField,
          targetStorageLocation: noDataField,
          cmoFpBatchId: noDataField,
          cmoExternalBatchSAP: noDataField,
          cmoBulkBatchId: noDataField
        },
        productMarketData: {
          productNameGlobal: noDataField,
          productName: noDataField,
          packSize: noDataField,
          dosageForm: noDataField,
          materialSKU: noDataField,
          targetCountry: noDataField,
          shelfLife: noDataField,
          storageStatement: noDataField
        },
        cmoSupplierQual: {
          cmoName: noDataField,
          cmoQualStatus: noDataField,
          qaaStatus: noDataField,
          qaaValidity: noDataField,
          cmoComplianceReviewDate: noDataField
        },
        cmoTechnicalDocs: {
          specsApi: noDataField,
          primaryPackagingMaterials: noDataField,
          manufacturingInstructions: noDataField,
          packagingInstructions: noDataField,
          releaseTestInstructions: noDataField,
          releaseSpecifications: noDataField
        },
        artworkPackaging: {
          cmoMaterialNumber: noDataField,
          versionNumber: noDataField,
          packagingMaterialApproval: [],
          printApprovalVersion: noDataField,
          bomAlignment: noDataField
        },
        logisticsDocs: {
          deliveryNote: { value: false, source: 'Not Found' },
          transportReport: { value: false, source: 'Not Found' },
          inboundProtocol: { value: false, source: 'Not Found' }
        },
        regulatoryQMS: {
          deviations: { value: [], source: 'Not Found', validationStatus: 'Compliant' },
          changeControls: { value: [], source: 'Not Found' },
          countryValidationStatus: noDataField
        },
        approvalDetails: {
          marketAuthorisationNumber: [],
          authorisationStatus: noDataField,
          validityPeriodEndValue: noDataField,
          apiName: [],
          apiManufacturer: [],
          reportedShelfLife: noDataField,
          primaryPackaging: noDataField,
          bulkManufacturer: [],
          qcTestingSite: [],
          primaryPackagingSite: [],
          secondaryPackagingSite: [],
          releaseSite: []
        }
      };
    }
  }, [safeField, safeArrayField]);

  // Empty BRR data structure - provides the expected shape with no fake values
  // All fields use empty strings/arrays so UI displays "Not available" appropriately
  const emptyBRRData = useMemo(() => ({
    sapIdentityBatch: {
      sapInternalBatch: { value: batchId || '', source: '' },
      inspectionLotNumber: { value: '', source: '' },
      poNumber: { value: '', source: '' },
      poLineItem: { value: '', source: '' },
      batchQuantity: { value: '', source: '' },
      batchQuantitySAP: { value: '', source: '' },
      unitOfMeasure: { value: '', source: '' },
      deliveryType: { value: batchType === 'indirect' ? 'Indirect Delivery' : 'Direct Delivery', source: 'BRR Session' },
      targetStorageLocation: { value: '', source: '' },
      cmoFpBatchId: { value: '', source: '' },
      cmoExternalBatchSAP: { value: '', source: '' },
      cmoBulkBatchId: { value: '', source: '' },
      batchManufactureDate: { value: '', source: '' },
      batchExpiryDate: { value: '', source: '' },
      batchManufactureDateSAP: { value: '', source: '' },
      batchExpiryDateSAP: { value: '', source: '' },
      calculatedExpiryDate: { value: '', source: '' },
      calculatedExpiryFormula: { value: '', source: '' },
      calculatedExpiryMatch: { value: '', source: '' },
      calculatedExpiryShelfLifeSource: { value: '', source: '' },
    },
    productMarketData: {
      productNameGlobal: { value: '', source: '' },
      productName: { value: productNameProp || '', source: '' },
      productNameCARA: { value: '', source: '' },
      packSize: { value: '', source: '' },
      packSizeSAP: { value: '', source: '' },
      packSizeCARA: { value: '', source: '' },
      dosageForm: { value: '', source: '' },
      dosageFormSAP: { value: '', source: '' },
      dosageFormCARA: { value: '', source: '' },
      materialSKU: { value: '', source: '' },
      materialSKUSAP: { value: '', source: '' },
      doseStrength: { value: '', source: '' },
      doseStrengthSAP: { value: '', source: '' },
      doseStrengthCARA: { value: '', source: '' },
      targetCountry: { value: '', source: '' },
      targetCountrySAP: { value: '', source: '' },
      marketCountryName: { value: '', source: '' },
      shelfLife: { value: '', source: '' },
      storageStatement: { value: '', source: '' },
      shelfLifeInMonths: { value: '', source: '' },
      temperatureConditions: { value: '', source: '' }
    },
    cmoSupplierQual: {
      cmoName: { value: '', source: '' },
      cmoNameOptions: [],
      cmoNameMatch: null,
      cmoNameSAPList: [],
      cmoQualStatus: { value: '', source: '' },
      qaaStatus: { value: '', source: '' },
      qaaValidity: { value: '', source: '' },
      cmoComplianceReviewDate: { value: '', source: '' }
    },
    cmoTechnicalDocs: {
      specsApi: { value: '', source: '' },
      primaryPackagingMaterials: { value: '', source: '' },
      manufacturingInstructions: { value: '', source: '' },
      packagingInstructions: { value: '', source: '' },
      releaseTestInstructions: { value: '', source: '' },
      releaseSpecifications: { value: '', source: '' }
    },
    artworkPackaging: {
      cmoMaterialNumber: { value: '', source: '' },
      versionNumber: { value: '', source: '' },
      packagingMaterialApproval: [],
      printApprovalVersion: { value: '', source: '' },
      bomAlignment: { value: '', source: '' }
    },
    logisticsDocs: {
      deliveryNote: { value: false, source: '' },
      transportReport: { value: false, source: '' },
      inboundProtocol: { value: false, source: '' }
    },
    regulatoryQMS: {
      deviations: {
        value: [],
        source: '',
        validationStatus: ''
      },
      changeControls: {
        value: [],
        source: ''
      },
      countryValidationStatus: { value: '', source: '' }
    },
    approvalDetails: {
      marketAuthorisationNumber: [],
      marketAuthorisationNumberCARA: { value: '', source: '' },
      packageLevelValue: { value: '', source: '' },
      authorisationStatus: { value: '', source: '' },
      validityPeriodEndValue: { value: '', source: '' },
      apiName: [],
      apiManufacturer: [],
      reportedShelfLife: { value: '', source: '' },
      primaryPackaging: { value: '', source: '' },
      bulkManufacturer: [],
      qcTestingSite: [],
      primaryPackagingSite: [],
      secondaryPackagingSite: [],
      releaseSite: []
    },
    boxArchive: null
  }), [batchId, productNameProp, batchType]);

  // Determine BRR form data source: API data, saved data, or empty structure
  const autoPopulatedBRR: BRRFormData = useMemo((): BRRFormData => {
    if (useRealData && apiFormData) {
      // API MODE: Use API data with empty defaults for missing fields
      // BRR Field Values (grey box) - pull from API (validated document data from Document Validation page)
      // Source of Truth fields (blue box) - pull from API (SAP/CARA graph data for validation)
      const allQualityEvents = apiFormData.qualityEvents ? [
        ...apiFormData.qualityEvents.deviations,
        ...apiFormData.qualityEvents.outOfSpecifications,
        ...apiFormData.qualityEvents.complaints,
        ...apiFormData.qualityEvents.marketComplaints,
        ...apiFormData.qualityEvents.outOfTrend
      ] : [];
      return ({
        ...emptyBRRData,
        sapIdentityBatch: {
          ...emptyBRRData.sapIdentityBatch,
          sapInternalBatch: apiFormData.sapIdentityBatch?.sapInternalBatch || emptyBRRData.sapIdentityBatch.sapInternalBatch,
          batchQuantity: apiFormData.sapIdentityBatch?.batchQuantity || emptyBRRData.sapIdentityBatch.batchQuantity,
          batchQuantitySAP: apiFormData.sapIdentityBatch?.batchQuantitySAP || emptyBRRData.sapIdentityBatch.batchQuantitySAP,
          unitOfMeasure: apiFormData.sapIdentityBatch?.unitOfMeasure || emptyBRRData.sapIdentityBatch.unitOfMeasure,
          batchManufactureDate: apiFormData.sapIdentityBatch?.batchManufactureDate || { value: '', source: '' },
          batchExpiryDate: apiFormData.sapIdentityBatch?.batchExpiryDate || { value: '', source: '' },
          batchManufactureDateSAP: apiFormData.sapIdentityBatch?.batchManufactureDateSAP || { value: '', source: '' },
          batchExpiryDateSAP: apiFormData.sapIdentityBatch?.batchExpiryDateSAP || { value: '', source: '' },
          calculatedExpiryDate: apiFormData.sapIdentityBatch?.calculatedExpiryDate || { value: '', source: '' },
          calculatedExpiryFormula: apiFormData.sapIdentityBatch?.calculatedExpiryFormula || { value: '', source: '' },
          calculatedExpiryMatch: apiFormData.sapIdentityBatch?.calculatedExpiryMatch || { value: '', source: '' },
          calculatedExpiryShelfLifeSource: apiFormData.sapIdentityBatch?.calculatedExpiryShelfLifeSource || { value: '', source: '' },
          inspectionLotNumber: apiFormData.sapIdentityBatch?.inspectionLotNumber || emptyBRRData.sapIdentityBatch.inspectionLotNumber,
          targetStorageLocation: apiFormData.sapIdentityBatch?.targetStorageLocation || emptyBRRData.sapIdentityBatch.targetStorageLocation,
          poNumber: apiFormData.sapIdentityBatch?.poNumber || emptyBRRData.sapIdentityBatch.poNumber,
          poLineItem: apiFormData.sapIdentityBatch?.poLineItem || emptyBRRData.sapIdentityBatch.poLineItem,
          cmoFpBatchId: apiFormData.sapIdentityBatch?.cmoFpBatchId || emptyBRRData.sapIdentityBatch.cmoFpBatchId,
          cmoExternalBatchSAP: apiFormData.sapIdentityBatch?.cmoExternalBatchSAP || emptyBRRData.sapIdentityBatch.cmoExternalBatchSAP,
          cmoBulkBatchId: apiFormData.sapIdentityBatch?.cmoBulkBatchId || emptyBRRData.sapIdentityBatch.cmoBulkBatchId,
          sapLotQuantities: apiFormData.sapIdentityBatch?.sapLotQuantities,
        },
        productMarketData: {
          ...emptyBRRData.productMarketData,
          // BRR Field Values (grey box) - pull from API (validated document data from Document Validation page)
          // Use productNameProp if provided (from Document Validation), otherwise fall back to API data
          productName: productNameProp
            ? { value: productNameProp, source: 'Certificate of Analysis' }
            : (apiFormData.productMarketData?.productName || emptyBRRData.productMarketData.productName),
          packSize: apiFormData.productMarketData?.packSize || emptyBRRData.productMarketData.packSize,
          dosageForm: apiFormData.productMarketData?.dosageForm || emptyBRRData.productMarketData.dosageForm,
          materialSKU: apiFormData.productMarketData?.materialSKU || emptyBRRData.productMarketData.materialSKU,
          doseStrength: apiFormData.productMarketData?.doseStrength || emptyBRRData.productMarketData.doseStrength,
          // Source of Truth fields (blue box) - pull from API (SAP/CARA graph data for validation)
          productNameGlobal: apiFormData.productMarketData?.productNameGlobal || emptyBRRData.productMarketData.productNameGlobal,
          productNameCARA: apiFormData.productMarketData?.productNameCARA || emptyBRRData.productMarketData.productNameCARA,
          packSizeSAP: apiFormData.productMarketData?.packSizeSAP || emptyBRRData.productMarketData.packSizeSAP,
          packSizeCARA: apiFormData.productMarketData?.packSizeCARA || emptyBRRData.productMarketData.packSizeCARA,
          dosageFormSAP: apiFormData.productMarketData?.dosageFormSAP || emptyBRRData.productMarketData.dosageFormSAP,
          dosageFormCARA: apiFormData.productMarketData?.dosageFormCARA || emptyBRRData.productMarketData.dosageFormCARA,
          materialSKUSAP: apiFormData.productMarketData?.materialSKUSAP || emptyBRRData.productMarketData.materialSKUSAP,
          doseStrengthSAP: apiFormData.productMarketData?.doseStrengthSAP || emptyBRRData.productMarketData.doseStrengthSAP,
          doseStrengthCARA: apiFormData.productMarketData?.doseStrengthCARA || emptyBRRData.productMarketData.doseStrengthCARA,
          // Storage Conditions fields
          shelfLifeInMonths: apiFormData.productMarketData?.shelfLifeInMonths || emptyBRRData.productMarketData.shelfLifeInMonths,
          temperatureConditions: apiFormData.productMarketData?.temperatureConditions || emptyBRRData.productMarketData.temperatureConditions,
          storageStatement: apiFormData.productMarketData?.storageStatement || emptyBRRData.productMarketData.storageStatement,
          // Target Country - user's start page selection takes priority over document extraction
          targetCountry: startPageCountry
            ? { value: startPageCountry, source: 'User Selection' }
            : (apiFormData.productMarketData?.targetCountry || emptyBRRData.productMarketData.targetCountry),
          targetCountrySAP: apiFormData.productMarketData?.targetCountrySAP || emptyBRRData.productMarketData.targetCountrySAP,
          marketCountryName: apiFormData.productMarketData?.marketCountryName || emptyBRRData.productMarketData.marketCountryName
        },
        cmoSupplierQual: {
          ...emptyBRRData.cmoSupplierQual,
          cmoName: apiFormData.cmoSupplierQual?.cmoName || emptyBRRData.cmoSupplierQual.cmoName,
          cmoNameOptions: apiFormData.cmoSupplierQual?.cmoNameOptions || emptyBRRData.cmoSupplierQual.cmoNameOptions,
          cmoNameMatch: apiFormData.cmoSupplierQual?.cmoNameMatch || emptyBRRData.cmoSupplierQual.cmoNameMatch,
          cmoNameSAPList: apiFormData.cmoSupplierQual?.cmoNameSAPList || emptyBRRData.cmoSupplierQual.cmoNameSAPList,
        },
        approvalDetails: {
          ...emptyBRRData.approvalDetails,
          marketAuthorisationNumber: (() => {
            const authNum = apiFormData.approvalDetails?.marketAuthorisationNumber as any;
            if (!authNum) {
              return emptyBRRData.approvalDetails.marketAuthorisationNumber;
            }
            // Handle array
            if (Array.isArray(authNum)) {
              return authNum.length > 0 && authNum[0]?.value
                ? authNum
                : [{ value: 'No Marketing Authorisation found', source: 'Extracted from Documents' }];
            }
            // Handle single object - if value is null/empty, use default message
            if (authNum.value) {
              return [authNum];
            }
            return [{ value: 'No Marketing Authorisation found', source: 'Extracted from Documents' }];
          })(),
          marketAuthorisationNumberCARA: apiFormData.approvalDetails?.marketAuthorisationNumberCARA || emptyBRRData.approvalDetails.marketAuthorisationNumberCARA,
          packageLevelValue: apiFormData.approvalDetails?.packageLevelValue || emptyBRRData.approvalDetails.packageLevelValue,
          authorisationStatus: apiFormData.approvalDetails?.authorisationStatus || emptyBRRData.approvalDetails.authorisationStatus,
          validityPeriodEndValue: apiFormData.approvalDetails?.validityPeriodEndValue || emptyBRRData.approvalDetails.validityPeriodEndValue,
          apiName: apiFormData.approvalDetails?.apiName || emptyBRRData.approvalDetails.apiName,
          apiManufacturer: apiFormData.approvalDetails?.apiManufacturer || emptyBRRData.approvalDetails.apiManufacturer,
          bulkManufacturer: apiFormData.approvalDetails?.bulkManufacturer || emptyBRRData.approvalDetails.bulkManufacturer,
          qcTestingSite: apiFormData.approvalDetails?.qcTestingSite || emptyBRRData.approvalDetails.qcTestingSite,
          primaryPackagingSite: apiFormData.approvalDetails?.primaryPackagingSite || emptyBRRData.approvalDetails.primaryPackagingSite,
          secondaryPackagingSite: apiFormData.approvalDetails?.secondaryPackagingSite || emptyBRRData.approvalDetails.secondaryPackagingSite,
          releaseSite: apiFormData.approvalDetails?.releaseSite || emptyBRRData.approvalDetails.releaseSite,
          reportedShelfLife: apiFormData.approvalDetails?.reportedShelfLife || emptyBRRData.approvalDetails.reportedShelfLife,
        },
        regulatoryQMS: {
          ...emptyBRRData.regulatoryQMS,
          deviations: apiFormData.qualityEvents ? {
            value: [
              ...apiFormData.qualityEvents.deviations,
              ...apiFormData.qualityEvents.outOfSpecifications,
              ...apiFormData.qualityEvents.complaints,
              ...apiFormData.qualityEvents.marketComplaints,
              ...apiFormData.qualityEvents.outOfTrend
            ].filter(qe => qe.finalApprovalCompleted !== true).map(qe => ({
              id: qe.name || 'Unknown',
              eventType: qe.eventType || 'Unknown',
              description: qe.description || '',
              titleValue: qe.titleValue || '',
              affectedProducts: qe.affectedProducts || '',
              affectedBatchNumber: qe.affectedBatchNumber || '',
              finalApprovalDate: qe.finalApprovalDate || '',
              finalApprovalCompleted: qe.finalApprovalCompleted,
              targetCompletionDate: qe.targetCompletionDate || '',
              matchedVia: Array.isArray(qe.matchedVia) ? qe.matchedVia.join(', ') : (qe.matchedVia || ''),
              affectedMarket: Array.isArray(qe.affectedMarket) ? qe.affectedMarket.join(', ') : (qe.affectedMarket || '')
            })),
            source: 'CARA Quality Events',
            validationStatus: 'Compliant'
          } : emptyBRRData.regulatoryQMS.deviations,
          changeControls: {
            value: apiFormData.changeControls?.map(cc => ({
              actionId: cc.name || 'Unknown',
              text: cc.titleValue || cc.description || '',
              status: cc.status || 'Open',
              source: 'CARA Change Control Data',
              matchedVia: Array.isArray(cc.matchedVia) ? cc.matchedVia.join(', ') : (cc.matchedVia || ''),
              affectedMarket: Array.isArray(cc.affectedMarket) ? cc.affectedMarket.join(', ') : (cc.affectedMarket || ''),
              actions: cc.actions || []
            })) || [],
            source: 'CARA Change Control Data'
          }
        },
        boxArchive: apiFormData.boxArchive || orchestratorBoxArchive || emptyBRRData.boxArchive,
        materialBoxFolders: apiFormData.materialBoxFolders || null,
        materialsUsed: apiFormData.materialsUsed || [],
      }) as BRRFormData;
    }
    // Use saved auto-populated data from Neo4j (for cloned/resumed BRRs)
    // Only use if it has the required structure (sapIdentityBatch and approvalDetails)
    if (savedAutoPopulatedData) {
      // Validate that saved data has required structure before using it
      if (savedAutoPopulatedData.sapIdentityBatch && savedAutoPopulatedData.approvalDetails) {
        return savedAutoPopulatedData;
      } else {
        console.warn('autoPopulatedBRR - Saved data missing required sections, falling back to empty structure');
      }
    }

    // No data available (including cloned sessions without valid saved data)
    // Fall back to empty structure - UI will show "Not available" for fields
    if (isClonedSession) {
      console.warn('autoPopulatedBRR - Cloned session missing valid autoPopulatedData, falling back to empty structure');
    }
    return emptyBRRData as BRRFormData;
  }, [useRealData, apiFormData, emptyBRRData, productNameProp, orchestratorBoxArchive, savedAutoPopulatedData, isClonedSession, startPageCountry]);

  // Keep the ref the upload handlers read in sync with the resolved Box archive folder
  const resolvedBoxFolderId = autoPopulatedBRR?.boxArchive?.folder_id || boxFolderIdFromProps;
  useEffect(() => {
    boxFolderIdRef.current = resolvedBoxFolderId;
  }, [resolvedBoxFolderId]);

  // Initialize materialsUsed from auto-populated data (when no saved state was restored)
  const autoMaterialsUsedLength = autoPopulatedBRR?.materialsUsed?.length ?? 0;
  useEffect(() => {
    if (autoMaterialsUsedLength > 0 && materialsUsed.length === 0 && !savedStateLoading) {
      const entries: MaterialUsedEntry[] = (autoPopulatedBRR.materialsUsed || []).map((item, idx) => ({
        id: `auto-${idx}`,
        material: item.material,
        vendorBatchId: item.vendorBatchId,
        source: 'extracted' as const,
      }));
      setMaterialsUsed(entries);
      // Set default compliance to 'compliant' for all auto-populated materials
      const defaultCompliance: Record<string, boolean> = {};
      entries.forEach(entry => {
        defaultCompliance[`materialsUsed_${entry.id}`] = true;
      });
      setMaterialsUsedCompliance(prev => ({ ...prev, ...defaultCompliance }));
    }
  }, [autoMaterialsUsedLength, savedStateLoading]);

  // Effective card lists: auto-populated (minus removed) + manually added
  const effectiveCCs = useMemo(() => {
    const autoCards = (autoPopulatedBRR?.regulatoryQMS?.changeControls?.value || [])
      .filter((cc: any) => !removedCCIds.has(cc.actionId))
      .map((cc: any) => ({ ...cc, stableId: cc.actionId, isManual: false }))
      .sort((a: any, b: any) => {
        const numA = parseInt(a.actionId.replace(/\D/g, ''), 10) || 0;
        const numB = parseInt(b.actionId.replace(/\D/g, ''), 10) || 0;
        return numA - numB;
      });
    const manualCards = manuallyAddedCCs.map(cc => ({ ...cc, isManual: true }));
    return [...autoCards, ...manualCards];
  }, [autoPopulatedBRR, removedCCIds, manuallyAddedCCs]);

  const effectiveQEs = useMemo(() => {
    const autoCards = (autoPopulatedBRR?.regulatoryQMS?.deviations?.value || [])
      .filter((qe: any) => !removedQEIds.has(qe.id))
      .map((qe: any) => ({ ...qe, stableId: qe.id, isManual: false }));
    const manualCards = manuallyAddedQEs.map(qe => ({ ...qe, isManual: true }));
    return [...autoCards, ...manualCards];
  }, [autoPopulatedBRR, removedQEIds, manuallyAddedQEs]);

  // Sync compliance keys when effective CC/QE lists change
  useEffect(() => {
    setChangeControlCompliance(prev => {
      const updated: Record<string, boolean> = {};
      for (const cc of effectiveCCs) {
        const key = `cc_${cc.stableId}`;
        updated[key] = key in prev ? prev[key] : false;
      }
      return updated;
    });
  }, [effectiveCCs]);

  useEffect(() => {
    setDeviationsOosCompliance(prev => {
      const updated: Record<string, boolean> = {};
      for (const qe of effectiveQEs) {
        const key = `qe_${qe.stableId}`;
        updated[key] = key in prev ? prev[key] : false;
      }
      return updated;
    });
  }, [effectiveQEs]);

  // Compute Box folder href from backend-provided URL, with folder_id fallback
  const boxFolderHref = useMemo(() => {
    return autoPopulatedBRR?.boxArchive?.folder_url
      || (autoPopulatedBRR?.boxArchive?.folder_id
          ? `https://cheplapharm.app.box.com/folder/${autoPopulatedBRR.boxArchive.folder_id}`
          : undefined);
  }, [autoPopulatedBRR]);

  // NOTE: Box folder links are now session-scoped, stored only in BRRReview.brrForm.boxFolderUrls
  // They are NOT loaded from Material node (materialBoxFolders) to ensure a fresh BRR starts with no links.
  // Links are restored from saved BRR state via loadSavedState (pageData.boxFolderUrls).

  // QAA contract status check
  const qaaCmoName = (fieldOverrides.cmoName?.value as string)
    || (autoPopulatedBRR?.cmoSupplierQual?.cmoName?.value as string)
    || null;
  const { data: qaaData, loading: qaaLoading, error: qaaError } = useQAACheck({
    cmoName: qaaCmoName,
    apiClient,
    autoLoad: true
  });

  // Enrich autoPopulatedBRR with QAA status (computed after useQAACheck hook)
  const autoPopulatedBRRWithQAA = useMemo(() => {
    if (!autoPopulatedBRR) return autoPopulatedBRR;
    if (!qaaData?.found) return autoPopulatedBRR;
    return {
      ...autoPopulatedBRR,
      cmoSupplierQual: {
        ...(autoPopulatedBRR.cmoSupplierQual || {}),
        qaaStatus: {
          value: [qaaData.matched_firma, qaaData.signing_date, qaaData.review_period, qaaData.review_date].filter(Boolean).join(' | '),
          source: 'QAA Repository (Status Verträge.xlsx)' as const
        },
      },
    };
  }, [autoPopulatedBRR, qaaData]);

  // Fetch TraceLink lot status when materialNumber and vendorBatchId are available
  useEffect(() => {
    // Get material number and vendor batch ID from autoPopulatedBRR (SAP data)
    const materialNumber = autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value ||
                           autoPopulatedBRR?.productMarketData?.materialSKU?.value;
    const vendorBatchId = autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value;

    if (!materialNumber || !vendorBatchId) return;

    // Skip fetch if we already have a result (found or not found)
    if (traceLinkFound !== null) return;

    let cancelled = false;

    const fetchTraceLinkData = async () => {
      setTraceLinkLoading(true);
      setTraceLinkError(null);
      setTraceLinkFound(null);
      setTraceLinkNotFoundMessage(null);

      try {
        const result = await apiClient.getTraceLinkLotStatus(String(materialNumber), String(vendorBatchId));
        if (cancelled) return;

        if (result.found && result.lotStatus) {
          setTraceLinkFound(true);
          setTraceLinkData(result.lotStatus);
        } else {
          setTraceLinkFound(false);
          setTraceLinkNotFoundMessage(
            result.message || 'This material number and vendor batch ID combination was not found in TraceLink.'
          );
        }
      } catch (err) {
        if (cancelled) return;
        let message = 'Failed to fetch TraceLink data';
        if (err instanceof Error) {
          const jsonMatch = err.message.match(/:\s*(\{.*\})\s*$/);
          if (jsonMatch) {
            try {
              const parsed = JSON.parse(jsonMatch[1]);
              message = parsed.error || parsed.message || err.message;
            } catch { message = err.message; }
          } else {
            message = err.message;
          }
        }
        setTraceLinkError(message);
      } finally {
        if (!cancelled) setTraceLinkLoading(false);
      }
    };

    fetchTraceLinkData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch when input data changes, not when result state changes
  }, [autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value,
      autoPopulatedBRR?.productMarketData?.materialSKU?.value,
      autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value,
      apiClient]);

  // Fetch TraceLink hierarchy levels
  useEffect(() => {
    const materialNumber = autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value ||
                           autoPopulatedBRR?.productMarketData?.materialSKU?.value;
    const vendorBatchId = autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value;

    if (!materialNumber || !vendorBatchId) return;

    // Skip if we already have saved data
    if (hierarchyLevels && hierarchyLevels.length > 0) return;

    let cancelled = false;

    const fetchHierarchyLevels = async () => {
      setHierarchyLoading(true);
      setHierarchyError(null);

      try {
        const result = await apiClient.getTraceLinkHierarchyLevels(String(materialNumber), String(vendorBatchId));
        if (cancelled) return;

        if (result.found && result.levels) {
          setHierarchyLevels(result.levels);
        } else {
          setHierarchyLevels(null);
        }
      } catch (err) {
        if (cancelled) return;
        let message = 'Failed to fetch hierarchy data';
        if (err instanceof Error) {
          const jsonMatch = err.message.match(/:\s*(\{.*\})\s*$/);
          if (jsonMatch) {
            try {
              const parsed = JSON.parse(jsonMatch[1]);
              message = parsed.error || parsed.message || err.message;
            } catch { message = err.message; }
          } else {
            message = err.message;
          }
        }
        setHierarchyError(message);
      } finally {
        if (!cancelled) setHierarchyLoading(false);
      }
    };

    fetchHierarchyLevels();
    return () => { cancelled = true; };
  }, [autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value,
      autoPopulatedBRR?.productMarketData?.materialSKU?.value,
      autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value,
      apiClient, hierarchyLevels]);

  // Dynamically track calculatedExpiryDate in batchDatesCompliance when data loads
  useEffect(() => {
    if (autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryDate?.value) {
      setBatchDatesCompliance(prev => {
        if ('calculatedExpiryDate' in prev) return prev;
        // Convert string compliance override to boolean for section state
        const savedValue = complianceOverrides.calculatedExpiryDate;
        const boolValue = savedValue ? isCompliantValue(savedValue) : true;
        return { ...prev, calculatedExpiryDate: boolValue };
      });
    }
  }, [autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryDate?.value]);

  // Pass enriched data to parent component if callback provided
  // MUST be before early returns to satisfy Rules of Hooks
  React.useEffect(() => {
    if (onDataChange && autoPopulatedBRRWithQAA) {
      const dataForParent = fieldOverrides.supplierName
        ? {
            ...autoPopulatedBRRWithQAA,
            cmoSupplierQual: {
              ...autoPopulatedBRRWithQAA.cmoSupplierQual,
              cmoName: { value: String(fieldOverrides.supplierName.value), source: 'User Selection' },
            },
          }
        : autoPopulatedBRRWithQAA;
      onDataChange(dataForParent);
    }
  }, [onDataChange, autoPopulatedBRRWithQAA, fieldOverrides.supplierName]);

  // Auto-save when autoPopulatedBRR is computed/changes
  // This ensures the full SAP/CARA data is persisted for cloning
  const hasAutoSavedOnceRef = useRef(false);
  const autoPopulatedSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset auto-save flag when session changes (ensures each session gets auto-saved)
  React.useEffect(() => {
    hasAutoSavedOnceRef.current = false;
  }, [sessionId]);

  React.useEffect(() => {
    if (!ENABLE_SAVE_FUNCTIONALITY || isReadOnly) return;
    if (!batchId || !sessionId) return;
    if (!autoPopulatedBRR?.sapIdentityBatch) return; // Wait for real data

    // Clear any pending timeout
    if (autoPopulatedSaveTimeoutRef.current) {
      clearTimeout(autoPopulatedSaveTimeoutRef.current);
    }

    // Only auto-save once when autoPopulatedBRR first becomes available
    // Debounce by 1 second to avoid racing with other state changes
    if (!hasAutoSavedOnceRef.current) {
      autoPopulatedSaveTimeoutRef.current = setTimeout(() => {
        if (!hasAutoSavedOnceRef.current) {
          hasAutoSavedOnceRef.current = true;
          if (handleSaveRef.current) {
            void handleSaveRef.current(true);
          }
        }
      }, 1000);
    }

    return () => {
      if (autoPopulatedSaveTimeoutRef.current) {
        clearTimeout(autoPopulatedSaveTimeoutRef.current);
      }
    };
  }, [ENABLE_SAVE_FUNCTIONALITY, isReadOnly, batchId, sessionId, autoPopulatedBRR]);

  // Scroll to top when component mounts (ensures user starts at top of form)
  // MUST be before early returns to satisfy Rules of Hooks
  React.useEffect(() => {
    // Aggressive scroll to top strategy to override any other scroll behavior
    const scrollToTop = () => {
      const headerElement = document.getElementById('form-header-top');

      // Scroll window first
      window.scrollTo(0, 0);

      if (headerElement) {
        // Find the scrollable parent container (BRRApp's overflow-auto div)
        let scrollableParent = headerElement.parentElement;
        while (scrollableParent) {
          const overflowY = window.getComputedStyle(scrollableParent).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') {
            // Found the scrollable container, scroll it to top immediately
            scrollableParent.scrollTop = 0;
            break;
          }
          scrollableParent = scrollableParent.parentElement;
        }

        // Use scrollIntoView with instant behavior
        headerElement.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    };

    // Scroll multiple times to ensure it sticks
    // Immediate scroll
    scrollToTop();

    // After first paint
    requestAnimationFrame(() => {
      scrollToTop();
    });

    // After a short delay to override any late-rendering scroll
    const scrollTimer = setTimeout(() => {
      scrollToTop();
    }, 200);

    // Final scroll after everything settles
    const finalScrollTimer = setTimeout(() => {
      scrollToTop();
    }, 500);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(finalScrollTimer);
    };
  }, []);

  // Get all non-validated fields for dropdown
  // MUST be before early returns to satisfy Rules of Hooks
  const nonValidatedFields = React.useMemo(() => {
    const getNonValidatedFields = () => {
    const fieldLabels: Record<string, { label: string; section: string }> = {
      // Basic Information
      productName: { label: 'Product Name', section: 'Basic Information' },
      materialDescription: { label: 'Product Name (Global)', section: 'Basic Information' },
      destinationCountry: { label: 'Destination Country', section: 'Basic Information' },
      packSize: { label: 'Pack Size', section: 'Basic Information' },
      dosageForm: { label: 'Dosage Form', section: 'Basic Information' },
      doseStrength: { label: 'Dose Strength', section: 'Basic Information' },
      materialSKU: { label: 'Material Number', section: 'Basic Information' },
      batchQuantity: { label: 'Batch Quantity', section: 'Basic Information' },
      nextPeriodicReview: { label: 'Next Periodic Review', section: 'Basic Information' },
      // Supplier Status
      cmoName: { label: 'CMO Name', section: 'Supplier Status' },
      cmoQualStatus: { label: 'CMO Qualification Status', section: 'Supplier Status' },
      supplierName: { label: 'CMO Name', section: 'Supplier Status' },
      cmoComplianceReviewDate: { label: 'CMO Compliance Review Date', section: 'Supplier Status' },
      // Approval Details
      marketAuthorisationNumber: { label: 'Marketing Authorisation Number', section: 'Approval Details' },
      authorisationStatus: { label: 'Authorisation Status', section: 'Approval Details' },
      validityPeriodEndValue: { label: 'Validity Period End', section: 'Approval Details' },
      apiName: { label: 'API Name', section: 'Approval Details' },
      apiManufacturer: { label: 'API Manufacturer', section: 'Approval Details' },
      primaryPackaging: { label: 'Primary Packaging', section: 'Approval Details' },
      bulkManufacturer: { label: 'Bulk Manufacturer', section: 'Approval Details' },
      qcTestingSite: { label: 'QC Testing Site', section: 'Approval Details' },
      primaryPackagingSite: { label: 'Primary Packaging Site', section: 'Approval Details' },
      secondaryPackagingSite: { label: 'Secondary Packaging Site', section: 'Approval Details' },
      releaseSite: { label: 'Certification of batch by manufacturer', section: 'Approval Details' },
      // QAA Status
      qaaStatus: { label: 'QAA Status', section: 'QAA Status' },
      // Storage Information
      targetStorageLocation: { label: 'Target Storage Location', section: 'Storage Information' },
      storageStatement: { label: 'Storage Statement', section: 'Storage Information' },
      // Country Requirements
      targetCountry: { label: 'Target Country', section: 'Country Requirements' },
      mraACAA: { label: 'MRA/ACAA', section: 'Country Requirements' },
      countrySpecificReqs: { label: 'Country Specific Requirements', section: 'Country Requirements' },
      // Sample Documents
      specsApi: { label: 'Specifications for Active Ingredients', section: 'Sample Documents' },
      primaryPackagingMaterials: { label: 'Primary Packaging Specifications', section: 'Sample Documents' },
      manufacturingInstructions: { label: 'Manufacturing Instructions', section: 'Sample Documents' },
      packagingInstructions: { label: 'Packaging Instructions', section: 'Sample Documents' },
      releaseTestInstructions: { label: 'Release Test Instructions', section: 'Sample Documents' },
      // Deviations / OOS
      deviations: { label: 'Deviations', section: 'Deviations / (Stabi-) OOS' }
    };

    const nonValidatedFields: { fieldId: string; label: string; section: string }[] = [];
    const allCompliance = [
      { obj: basicInfoCompliance, name: 'basicInfo' },
      { obj: cmoSupplierCompliance, name: 'cmoSupplier' },
      { obj: approvalDetailsCompliance, name: 'approvalDetails' },
      { obj: qaaStatusCompliance, name: 'qaaStatus' },
      { obj: storageInfoCompliance, name: 'storageInfo' },
      { obj: countryRequirementsCompliance, name: 'countryRequirements' },
      { obj: changeControlCompliance, name: 'changeControl' },
      { obj: sampleDocsCompliance, name: 'sampleDocs' },
      { obj: deviationsOosCompliance, name: 'deviationsOos' }
    ];

    allCompliance.forEach(({ obj }) => {
      Object.entries(obj).forEach(([fieldId, isCompliant]) => {
        if (!isCompliant && fieldLabels[fieldId]) {
          nonValidatedFields.push({
            fieldId,
            label: fieldLabels[fieldId].label,
            section: fieldLabels[fieldId].section
          });
        }
      });
    });

    return nonValidatedFields;
    };
    return getNonValidatedFields();
  }, [
    basicInfoCompliance,
    cmoSupplierCompliance,
    approvalDetailsCompliance,
    qaaStatusCompliance,
    storageInfoCompliance,
    countryRequirementsCompliance,
    changeControlCompliance,
    sampleDocsCompliance,
    deviationsOosCompliance
  ]);

  // ALL HOOKS ABOVE THIS LINE - Early returns below are safe now

  // Show full-page loading state while fetching saved data from Neo4j
  // Only show in real data mode with a session (skip in mock/test mode)
  if (savedStateLoading && sessionId && useRealData) {
    return (
      <div className="min-h-[500px] flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 relative mb-6">
            {/* Outer ring */}
            <div className="absolute inset-0 border-4 border-green-200 rounded-full"></div>
            {/* Spinning segment */}
            <div
              className="absolute inset-0 border-4 border-transparent border-t-green-600 rounded-full animate-spin"
              style={{ animationDuration: '1s' }}
            ></div>
            {/* Inner icon */}
            <div className="absolute inset-0 flex items-center justify-center">
              <FileText className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Batch Record Review</h3>
          <p className="text-sm text-gray-500">Retrieving saved submission data...</p>
        </div>
      </div>
    );
  }

  // Handle loading and error states for real data
  if (useRealData && formLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  // Safety check - ensure autoPopulatedBRR is valid before rendering
  // Note: productMarketData is optional since older saved drafts may lack it; we handle it with optional chaining
  if (!autoPopulatedBRR || !autoPopulatedBRR.sapIdentityBatch || !autoPopulatedBRR.approvalDetails) {
    console.error('autoPopulatedBRR is invalid:', autoPopulatedBRR);
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-3" />
          <p className="text-red-600 font-semibold">Error loading form data</p>
          <p className="text-gray-600 text-sm mt-2">The form data structure is invalid. Please try again.</p>
        </div>
      </div>
    );
  }

  // Show warning banner if real data failed, but continue with mock data
  const showDataWarning = useRealData && (formError || !apiFormData);

  // Function to scroll to a field
  const scrollToField = (fieldId: string, index?: number) => {
    const element = document.getElementById(`field-${fieldId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Flash the field to highlight it
      element.classList.add('ring-4', 'ring-green-400', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-green-400', 'ring-opacity-50');
      }, 2000);
    }
    if (typeof index === 'number') {
      setCurrentFieldIndex(index);
    }
    setShowNonValidatedDropdown(false);
  };

  // Navigate to next non-validated field
  const goToNextField = () => {
    if (nonValidatedFields.length === 0) return;
    const nextIndex = (currentFieldIndex + 1) % nonValidatedFields.length;
    setCurrentFieldIndex(nextIndex);
    scrollToField(nonValidatedFields[nextIndex].fieldId);
  };

  // Navigate to previous non-validated field
  const goToPreviousField = () => {
    if (nonValidatedFields.length === 0) return;
    const prevIndex = currentFieldIndex === 0 ? nonValidatedFields.length - 1 : currentFieldIndex - 1;
    setCurrentFieldIndex(prevIndex);
    scrollToField(nonValidatedFields[prevIndex].fieldId);
  };

  // Scroll to top of page
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to Actions section
  const scrollToActions = () => {
    const element = document.getElementById('actions-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Display values for header
  const displayBatchNumber = batchId ? formatBatchId(batchId) : 'Not Selected';
  // Use selectedLotIds if available (multiple lots), otherwise fall back to single inspectionLotId or API data
  const displayInspectionLots = selectedLotIds && selectedLotIds.length > 0
    ? selectedLotIds
    : (autoPopulatedBRR?.sapIdentityBatch?.inspectionLotNumber?.value || inspectionLotId)
      ? [autoPopulatedBRR?.sapIdentityBatch?.inspectionLotNumber?.value || inspectionLotId || '']
      : [];
  const displayProductName = `${autoPopulatedBRR?.productMarketData?.productName?.value || ''} ${autoPopulatedBRR?.productMarketData?.packSize?.value || ''} ${autoPopulatedBRR?.productMarketData?.dosageForm?.value || ''}`.trim() || 'No Data';

  return (
    <div className="space-y-6">
      {/* Read-Only Banner */}
      {isReadOnly && (
        <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <Eye size={16} />
          <span>You are viewing this BRR in read-only mode. Only the owner can edit.</span>
        </div>
      )}

      {/* Form Header */}
      <div id="form-header-top" className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-bold text-gray-900">Batch Record Review</h2>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                Auto-Populated
              </span>
              {/* Priority badge with admin override */}
              {isAdmin && !isReadOnly ? (
                <select
                  value={priority}
                  onChange={(e) => onPriorityChange?.(e.target.value as 'low' | 'medium' | 'high')}
                  className={`px-3 py-1 rounded text-sm font-medium border cursor-pointer ${
                    priority === 'high' ? 'bg-red-100 text-red-800 border-red-200' :
                    priority === 'medium' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                    'bg-green-100 text-green-800 border-green-200'
                  }`}
                >
                  <option value="high">High Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="low">Low Priority</option>
                </select>
              ) : (
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded text-sm font-medium ${
                  priority === 'high' ? 'bg-red-100 text-red-800' :
                  priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {priority === 'high' ? 'High' : priority === 'medium' ? 'Medium' : 'Low'} Priority
                  {priorityLockedBy && (
                    <svg className="ml-1 w-3.5 h-3.5 inline" fill="currentColor" viewBox="0 0 20 20" aria-label={`Locked by ${priorityLockedBy}`}><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                  )}
                </span>
              )}
            </div>
            <div className="space-y-1">
              <div className="text-base font-semibold text-gray-900 truncate" title={displayProductName}>{displayProductName}</div>
              <div className="flex items-center gap-4 text-gray-500 text-sm">
                <span className="flex items-center gap-1"><Package size={14} /> {displayBatchNumber}</span>
                {displayInspectionLots.length > 0 && (
                  <span className="flex items-center gap-1">
                    <FileCheck size={14} />
                    {displayInspectionLots.length === 1
                      ? `Lot ${displayInspectionLots[0]}`
                      : `Lots: ${displayInspectionLots.join(', ')}`}
                  </span>
                )}
                <span className="flex items-center gap-1"><Truck size={14} /> {batchType === 'indirect' ? 'Indirect Transport' : 'Direct Transport'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Non-Validated Fields Dropdown */}
        {nonValidatedFields.length > 0 && (
          <div className="mt-3 relative">
            <button
              onClick={() => setShowNonValidatedDropdown(!showNonValidatedDropdown)}
              className="w-full px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg text-sm font-medium text-amber-700 hover:bg-amber-100 transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <AlertTriangle size={16} />
                {nonValidatedFields.length} field{nonValidatedFields.length !== 1 ? 's' : ''} require{nonValidatedFields.length === 1 ? 's' : ''} validation
              </span>
              <ChevronDown size={16} className={`transition-transform ${showNonValidatedDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showNonValidatedDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {nonValidatedFields.map((field, index) => (
                  <button
                    key={field.fieldId}
                    onClick={() => scrollToField(field.fieldId, index)}
                    className={`w-full text-left px-4 py-3 hover:bg-green-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      index === currentFieldIndex ? 'bg-green-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{field.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{field.section}</div>
                      </div>
                      {index === currentFieldIndex && (
                        <ChevronRight size={16} className="text-green-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Loading Warning Banner */}
      {showDataWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900 mb-1">
                Using Mock Data
              </h4>
              <p className="text-sm text-amber-800">
                {formError
                  ? `Failed to load real data from inspection lot ${inspectionLotId}: ${formError.message}. Displaying mock data for testing.`
                  : `No data available for inspection lot ${inspectionLotId}. Displaying mock data for testing.`
                }
              </p>
            </div>
          </div>
        </div>
      )}


      {/* Box Folder Notification — only shown when box archive data exists */}
      {boxFolderHref && (
      <div className="bg-white rounded-lg border-2 border-blue-300 shadow-sm p-4">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Package size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-gray-900">Box Folder Created</h3>
              <CheckCircle size={18} className="text-green-600" />
            </div>
            <p className="text-sm text-gray-700 mb-3">
              A Box folder has been automatically created for Batch <span className="font-semibold text-blue-700">{displayBatchNumber}</span> and all supporting documentation has been uploaded.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <a href={boxFolderHref} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors flex items-center gap-2">
                <ExternalLink size={14} />
                Open Box Folder
              </a>
              <span className="text-xs text-gray-500">Box/{displayBatchNumber}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Basic Information Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('basicInfo')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded">
              <FileText size={18} className="text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Basic Information</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {basicInfoCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                basicInfoCount.validated === basicInfoCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {basicInfoCount.validated}/{basicInfoCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('basicInfo') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('basicInfo') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            <ValidatedFieldWithCompliance
              fieldId="productName"
              label="Product Name"
              brrValue={autoPopulatedBRR?.productMarketData?.productName?.value || ''}
              sourceValue={{
                "CARA Product (object_name_value)": autoPopulatedBRR?.productMarketData?.productNameCARA?.value || "",
                "SAP Material (product_name)": autoPopulatedBRR?.productMarketData?.productNameGlobal?.value || ''
              }}
              source="CARA Product (object_name_value) / SAP Material (product_name)"
              brrSourceDoc="Selected Lot"
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="productNameGlobal"
              label="Product Name (Global)"
              sourceOnly
              brrValue={autoPopulatedBRR?.productMarketData?.productNameGlobal?.value || ''}
              sourceValue={autoPopulatedBRR?.productMarketData?.productNameGlobal?.value || ''}
              source="SAP Material (product_name)"
              brrSourceDoc="SAP"
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="destinationCountry"
              label="Destination Country"
              brrValue={String(fieldOverrides.destinationCountry?.value ?? autoPopulatedBRR?.productMarketData?.targetCountry?.value ?? '')}
              sourceValue={(() => {
                const sap = autoPopulatedBRR?.productMarketData?.targetCountrySAP?.value || "";
                const cara = autoPopulatedBRR?.productMarketData?.marketCountryName?.value || "";
                if (sap && cara) return { "SAP": sap, "CARA": cara };
                if (sap) return sap;
                if (cara) return cara;
                return "";
              })()}
              source={(() => {
                const sap = autoPopulatedBRR?.productMarketData?.targetCountrySAP?.value || "";
                const cara = autoPopulatedBRR?.productMarketData?.marketCountryName?.value || "";
                if (sap && cara) return "SAP / CARA";
                if (sap) return "SAP";
                if (cara) return "CARA";
                return "SAP";
              })()}
              brrSourceDoc={fieldOverrides.destinationCountry?.source || autoPopulatedBRR?.productMarketData?.targetCountry?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="packSize"
              label="Pack Size"
              brrValue={autoPopulatedBRR?.productMarketData?.packSize?.value || ''}
              sourceValue={{
                "SAP Material (package_size)": autoPopulatedBRR?.productMarketData?.packSizeSAP?.value || "",
                "CARA Product (pack_quantity)": autoPopulatedBRR?.productMarketData?.packSizeCARA?.value || ""
              }}
              source="SAP Material (package_size) / CARA Product (pack_quantity)"
              brrSourceDoc={fieldOverrides.packSize?.source || autoPopulatedBRR?.productMarketData?.packSize?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="dosageForm"
              label="Dosage Form"
              brrValue={autoPopulatedBRR?.productMarketData?.dosageForm?.value || ''}
              sourceValue={{
                "SAP Material (dosage_form)": autoPopulatedBRR?.productMarketData?.dosageFormSAP?.value || "",
                "CARA Product (dosage_form)": autoPopulatedBRR?.productMarketData?.dosageFormCARA?.value || ""
              }}
              source="SAP Material (dosage_form) / CARA Product (dosage_form)"
              brrSourceDoc={fieldOverrides.dosageForm?.source || autoPopulatedBRR?.productMarketData?.dosageForm?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="doseStrength"
              label="Dose Strength"
              brrValue={autoPopulatedBRR?.productMarketData?.doseStrength?.value || ''}
              sourceValue={{
                "SAP Material (strength)": autoPopulatedBRR?.productMarketData?.doseStrengthSAP?.value || "",
                "CARA Product (strength)": autoPopulatedBRR?.productMarketData?.doseStrengthCARA?.value || ""
              }}
              source="SAP Material (strength) / CARA Product (strength)"
              brrSourceDoc={fieldOverrides.doseStrength?.source || autoPopulatedBRR?.productMarketData?.doseStrength?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            <ValidatedFieldWithCompliance
              fieldId="materialSKU"
              label="Material Number"
              brrValue={String(fieldOverrides.materialSKU?.value ?? autoPopulatedBRR?.productMarketData?.materialSKU?.value ?? '')}
              sourceValue={String(autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value || "")}
              source="SAP Material (material_id)"
              brrSourceDoc={fieldOverrides.materialSKU?.source || autoPopulatedBRR?.productMarketData?.materialSKU?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
            {/* Batch Quantity - shows per-lot quantities when multiple lots selected */}
            {(() => {
              const sapLotQuantities = autoPopulatedBRR?.sapIdentityBatch?.sapLotQuantities;
              const unit = String(autoPopulatedBRR?.sapIdentityBatch?.unitOfMeasure?.value || '');
              const hasMultipleLots = sapLotQuantities && Array.isArray(sapLotQuantities) && sapLotQuantities.length > 1;
              const formatQtyWithUnit = (val: string | boolean) => {
                const formatted = formatNumber(val);
                return unit && formatted ? `${formatted} ${unit}` : formatted;
              };

              if (hasMultipleLots) {
                // Build per-lot source values and compute sum
                const sourceValueObj: Record<string, string> = {};
                let totalQuantity = 0;
                sapLotQuantities.forEach((lq: { lotId: string; quantity: number | null }) => {
                  const qty = lq.quantity ?? 0;
                  totalQuantity += qty;
                  const formattedQty = formatNumber(String(qty));
                  sourceValueObj[`Lot ${lq.lotId}`] = unit ? `${formattedQty} ${unit}` : formattedQty;
                });
                const sourceStr = sapLotQuantities.map((lq: { lotId: string; quantity: number | null }) => `Lot ${lq.lotId}`).join(' / ');
                const totalFormatted = formatNumber(String(totalQuantity));
                const subtitleText = unit ? `Total: ${totalFormatted} ${unit}` : `Total: ${totalFormatted}`;

                return (
                  <ValidatedFieldWithCompliance
                    fieldId="batchQuantity"
                    label="Batch Quantity"
                    brrValue={fieldOverrides.batchQuantity?.value ?? autoPopulatedBRR?.sapIdentityBatch?.batchQuantity?.value ?? ''}
                    sourceValue={sourceValueObj}
                    source={sourceStr}
                    brrSourceDoc={fieldOverrides.batchQuantity?.source || autoPopulatedBRR?.sapIdentityBatch?.batchQuantity?.source || 'Unknown'}
                    onComplianceChange={handleBasicInfoComplianceChange}
                    complianceOverrides={complianceOverrides}
                    fieldOverrides={fieldOverrides}
                    setFieldOverrides={setFieldOverrides}
                    subtitle={subtitleText}
                    displayFormatter={formatQtyWithUnit}
                  />
                );
              }

              // Single lot
              return (
                <ValidatedFieldWithCompliance
                  fieldId="batchQuantity"
                  label="Batch Quantity"
                  brrValue={fieldOverrides.batchQuantity?.value ?? autoPopulatedBRR?.sapIdentityBatch?.batchQuantity?.value ?? ''}
                  sourceValue={autoPopulatedBRR?.sapIdentityBatch?.batchQuantitySAP?.value || ''}
                  source="SAP Lot (lot_quantity)"
                  brrSourceDoc={fieldOverrides.batchQuantity?.source || autoPopulatedBRR?.sapIdentityBatch?.batchQuantity?.source || 'Unknown'}
                  onComplianceChange={handleBasicInfoComplianceChange}
                  complianceOverrides={complianceOverrides}
                  fieldOverrides={fieldOverrides}
                  setFieldOverrides={setFieldOverrides}
                  displayFormatter={formatQtyWithUnit}
                />
              );
            })()}
            <ValidatedFieldWithCompliance
              fieldId="cmoFpBatchId"
              label="CMO FP Batch ID"
              brrValue={String(fieldOverrides.cmoFpBatchId?.value ?? autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value ?? '')}
              sourceValue={autoPopulatedBRR?.sapIdentityBatch?.cmoExternalBatchSAP?.value || ''}
              source="SAP Batch (vendor_batch_id)"
              brrSourceDoc={fieldOverrides.cmoFpBatchId?.source || autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.source || 'Unknown'}
              onComplianceChange={handleBasicInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />
          </div>
        )}
      </div>

      {/* Materials Used Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('materialsUsed')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded">
              <Package size={18} className="text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Materials Used</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {materialsUsed.length > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                materialsUsedCount.total > 0 && materialsUsedCount.validated === materialsUsedCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {materialsUsedCount.validated}/{materialsUsedCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('materialsUsed') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('materialsUsed') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
            {materialsUsed.length === 0 && !showAddMaterialForm && (
              <p className="text-sm text-gray-500 italic">No materials used entries. Click "Add Material" to add one manually.</p>
            )}
            {materialsUsed.map((entry) => {
              const fieldId = `materialsUsed_${entry.id}`;
              const isCompliant = materialsUsedCompliance[fieldId] === true || materialsUsedCompliance[fieldId] === 'compliant';
              return (
                <div key={entry.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Material</label>
                      <span className="text-sm text-gray-900">{entry.material || '—'}</span>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Vendor Batch ID</label>
                      <span className="text-sm font-mono text-gray-900">{entry.vendorBatchId || '—'}</span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                    entry.source === 'manual' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {entry.source === 'manual' ? 'Manual' : 'SAP'}
                  </span>
                  <select
                    data-testid={`materials-used-compliance-${entry.id}`}
                    value={isCompliant ? 'compliant' : 'non-compliant'}
                    onChange={(e) => {
                      const newIsCompliant = e.target.value === 'compliant';
                      handleMaterialsUsedComplianceChange(fieldId, newIsCompliant);
                    }}
                    className={`flex-shrink-0 px-2 py-1 text-xs font-medium border rounded focus:outline-none focus:ring-2 bg-white ${
                      isCompliant
                        ? 'border-green-300 text-green-700 focus:ring-green-200'
                        : 'border-red-300 text-red-700 focus:ring-red-200'
                    }`}
                  >
                    <option value="compliant" className="bg-white text-gray-900">Compliant</option>
                    <option value="non-compliant" className="bg-white text-gray-900">Non-Compliant</option>
                  </select>
                  <button
                    onClick={() => {
                      setMaterialsUsed(prev => prev.filter(m => m.id !== entry.id));
                      const newCompliance = { ...materialsUsedCompliance };
                      delete newCompliance[fieldId];
                      setMaterialsUsedCompliance(newCompliance);
                      onDirtyChange?.(true);
                    }}
                    className="flex-shrink-0 p-1 rounded hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500"
                    title="Remove material"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
            {showAddMaterialForm ? (
              <div className="p-3 bg-white rounded-lg border border-dashed border-purple-300 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Material Type</label>
                    <select
                      value={newMaterialEntry.material}
                      onChange={(e) => { const val = e.target.value; setNewMaterialEntry(prev => ({ ...prev, material: val })); }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="">Select material type…</option>
                      {MATERIAL_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Vendor Batch ID</label>
                    <input
                      type="text"
                      value={newMaterialEntry.vendorBatchId}
                      onChange={(e) => { const val = e.target.value; setNewMaterialEntry(prev => ({ ...prev, vendorBatchId: val })); }}
                      className="w-full px-2 py-1.5 text-sm font-mono border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="e.g. 25J84B"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (newMaterialEntry.material && newMaterialEntry.vendorBatchId) {
                        const newEntryId = `manual-${Date.now()}`;
                        const newEntry: MaterialUsedEntry = {
                          id: newEntryId,
                          material: newMaterialEntry.material,
                          vendorBatchId: newMaterialEntry.vendorBatchId,
                          source: 'manual',
                        };
                        setMaterialsUsed(prev => [...prev, newEntry]);
                        handleMaterialsUsedComplianceChange(`materialsUsed_${newEntryId}`, true);
                        setNewMaterialEntry({ material: '', vendorBatchId: '' });
                        setShowAddMaterialForm(false);
                        onDirtyChange?.(true);
                      }
                    }}
                    disabled={!newMaterialEntry.material || !newMaterialEntry.vendorBatchId}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMaterialForm(false);
                      setNewMaterialEntry({ material: '', vendorBatchId: '' });
                    }}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddMaterialForm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
              >
                <Plus size={14} />
                Add Material
              </button>
            )}
          </div>
        )}
      </div>

      {/* BRR Creation Basis Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">BRR Creation Basis (Select all that apply)</h3>
        <div className="space-y-3">
          {[
            { value: 'complete-sample', label: 'Complete sample document comparison' },
            { value: 'partial-sample', label: 'Partially completed sample document comparison' },
            { value: 'compliance-statement', label: 'Compliance statement available (as a substitute)' },
            { value: 'qaa-extract', label: 'Extract from batch documentation in accordance with QAA' },
            { value: 'complete-batch', label: 'Complete batch documentation' }
          ].map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={brrCreationBasis.includes(option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setBrrCreationBasis(prev => [...prev, option.value]);
                  } else {
                    setBrrCreationBasis(prev => prev.filter(v => v !== option.value));
                  }
                }}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* CP Internal Documents Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">CP Internal Documents (Select all that apply)</h3>
        <div className="space-y-3">
          {CP_INTERNAL_DOC_ITEMS.map((option) => (
            <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={cpInternalDocs.includes(option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setCpInternalDocs(prev => [...prev, option.value]);
                  } else {
                    setCpInternalDocs(prev => prev.filter(v => v !== option.value));
                  }
                }}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Quality Management System Compliance Header */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-4">
        <h2 className="text-xl font-bold text-green-900 flex items-center gap-2">
          <Shield size={24} className="text-green-600" />
          Quality Management System Compliance
        </h2>
      </div>

      {/* Supplier Status Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('supplierStatus')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded">
              <Building2 size={18} className="text-green-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Supplier Status</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {cmoSupplierCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                cmoSupplierCount.validated === cmoSupplierCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {cmoSupplierCount.validated}/{cmoSupplierCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('supplierStatus') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('supplierStatus') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            {/* Supplier Dropdown */}
            <div id="field-supplierName" className="p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-all">
              {/* Source indicator */}
              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                <Database size={10} />
                <span>Source: SAP</span>
              </div>
              <ManufacturerDropdown
                label="CMO Name"
                value={String(fieldOverrides.supplierName?.value ?? '')}
                options={
                  autoPopulatedBRR.cmoSupplierQual?.cmoNameSAPList?.map((supplier: { name: { value: string; source: string }; validToDate: { value: string; source: string } }) => ({
                    value: supplier.name.value,
                    source: supplier.name.source
                  })) || []
                }
                onChange={(opt) => {
                  const supplierOptions = autoPopulatedBRR.cmoSupplierQual?.cmoNameSAPList || [];
                  const originalValue = supplierOptions.length > 0 ? supplierOptions[0].name.value : '';

                  // Find the selected supplier to get validToDate
                  const selectedSupplier = supplierOptions.find(
                    (s: { name: { value: string; source: string }; validToDate: { value: string; source: string } }) =>
                      s.name.value === opt.value
                  );

                  setFieldOverrides(prev => {
                    const next: FieldOverrides = {
                      ...prev,
                      supplierName: { value: opt.value, originalValue }
                    };

                    // Clear validToDate override when supplier changes
                    // The Valid To Date will auto-populate from the selected supplier via the brrValue prop
                    // An override should only exist if the user manually edits the date
                    delete next.validToDate;

                    if (opt.value !== NOT_FOUND_VALUE) {
                      delete next.supplierNameManualEntry;
                    }
                    return next;
                  });
                }}
              />

              {/* Manual Entry Input - shown when "Value not in list" selected */}
              {fieldOverrides.supplierName?.value === NOT_FOUND_VALUE && (
                <div className="mt-2">
                  <input
                    type="text"
                    value={String(fieldOverrides.supplierNameManualEntry?.value ?? '')}
                    onChange={(e) => {
                      setFieldOverrides(prev => ({
                        ...prev,
                        supplierNameManualEntry: {
                          value: e.target.value,
                          originalValue: ''
                        }
                      }));
                    }}
                    placeholder="Enter supplier name"
                    className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
                  />
                </div>
              )}

              {/* Compliance Validation */}
              <div className="flex items-center gap-3 mt-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Validation Status</label>
                  <select
                    value={cmoSupplierCompliance.supplierName === false ? 'non-compliant' : 'compliant'}
                    onChange={(e) => {
                      const isCompliant = e.target.value === 'compliant';
                      handleCmoSupplierComplianceChange('supplierName', isCompliant);
                    }}
                    className={`w-full px-3 py-2 border rounded text-sm font-medium focus:outline-none focus:ring-2 bg-white ${
                      cmoSupplierCompliance.supplierName === false
                        ? 'border-red-300 text-red-700 focus:ring-red-200'
                        : 'border-green-300 text-green-700 focus:ring-green-200'
                    }`}
                  >
                    <option value="compliant" className="bg-white text-gray-900">Complies</option>
                    <option value="non-compliant" className="bg-white text-gray-900">Does not correspond</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Valid To Date - Validated Field with Compliance */}
            <ValidatedFieldWithCompliance
              fieldId="validToDate"
              label="Valid To Date"
              sourceOnly
              brrValue={String(
                (fieldOverrides.validToDate?.value ??
                (() => {
                  const selectedSupplierName = fieldOverrides.supplierName?.value;
                  if (!selectedSupplierName || selectedSupplierName === NOT_FOUND_VALUE) {
                    return '';
                  }
                  const selectedSupplier = autoPopulatedBRR.cmoSupplierQual?.cmoNameSAPList?.find(
                    (s: { name: { value: string; source: string }; validToDate: { value: string; source: string } }) =>
                      s.name.value === selectedSupplierName
                  );
                  return selectedSupplier?.validToDate?.value ?? '';
                })()) ||
                'No Valid To Date found'
              )}
              sourceValue={String(
                (() => {
                  const selectedSupplierName = fieldOverrides.supplierName?.value;
                  if (!selectedSupplierName || selectedSupplierName === NOT_FOUND_VALUE) {
                    return '';
                  }
                  const selectedSupplier = autoPopulatedBRR.cmoSupplierQual?.cmoNameSAPList?.find(
                    (s: { name: { value: string; source: string }; validToDate: { value: string; source: string } }) =>
                      s.name.value === selectedSupplierName
                  );
                  return selectedSupplier?.validToDate?.value || '';
                })() ||
                'No Valid To Date found'
              )}
              source="SAP"
              brrSourceDoc={
                fieldOverrides.validToDate?.source || (() => {
                  const selectedSupplierName = fieldOverrides.supplierName?.value;
                  if (!selectedSupplierName || selectedSupplierName === NOT_FOUND_VALUE) {
                    return 'SAP';
                  }
                  const selectedSupplier = autoPopulatedBRR.cmoSupplierQual?.cmoNameSAPList?.find(
                    (s: { name: { value: string; source: string }; validToDate: { value: string; source: string } }) =>
                      s.name.value === selectedSupplierName
                  );
                  return selectedSupplier?.validToDate?.source || 'SAP';
                })()
              }
              onComplianceChange={handleCmoSupplierComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />

            {/* Next Periodic Review - User-editable date */}
            <div className="p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-all">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-green-600" />
                  Next Periodic Review
                </div>
              </label>
              <input
                type="date"
                value={nextPeriodicReview}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setNextPeriodicReview(newDate);
                  setFieldOverrides(prev => ({
                    ...prev,
                    nextPeriodicReview: {
                      value: newDate,
                      originalValue: ''
                    }
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <div className="mt-2 text-xs text-gray-500">
                Select the date for the next periodic review
              </div>
            </div>
          </div>
        )}
      </div>

      {/* QAA Status Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('qaaStatus')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded ${qaaData?.found ? 'bg-green-100' : qaaError || qaaData?.file_missing ? 'bg-red-100' : 'bg-gray-100'}`}>
              <Shield size={18} className={qaaData?.found ? 'text-green-600' : qaaError || qaaData?.file_missing ? 'text-red-600' : 'text-gray-600'} />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">QAA Status</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {qaaLoading ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-blue-100 text-blue-700">
                Checking...
              </span>
            ) : qaaError ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-red-100 text-red-700">
                Error
              </span>
            ) : qaaData?.found && qaaStatusCount.total > 0 ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-green-100 text-green-700">
                {qaaStatusCount.validated}/{qaaStatusCount.total} validated
              </span>
            ) : qaaData?.found ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-green-100 text-green-700">
                Found
              </span>
            ) : qaaData?.file_missing ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-red-100 text-red-700">
                File Missing
              </span>
            ) : qaaData ? (
              <span className="px-2 py-1 text-xs rounded font-medium bg-amber-100 text-amber-700">
                No Match
              </span>
            ) : (
              <span className="px-2 py-1 text-xs rounded font-medium bg-gray-100 text-gray-500">
                Not Checked
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('qaaStatus') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('qaaStatus') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            {qaaLoading ? (
              <div className="text-center py-6">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Checking QAA status...</p>
              </div>
            ) : qaaError ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-600" />
                  <span className="text-sm font-medium text-red-800">QAA check failed</span>
                </div>
                <p className="text-sm text-red-600 mt-1">{qaaError.message}</p>
              </div>
            ) : qaaData?.file_missing ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-red-600" />
                  <span className="text-sm font-medium text-red-800">QAA contract file not found in Box</span>
                </div>
              </div>
            ) : qaaData?.found ? (
              <>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="text-sm font-medium text-green-800">Verified</span>
                    {qaaData.confidence && (
                      <span className="text-xs text-green-600">({Math.round(qaaData.confidence * 100)}% confidence)</span>
                    )}
                  </div>
                </div>
                <ValidatedFieldWithCompliance
                  fieldId="qaaStatus"
                  label="QAA Status"
                  sourceOnly
                  brrValue={(() => {
                    const val = [
                      qaaData.matched_firma ? `Firma: ${qaaData.matched_firma}` : null,
                      qaaData.signing_date ? `Signed: ${qaaData.signing_date}` : null,
                      qaaData.review_period ? `Review period: ${qaaData.review_period}` : null,
                      qaaData.review_date ? `Review date: ${qaaData.review_date}` : null,
                    ].filter(Boolean).join('\n') || 'No contract information';
                    return val;
                  })()}
                  sourceValue={[
                    qaaData.matched_firma ? `Firma: ${qaaData.matched_firma}` : null,
                    qaaData.signing_date ? `Signed: ${qaaData.signing_date}` : null,
                    qaaData.review_period ? `Review period: ${qaaData.review_period}` : null,
                    qaaData.review_date ? `Review date: ${qaaData.review_date}` : null,
                  ].filter(Boolean).join('\n') || 'No contract information'}
                  source="QAA Repository (Status Verträge.xlsx)"
                  onComplianceChange={handleQaaStatusComplianceChange}
                  complianceOverrides={complianceOverrides}
                  fieldOverrides={fieldOverrides}
                  setFieldOverrides={setFieldOverrides}
                />
              </>
            ) : qaaData ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">
                    No QAA contract match found for &quot;{qaaCmoName}&quot;
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">QAA status has not been checked yet.</p>
            )}

            {/* Box file link */}
            <a
              href={qaaData?.box_file_url || 'https://app.box.com/file/1231201003809'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
            >
              <ExternalLink size={14} />
              {qaaData?.file_missing ? 'Open Box' : 'Open Status Verträge.xlsx in Box'}
            </a>
          </div>
        )}
      </div>

      {/* Approval Details Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('approvalDetails')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded">
              <FileCheck size={18} className="text-blue-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Approval Details</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {approvalDetailsCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                approvalDetailsCount.validated === approvalDetailsCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {approvalDetailsCount.validated}/{approvalDetailsCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('approvalDetails') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('approvalDetails') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            {/* Marketing Authorisation Number */}
            <ValidatedFieldWithCompliance
              fieldId="marketAuthorisationNumber"
              label="Marketing Authorisation Number"
              brrValue={String((fieldOverrides.marketAuthorisationNumber?.value ?? (Array.isArray(autoPopulatedBRR.approvalDetails.marketAuthorisationNumber) && autoPopulatedBRR.approvalDetails.marketAuthorisationNumber.length > 0 ? autoPopulatedBRR.approvalDetails.marketAuthorisationNumber[0].value : '')) || 'No Marketing Authorisation found')}
              sourceValue={String(autoPopulatedBRR.approvalDetails.marketAuthorisationNumberCARA?.value || 'No CARA Marketing Authorisation found')}
              source="CARA"
              subtitle={`Package Level: ${autoPopulatedBRR.approvalDetails.packageLevelValue?.value != null && autoPopulatedBRR.approvalDetails.packageLevelValue.value !== '' ? (String(autoPopulatedBRR.approvalDetails.packageLevelValue.value).toLowerCase() === 'true' ? 'Yes' : 'No') : 'N/A'}`}
              brrSourceDoc={fieldOverrides.marketAuthorisationNumber?.source || (Array.isArray(autoPopulatedBRR.approvalDetails.marketAuthorisationNumber) && autoPopulatedBRR.approvalDetails.marketAuthorisationNumber.length > 0 ? autoPopulatedBRR.approvalDetails.marketAuthorisationNumber[0].source : null) || 'Extracted from Documents'}
              onComplianceChange={handleApprovalDetailsComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* Authorisation Status */}
            <ValidatedFieldWithCompliance
              fieldId="authorisationStatus"
              label="Authorisation Status"
              sourceOnly
              brrValue={String(fieldOverrides.authorisationStatus?.value ?? autoPopulatedBRR.approvalDetails.authorisationStatus?.value ?? '')}
              sourceValue={String(autoPopulatedBRR.approvalDetails.authorisationStatus?.value || '')}
              source="CARA"
              brrSourceDoc={fieldOverrides.authorisationStatus?.source || autoPopulatedBRR.approvalDetails.authorisationStatus?.source || 'CARA'}
              onComplianceChange={handleApprovalDetailsComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* Validity Period End */}
            <ValidatedFieldWithCompliance
              fieldId="validityPeriodEndValue"
              label="Validity Period End"
              sourceOnly
              brrValue={String(fieldOverrides.validityPeriodEndValue?.value ?? autoPopulatedBRR.approvalDetails.validityPeriodEndValue?.value ?? '')}
              sourceValue={String(autoPopulatedBRR.approvalDetails.validityPeriodEndValue?.value || '')}
              source="CARA"
              brrSourceDoc={fieldOverrides.validityPeriodEndValue?.source || autoPopulatedBRR.approvalDetails.validityPeriodEndValue?.source || 'CARA'}
              onComplianceChange={handleApprovalDetailsComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* API Name */}
            <ValidatedFieldWithCompliance
              fieldId="apiName"
              label="API Name"
              sourceOnly
              brrValue={String((fieldOverrides.apiName?.value ?? (Array.isArray(autoPopulatedBRR.approvalDetails.apiName) && autoPopulatedBRR.approvalDetails.apiName.length > 0 ? autoPopulatedBRR.approvalDetails.apiName[0].value : '')) || 'No API Name found')}
              sourceValue={String(Array.isArray(autoPopulatedBRR.approvalDetails.apiName) && autoPopulatedBRR.approvalDetails.apiName.length > 0 ? autoPopulatedBRR.approvalDetails.apiName[0].value : '') || 'No API Name found'}
              source="CARA"
              brrSourceDoc={fieldOverrides.apiName?.source || (Array.isArray(autoPopulatedBRR.approvalDetails.apiName) && autoPopulatedBRR.approvalDetails.apiName.length > 0 ? autoPopulatedBRR.approvalDetails.apiName[0].source : 'CARA')}
              onComplianceChange={handleApprovalDetailsComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* Manufacturer dropdowns — data-driven to avoid 6 near-identical blocks */}
            {([
              { fieldId: 'apiManufacturer', label: 'API Manufacturer' },
              { fieldId: 'bulkManufacturer', label: 'Bulk Manufacturer' },
              { fieldId: 'qcTestingSite', label: 'QC Testing Site' },
              { fieldId: 'primaryPackagingSite', label: 'Primary Packaging Site' },
              { fieldId: 'secondaryPackagingSite', label: 'Secondary Packaging Site' },
              { fieldId: 'releaseSite', label: 'Certification of batch by manufacturer' },
            ] as const).map(({ fieldId, label }) => {
              const manualEntryKey = `${fieldId}ManualEntry`;
              const options = autoPopulatedBRR.approvalDetails[fieldId] || [];
              const originalValue = Array.isArray(options) && options.length > 0 ? options[0].value : '';

              return (
                <div key={fieldId} id={`field-${fieldId}`} className="p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-all">
                  <ManufacturerDropdown
                    label={label}
                    value={String(fieldOverrides[fieldId]?.value || '')}
                    options={options}
                    onChange={(opt) => {
                      setFieldOverrides(prev => {
                        const next: FieldOverrides = {
                          ...prev,
                          [fieldId]: {
                            value: opt.value,
                            originalValue,
                          }
                        };
                        if (opt.value !== NOT_FOUND_VALUE) {
                          delete next[manualEntryKey];
                        }
                        return next;
                      });
                    }}
                  />
                  {fieldOverrides[fieldId]?.value === NOT_FOUND_VALUE && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={String(fieldOverrides[manualEntryKey]?.value || '')}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFieldOverrides(prev => ({
                            ...prev,
                            [manualEntryKey]: {
                              value: val,
                              originalValue: ''
                            }
                          }));
                        }}
                        placeholder="No Data"
                        className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Validation Status</label>
                      <select
                        value={isCompliantValue(complianceOverrides[fieldId]) ? 'compliant' : 'non-compliant'}
                        onChange={(e) => {
                          const isCompliant = e.target.value === 'compliant';
                          handleApprovalDetailsComplianceChange(fieldId, isCompliant);
                        }}
                        className={`w-full px-3 py-2 border rounded text-sm font-medium focus:outline-none focus:ring-2 bg-white ${
                          isCompliantValue(complianceOverrides[fieldId])
                            ? 'border-green-300 text-green-700 focus:ring-green-200'
                            : 'border-red-300 text-red-700 focus:ring-red-200'
                        }`}
                      >
                        <option value="compliant" className="bg-white text-gray-900">Complies</option>
                        <option value="non-compliant" className="bg-white text-gray-900">Does not correspond</option>
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* GMP Annex 16: Batch Release Site Confirmation */}
            <div className="mt-6 pt-4 border-t border-gray-200" style={{ textAlign: 'left' }}>
              <h4 className="text-sm font-semibold text-gray-800 mb-3">
                Confirmation of Batch Release Site according to GMP Annex 16
              </h4>
              <div className="space-y-3">
                {/* Certification of batch by manufacturer */}
                <label htmlFor="batchReleaseSiteConfirmed" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    id="batchReleaseSiteConfirmed"
                    checked={batchReleaseSiteConfirmed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setBatchReleaseSiteConfirmed(checked);
                      if (checked) {
                        setBatchReleaseSiteCheplapharmConfirmed(false);
                      }
                      if (onDirtyChange) onDirtyChange(true);
                    }}
                    disabled={isReadOnly}
                  />
                  <span className="text-sm text-gray-700">Certification of batch by manufacturer</span>
                </label>

                {/* Certification of batch by Cheplapharm */}
                <label htmlFor="batchReleaseSiteCheplapharmConfirmed" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    id="batchReleaseSiteCheplapharmConfirmed"
                    checked={batchReleaseSiteCheplapharmConfirmed}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setBatchReleaseSiteCheplapharmConfirmed(checked);
                      if (checked) {
                        setBatchReleaseSiteConfirmed(false);
                        setSelectedReleaseSites([]);
                      }
                      if (onDirtyChange) onDirtyChange(true);
                    }}
                    disabled={isReadOnly}
                  />
                  <span className="text-sm text-gray-700">Certification of batch by Cheplapharm</span>
                </label>

                {/* Display selectable CARA release sites only when "by manufacturer" is checked */}
                {batchReleaseSiteConfirmed && (
                  <>
                    {Array.isArray(autoPopulatedBRR.approvalDetails.releaseSite) && autoPopulatedBRR.approvalDetails.releaseSite.length > 0 ? (
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <label className="block text-xs font-medium text-blue-700 mb-1">Release Site (from CARA)</label>
                        <div className="text-sm text-gray-800">
                          {autoPopulatedBRR.approvalDetails.releaseSite.map((site: any, idx: number) => (
                            <label key={idx} htmlFor={`releaseSite-${idx}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginBottom: '4px' }}>
                              <input
                                type="checkbox"
                                id={`releaseSite-${idx}`}
                                checked={selectedReleaseSites.includes(site.value)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedReleaseSites(prev => [...prev, site.value]);
                                  } else {
                                    setSelectedReleaseSites(prev => prev.filter(s => s !== site.value));
                                  }
                                  if (onDirtyChange) onDirtyChange(true);
                                }}
                                style={{ marginTop: '3px' }}
                                disabled={isReadOnly}
                              />
                              <span style={{ textAlign: 'left', overflowWrap: 'break-word', wordBreak: 'break-word', minWidth: 0 }}>{site.value}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="p-3 bg-amber-50 rounded border border-amber-200">
                        <div className="flex items-center gap-2 text-amber-700 text-sm">
                          <AlertCircle size={14} />
                          <span>No release site data found in CARA. Enter details manually below.</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Editable confirmation text */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Additional Notes / Confirmation Details
                  </label>
                  <textarea
                    value={batchReleaseSiteText}
                    onChange={(e) => {
                      setBatchReleaseSiteText(e.target.value);
                      if (onDirtyChange) onDirtyChange(true);
                    }}
                    placeholder="Enter batch release site confirmation details, GMP compliance notes, or any additional information required for Annex 16 certification..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 resize-y"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Storage Conditions Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('storageConditions')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-100 rounded">
              <Package size={18} className="text-cyan-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Storage Conditions</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {storageInfoCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                storageInfoCount.validated === storageInfoCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {storageInfoCount.validated}/{storageInfoCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('storageConditions') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('storageConditions') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            {/* Shelf life as packaged for sale - SAP/CARA comparison */}
            <ValidatedFieldWithCompliance
              fieldId="reportedShelfLife"
              label="Shelf life as packaged for sale (months)"
              brrValue={String(fieldOverrides.reportedShelfLife?.value ?? autoPopulatedBRR?.approvalDetails?.reportedShelfLife?.value ?? '')}
              sourceValue={{
                "SAP": autoPopulatedBRR?.productMarketData?.shelfLifeInMonths?.value || "No SAP data",
                "CARA": autoPopulatedBRR?.approvalDetails?.reportedShelfLife?.value || "No CARA data"
              }}
              source="SAP / CARA"
              brrSourceDoc={fieldOverrides.reportedShelfLife?.source || autoPopulatedBRR?.approvalDetails?.reportedShelfLife?.source || 'Unknown'}
              onComplianceChange={handleStorageInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* Storage Statement - SAP/CARA comparison */}
            <ValidatedFieldWithCompliance
              fieldId="storageStatement"
              label="Storage Statement"
              brrValue={String(fieldOverrides.storageStatement?.value ?? autoPopulatedBRR?.productMarketData?.storageStatement?.value ?? '')}
              sourceValue={{
                "SAP": autoPopulatedBRR?.productMarketData?.temperatureConditions?.value || "No SAP data",
                "CARA": autoPopulatedBRR?.productMarketData?.storageStatement?.value || "No CARA data"
              }}
              source="SAP / CARA"
              brrSourceDoc={fieldOverrides.storageStatement?.source || autoPopulatedBRR?.productMarketData?.storageStatement?.source || 'Unknown'}
              onComplianceChange={handleStorageInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
            {/* Target Storage Location - from Transport Report */}
            <ValidatedFieldWithCompliance
              fieldId="targetStorageLocation"
              label="Target Storage Location"
              brrValue={String(fieldOverrides.targetStorageLocation?.value ?? autoPopulatedBRR?.sapIdentityBatch?.targetStorageLocation?.value ?? '')}
              sourceValue={autoPopulatedBRR?.sapIdentityBatch?.targetStorageLocation?.value || ''}
              source="Transport Report"
              brrSourceDoc={fieldOverrides.targetStorageLocation?.source || autoPopulatedBRR?.sapIdentityBatch?.targetStorageLocation?.source || 'Transport Report'}
              onComplianceChange={handleStorageInfoComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
              defaultNonCompliant
            />
          </div>
        )}
      </div>

      {/* Additional sections can be added here following the same pattern */}

      {/* Additional Country-Specific Requirements Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('countryRequirements')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded">
              <FileText size={18} className="text-orange-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Additional Country-Specific Requirements</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* No validation badge - this section has notes only, no compliance dropdowns */}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('countryRequirements') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('countryRequirements') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Country-Specific Notes
              </label>
              <textarea
                value={countrySpecificReqsText}
                onChange={(e) => {
                  setCountrySpecificReqsText(e.target.value);
                  if (onDirtyChange) onDirtyChange(true);
                }}
                placeholder="Enter any additional country-specific requirements, regulations, or notes relevant to this batch review..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
              />
            </div>
          </div>
        )}
      </div>

      {/* Change Control Procedure Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('changeControl')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded">
              <FileCheck size={18} className="text-green-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Change Control Procedure ({effectiveCCs.length})</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-gray-500">Review change control records</p>
                {effectiveCCs.length > 0 && (() => {
                  const statusCounts: Record<string, number> = {};
                  effectiveCCs.forEach((cc: any) => {
                    const status = cc.status && cc.status !== 'Open' ? cc.status.trim() : 'No Status';
                    statusCounts[status] = (statusCounts[status] || 0) + 1;
                  });
                  return (
                    <>
                      <span className="text-xs text-gray-300">|</span>
                      {Object.entries(statusCounts).map(([status, count]) => (
                        <span
                          key={status}
                          className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                            status === 'Closed' ? 'bg-green-100 text-green-700' :
                            status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            status === 'Initiation' ? 'bg-purple-100 text-purple-700' :
                            status === 'Initial Review' ? 'bg-amber-100 text-amber-700' :
                            status === 'No Status' ? 'bg-gray-100 text-gray-500' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {status}: {count}
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {changeControlCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                changeControlCount.validated === changeControlCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {changeControlCount.validated}/{changeControlCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('changeControl') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('changeControl') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            <div className="flex justify-end">
              <button
                onClick={() => setShowCcSearch(!showCcSearch)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
              >
                <Search size={12} />
                {showCcSearch ? 'Hide Search' : 'Search Change Control'}
              </button>
            </div>

            {showCcSearch && (
              <div className="p-3 bg-blue-50 rounded border border-blue-200 space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={ccSearchQuery}
                    onChange={(e) => setCcSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCcSearch(); }}
                    placeholder="Search by CC number or description"
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleCcSearch}
                    disabled={ccSearchLoading || ccSearchQuery.trim().length < 2}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    {ccSearchLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                    Search
                  </button>
                </div>

                {ccSearchError && (
                  <div className="flex items-center gap-2 text-xs text-red-600">
                    <AlertCircle size={14} />
                    {ccSearchError}
                  </div>
                )}

                {ccSearchResults.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500">{ccSearchResults.length} result(s) found</div>
                    {ccSearchCapped && (
                      <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        <AlertCircle size={14} className="flex-shrink-0" />
                        Results limited to 50. Try narrowing your search with more specific terms.
                      </div>
                    )}
                    <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                    {ccSearchResults.map((cc) => {
                      const alreadyAdded = effectiveCCs.some((existing: any) =>
                        (existing.actionId || existing.id || existing.name) === cc.name
                      );
                      return (
                        <div key={cc.name} className="p-2 bg-white rounded border border-gray-200 flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-sm text-gray-900">{cc.name}</span>
                              <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                cc.status === 'Approved' || cc.status === 'Submitted' ? 'bg-green-100 text-green-700' :
                                cc.status === 'Open' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {cc.status}
                              </span>
                            </div>
                            {(cc.titleValue || cc.description) && <p className="text-xs text-gray-600 truncate">{cc.titleValue || cc.description}</p>}
                            {cc.affectedProducts && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Products: {Array.isArray(cc.affectedProducts) ? cc.affectedProducts.join(', ') : cc.affectedProducts}
                              </p>
                            )}
                          </div>
                          {alreadyAdded ? (
                            <span className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 rounded whitespace-nowrap">
                              Already Added
                            </span>
                          ) : (
                            <button
                              onClick={() => handleAddCcFromSearch(cc)}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded border border-green-200 hover:bg-green-100 transition-colors whitespace-nowrap"
                            >
                              <Plus size={12} />
                              Add to BRR
                            </button>
                          )}
                        </div>
                      );
                    })}
                    </div>
                  </div>
                )}

                {!ccSearchLoading && ccSearchResults.length === 0 && ccSearchQuery.trim().length >= 2 && !ccSearchError && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    No results. Click Search to query change controls.
                  </div>
                )}
              </div>
            )}

            {effectiveCCs.map((cc: any, index: number) => {
              const allActions = cc.actions || [];
              const isActionsExpanded = expandedChangeControlActions.has(index);
              const currentFilter = actionStatusFilter[index] || 'All';
              const filteredActions = currentFilter === 'All'
                ? allActions
                : allActions.filter((a: any) => (a.status ? a.status.trim() : 'No Status') === currentFilter);

              return (
                <div key={cc.stableId} id={`field-changeControl-${cc.stableId}`} className="bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-all">
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0 overflow-hidden" style={{ minWidth: 0, overflow: 'hidden' }}>
                        <div className="flex items-center gap-2 mb-1 flex-wrap" style={{ flexWrap: 'wrap' }}>
                          <span className="font-medium text-gray-900">{cc.actionId}</span>
                          <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                            cc.status === 'Approved' ? 'bg-green-100 text-green-700' :
                            cc.status === 'Submitted' ? 'bg-blue-100 text-blue-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {cc.status}
                          </span>
                          {cc.isManual && cc.source === 'CARA Search' ? (
                            <span className="px-1.5 py-0.5 text-[10px] rounded-full font-medium bg-indigo-100 text-indigo-700">
                              CARA Search
                            </span>
                          ) : cc.isManual ? (
                            <span className="px-1.5 py-0.5 text-[10px] rounded-full font-medium bg-blue-100 text-blue-700">
                              Manually Added
                            </span>
                          ) : null}
                        </div>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>{cc.text}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <Shield size={14} className="text-gray-400" />
                          <span className="text-xs text-gray-500">{cc.source}</span>
                        </div>
                        {cc.matchedVia && (
                          <div className="text-xs text-gray-400 mt-1">Matched via: {cc.matchedVia}</div>
                        )}
                        {cc.affectedMarket && (
                          <div className="text-xs text-gray-400 mt-1">Affected Market: {cc.affectedMarket}</div>
                        )}
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <select
                          value={changeControlCompliance[`cc_${cc.stableId}`] === true ? 'Complies' : 'Does not correspond'}
                          onChange={(e) => handleChangeControlComplianceChange(`cc_${cc.stableId}`, e.target.value === 'Complies')}
                          className={`px-3 py-1.5 text-sm rounded border ${
                            changeControlCompliance[`cc_${cc.stableId}`] === true
                              ? 'border-green-300 bg-green-50 text-green-700'
                              : 'border-red-300 bg-red-50 text-red-700'
                          } font-medium focus:outline-none focus:ring-2 focus:ring-green-500`}
                        >
                          <option value="Complies">Complies</option>
                          <option value="Does not correspond">Does not correspond</option>
                        </select>
                        <button
                          onClick={() => {
                            if (cc.isManual) {
                              setManuallyAddedCCs(prev => prev.filter(c => c.stableId !== cc.stableId));
                            } else {
                              setRemovedCCIds(prev => new Set([...prev, cc.stableId]));
                            }
                          }}
                          className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600 transition-colors"
                          title="Remove this change control"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Validation Notes for this Change Control */}
                    {individualCCNotes[cc.stableId] && individualCCNotes[cc.stableId].length > 0 && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="p-1.5 bg-indigo-100 rounded">
                            <FileText size={14} className="text-indigo-600" />
                          </div>
                          <span className="text-xs font-bold text-indigo-900 uppercase tracking-wide">Validation Notes</span>
                          <span className="text-xs text-gray-500">({individualCCNotes[cc.stableId].length})</span>
                        </div>
                        {individualCCNotes[cc.stableId].map((note, noteIndex) => (
                          <div key={noteIndex} className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-indigo-600 font-medium">{formatDateTime(note.timestamp)}</span>
                                </div>
                                <div className="text-sm text-gray-900 break-words">
                                  {note.text}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to delete this validation note?')) {
                                    setIndividualCCNotes(prev => {
                                      const updated = { ...prev };
                                      updated[cc.stableId] = updated[cc.stableId].filter((_, i) => i !== noteIndex);
                                      return updated;
                                    });
                                  }
                                }}
                                className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600 hover:text-red-700"
                                title="Delete this note"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Validation Notes Button */}
                    <button
                      onClick={() => setShowIndividualCCNoteForm(prev => ({ ...prev, [cc.stableId]: !prev[cc.stableId] }))}
                      className="mt-3 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors flex items-center gap-1"
                    >
                      <FileText size={14} />
                      Add Validation Notes
                    </button>

                    {/* New Note Form */}
                    {showIndividualCCNoteForm[cc.stableId] && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                        <label className="block text-sm font-medium text-gray-900">
                          New Validation Note
                        </label>
                        <textarea
                          value={individualCCNoteText[cc.stableId] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setIndividualCCNoteText(prev => ({ ...prev, [cc.stableId]: value }));
                          }}
                          placeholder="Enter validation note about this change control..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                          rows={3}
                        />
                        <button
                          onClick={() => {
                            const noteText = individualCCNoteText[cc.stableId] || '';
                            if (noteText.trim()) {
                              setIndividualCCNotes(prev => ({
                                ...prev,
                                [cc.stableId]: [...(prev[cc.stableId] || []), { text: noteText, timestamp: new Date().toISOString() }]
                              }));
                              setIndividualCCNoteText(prev => ({ ...prev, [cc.stableId]: '' }));
                              setShowIndividualCCNoteForm(prev => ({ ...prev, [cc.stableId]: false }));
                            }
                          }}
                          disabled={!individualCCNoteText[cc.stableId]?.trim()}
                          className={`w-full py-2 font-medium rounded transition-all flex items-center justify-center gap-2 text-sm ${
                            !individualCCNoteText[cc.stableId]?.trim()
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          <FileText size={14} />
                          Save Note
                        </button>
                      </div>
                    )}

                    {/* Actions Section */}
                    {allActions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <button
                          onClick={() => {
                            setExpandedChangeControlActions(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(index)) {
                                newSet.delete(index);
                              } else {
                                newSet.add(index);
                              }
                              return newSet;
                            });
                          }}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        >
                          <ChevronDown size={16} className={`transition-transform ${isActionsExpanded ? 'rotate-180' : ''}`} />
                          <span>Actions ({allActions.length})</span>
                          {(() => {
                            const actionStatusCounts: Record<string, number> = {};
                            allActions.forEach((a: any) => {
                              const s = a.status ? a.status.trim() : 'No Status';
                              actionStatusCounts[s] = (actionStatusCounts[s] || 0) + 1;
                            });
                            return Object.entries(actionStatusCounts).map(([s, count]) => (
                              <span
                                key={s}
                                className={`px-1.5 py-0.5 text-[10px] rounded-full font-medium ${
                                  s === 'Open' ? 'bg-orange-100 text-orange-700' :
                                  s === 'Closed - Done' ? 'bg-green-100 text-green-700' :
                                  s === 'Closed - Not Applicable' ? 'bg-gray-200 text-gray-600' :
                                  s === 'Initiation' ? 'bg-purple-100 text-purple-700' :
                                  s === 'Cancelled' ? 'bg-red-100 text-red-600' :
                                  s === 'No Status' ? 'bg-gray-100 text-gray-500' :
                                  'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {s}: {count}
                              </span>
                            ));
                          })()}
                        </button>

                        {isActionsExpanded && (
                          <div className="mt-3 space-y-2">
                            {/* Status filter */}
                            <div className="flex flex-wrap gap-1.5">
                              {(['All', ...Array.from(new Set(allActions.map((a: any) => a.status ? a.status.trim() : 'No Status')))] as string[]).map((s) => (
                                <button
                                  key={s}
                                  onClick={(e) => { e.stopPropagation(); setActionStatusFilter(prev => ({ ...prev, [index]: s })); }}
                                  className={`px-2 py-0.5 text-[11px] rounded-full font-medium border transition-colors ${
                                    currentFilter === s
                                      ? 'bg-gray-800 text-white border-gray-800'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                                  }`}
                                >
                                  {s}{s === 'All' ? ` (${allActions.length})` : ''}
                                </button>
                              ))}
                            </div>
                            {/* Scrollable action list */}
                            <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                              {filteredActions.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No actions match the selected filter</p>
                              )}
                              {filteredActions.map((action: any, actionIndex: number) => (
                                <div key={actionIndex} className="p-3 bg-gray-50 rounded border border-gray-200">
                                  <div className="flex items-start justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-900">{action.name || 'Unnamed Action'}</span>
                                    <span className={`ml-2 shrink-0 px-2 py-0.5 text-xs rounded font-medium ${
                                      action.status === 'Closed - Done' ? 'bg-green-100 text-green-700' :
                                      action.status === 'Closed - Not Applicable' ? 'bg-gray-200 text-gray-600' :
                                      action.status === 'Open' ? 'bg-orange-100 text-orange-700' :
                                      action.status === 'Initiation' ? 'bg-purple-100 text-purple-700' :
                                      action.status === 'Cancelled' ? 'bg-red-100 text-red-600' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {action.status || 'No Status'}
                                    </span>
                                  </div>
                                  {action.titleValue && (
                                    <div className="mb-2">
                                      <p className="text-sm text-gray-600" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {action.titleValue}
                                      </p>
                                    </div>
                                  )}
                                  {action.description && (
                                    <div className="mb-2">
                                      <p className="text-sm text-gray-400" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {action.description}
                                      </p>
                                    </div>
                                  )}
                                  {!action.titleValue && !action.description && (
                                    <div className="mb-2">
                                      <p className="text-sm text-gray-400 italic">No information available</p>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-4 text-xs text-gray-500">
                                    {action.initiationDate && (
                                      <span>Initiated: {formatDate(action.initiationDate)}</span>
                                    )}
                                    {action.dueDate && (
                                      <span>Due: {formatDate(action.dueDate)}</span>
                                    )}
                                    {action.completionDate && (
                                      <span>Completed: {formatDate(action.completionDate)}</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {effectiveCCs.length === 0 && (
              <div className="space-y-4">
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle size={24} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium">No change control records found</p>
                  <p className="text-xs mt-1">No change controls are affecting this product in the target market</p>
                </div>

                {/* Saved validation notes */}
                {changeControlNotesArray.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="p-1.5 bg-indigo-100 rounded">
                        <FileText size={14} className="text-indigo-600" />
                      </div>
                      <span className="text-xs font-bold text-indigo-900 uppercase tracking-wide">Validation Notes</span>
                      <span className="text-xs text-gray-500">({changeControlNotesArray.length})</span>
                    </div>
                    {changeControlNotesArray.map((note, index) => (
                      <div key={index} className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs text-indigo-600 font-medium">{formatDateTime(note.timestamp)}</span>
                            </div>
                            <div className="text-sm text-gray-900 break-words">
                              {note.text}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to delete this validation note?')) {
                                const updated = changeControlNotesArray.filter((_, i) => i !== index);
                                setChangeControlNotesArray(updated);
                                setFieldOverrides(prev => ({
                                  ...prev,
                                  changeControls: {
                                    ...prev.changeControls,
                                    value: prev.changeControls?.value || '',
                                    originalValue: prev.changeControls?.originalValue || '',
                                    validationNotesArray: updated
                                  }
                                }));
                              }
                            }}
                            className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600 hover:text-red-700"
                            title="Delete this note"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add validation notes button */}
                <button
                  onClick={() => setShowChangeControlNotes(!showChangeControlNotes)}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors flex items-center gap-1"
                >
                  <FileText size={14} />
                  Add Validation Notes
                </button>

                {/* New note form */}
                {showChangeControlNotes && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                    <label className="block text-sm font-medium text-gray-900">
                      New Validation Note
                    </label>
                    <textarea
                      value={changeControlNoteText}
                      onChange={(e) => setChangeControlNoteText(e.target.value)}
                      placeholder="Enter validation note about change controls..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      rows={3}
                    />
                    <button
                      onClick={() => {
                        const updated = [...changeControlNotesArray, { text: changeControlNoteText, timestamp: new Date().toISOString() }];
                        setChangeControlNotesArray(updated);
                        setChangeControlNoteText('');
                        setShowChangeControlNotes(false);
                        setFieldOverrides(prev => ({
                          ...prev,
                          changeControls: {
                            value: prev.changeControls?.value || '',
                            originalValue: prev.changeControls?.originalValue || '',
                            validationNotesArray: updated
                          }
                        }));
                      }}
                      disabled={!changeControlNoteText.trim()}
                      className={`w-full py-2 font-medium rounded transition-all flex items-center justify-center gap-2 text-sm ${
                        !changeControlNoteText.trim()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      <FileText size={14} />
                      Save Note
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Serialization & Aggregation Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('serializationAggregation')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded">
              <Database size={18} className="text-purple-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Serialization & Aggregation</h3>
              <p className="text-xs text-gray-500">TraceLink lot status and serial number hierarchy</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('serializationAggregation') ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {expandedSections.includes('serializationAggregation') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-6">

            {/* Serialization Requirement Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Serialization Requirement Status
              </label>
              <select
                value={serializationRequirement}
                onChange={(e) => {
                  setSerializationRequirement(e.target.value);
                  if (onDirtyChange) onDirtyChange(true);
                }}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select serialization requirement...</option>
                <option value="yes-required-serialised">Yes, serialisation requirement in the country, goods are serialised</option>
                <option value="no-requirement-not-serialised">No, no requirement in the destination country, goods are NOT serialised</option>
                <option value="no-requirement-serialised-nonetheless">No, no requirement in the destination country, goods are serialised nonetheless</option>
                <option value="na">N/A</option>
              </select>
            </div>

            {/* Aggregation Requirement Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Aggregation Requirement Status
              </label>
              <select
                value={aggregationRequirement}
                onChange={(e) => {
                  setAggregationRequirement(e.target.value);
                  if (onDirtyChange) onDirtyChange(true);
                }}
                disabled={isReadOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Select aggregation requirement...</option>
                <option value="yes-required-aggregated">Yes, aggregation requirement in the country, goods are aggregated</option>
                <option value="no-requirement-not-aggregated">No, no requirement in the destination country, goods are NOT aggregated</option>
                <option value="no-requirement-aggregated-nonetheless">No, no requirement in the destination country, goods are nevertheless aggregated</option>
                <option value="na">N/A</option>
              </select>
            </div>

            {/* TraceLink Lot Status Table (Read-only) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="p-3 bg-gray-100 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900">TraceLink Lot Status</h4>
              </div>
              <div className="p-4">
                {traceLinkLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Fetching TraceLink lot status...</span>
                  </div>
                )}
                {traceLinkError && (
                  <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 rounded-md p-3">
                    <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
                    <span>{traceLinkError}</span>
                  </div>
                )}
                {!traceLinkLoading && !traceLinkError && traceLinkFound === false && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-md p-3">
                    <AlertCircle size={16} className="text-gray-400 flex-shrink-0" />
                    <span>{traceLinkNotFoundMessage}</span>
                  </div>
                )}
                {!traceLinkLoading && !traceLinkError && traceLinkFound === true && traceLinkData && (() => {
                  const hiddenFields = new Set(['Success', 'Item Code', 'Lot Number', 'Internal Material Code']);
                  const dateFields = new Set(['Expiration Date', 'Manufacturing Date']);
                  const booleanValues: Record<string, boolean> = { 'true': true, 'false': false, 'yes': true, 'no': false };

                  const renderValue = (key: string, value: string) => {
                    if (dateFields.has(key)) {
                      return <span>{formatDate(value)}</span>;
                    }
                    const lower = value.toLowerCase();
                    if (lower in booleanValues) {
                      const isTrue = booleanValues[lower];
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isTrue ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                          {isTrue ? 'Yes' : 'No'}
                        </span>
                      );
                    }
                    return <span>{value || '—'}</span>;
                  };

                  const visibleEntries = Object.entries(traceLinkData).filter(([key]) => !hiddenFields.has(key));
                  const materialNumber = autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value || autoPopulatedBRR?.productMarketData?.materialSKU?.value;
                  const vendorBatchId = autoPopulatedBRR?.sapIdentityBatch?.cmoFpBatchId?.value;

                  return (
                    <div>
                      <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-md p-3 mb-3">
                        <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                        <span>
                          TraceLink serialisation information found for Material Number: <strong>{materialNumber}</strong> and Vendor Batch ID: <strong>{vendorBatchId}</strong>
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm table-fixed">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Field</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleEntries.map(([key, value]) => (
                              <tr key={key} className="border-b border-gray-100">
                                <td className="py-2 px-3 text-gray-600">{key}</td>
                                <td className="py-2 px-3 text-gray-900 break-words">{renderValue(key, value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}
                {!traceLinkLoading && !traceLinkError && !traceLinkData && (
                  <p className="text-sm text-gray-400">No TraceLink data available. Material number or vendor batch ID may be missing.</p>
                )}
              </div>
            </div>

            {/* Serial Number Hierarchy Levels Table (Read-only) */}
            {!traceLinkLoading && !traceLinkError && traceLinkFound === true && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-3 bg-gray-100 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900">Serial Number Hierarchy</h4>
                </div>
                <div className="p-4">
                  {hierarchyLoading && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Fetching serial number hierarchy...</span>
                    </div>
                  )}
                  {hierarchyError && (
                    <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-md p-3">
                      <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
                      <span>{hierarchyError}</span>
                    </div>
                  )}
                  {!hierarchyLoading && !hierarchyError && hierarchyLevels && hierarchyLevels.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm table-fixed">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Level</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700 w-1/2">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hierarchyLevels.map(({ level, quantity }) => (
                            <tr key={level} className="border-b border-gray-100">
                              <td className="py-2 px-3 text-gray-600">{level}</td>
                              <td className="py-2 px-3 text-gray-900">{quantity.toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="border-t border-gray-300 font-semibold">
                            <td className="py-2 px-3 text-gray-900">Total</td>
                            <td className="py-2 px-3 text-gray-900">
                              {hierarchyLevels.reduce((sum, l) => sum + l.quantity, 0).toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                  {!hierarchyLoading && !hierarchyError && (!hierarchyLevels || hierarchyLevels.length === 0) && (
                    <p className="text-sm text-gray-400">No hierarchy level data available.</p>
                  )}
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Sample Documents Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-green-50 border-2 border-indigo-200 rounded-lg p-4 mt-6">
        <h2 className="text-xl font-bold text-indigo-900 flex items-center gap-2">
          <FileText size={24} className="text-indigo-600" />
          Sample Documents
        </h2>
      </div>

      {/* Packaging Materials (KAU Data) */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('sampleDoc_packagingMaterials')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded">
              <Package size={18} className="text-indigo-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Packaging Materials (KAU Data)</h3>
              <span className="text-xs text-gray-500">{packagingMaterials.length} {packagingMaterials.length === 1 ? 'material' : 'materials'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {packagingMaterials.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                {packagingMaterials.length}
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('sampleDoc_packagingMaterials') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('sampleDoc_packagingMaterials') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3 text-left">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Packaging Materials Used ({packagingMaterials.length})</span>
              {!isReadOnly && (
                <button
                  onClick={() => setShowAddPackagingForm(!showAddPackagingForm)}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 rounded border border-green-200 hover:bg-green-100 transition-colors"
                >
                  <Plus size={12} />
                  {showAddPackagingForm ? 'Cancel' : 'Add'}
                </button>
              )}
            </div>

            {/* Manual Entry Form */}
            {showAddPackagingForm && !isReadOnly && (
              <div className="p-3 bg-green-50 rounded border border-green-200 space-y-3 mb-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Component Type</label>
                    <select
                      value={newPackagingEntry.componentType}
                      onChange={(e) => { const val = e.target.value; setNewPackagingEntry(prev => ({ ...prev, componentType: val })); }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="">Select type...</option>
                      <option value="Folding Box">Folding Box</option>
                      <option value="Leaflet">Leaflet</option>
                      <option value="Foil">Foil</option>
                      <option value="Label">Label</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Artworks Version Number</label>
                    <input
                      type="text"
                      value={newPackagingEntry.versionNumber}
                      onChange={(e) => { const val = e.target.value; setNewPackagingEntry(prev => ({ ...prev, versionNumber: val })); }}
                      placeholder="e.g., 1.0, 2.1"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Artworks Material Number</label>
                    <input
                      type="text"
                      value={newPackagingEntry.cpMaterialNumber}
                      onChange={(e) => { const val = e.target.value; setNewPackagingEntry(prev => ({ ...prev, cpMaterialNumber: val })); }}
                      placeholder="Artworks material #"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">CMO Material Number</label>
                    <input
                      type="text"
                      value={newPackagingEntry.cmoMaterialNumber}
                      onChange={(e) => { const val = e.target.value; setNewPackagingEntry(prev => ({ ...prev, cmoMaterialNumber: val })); }}
                      placeholder="CMO material #"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setNewPackagingEntry({ componentType: '', cpMaterialNumber: '', cmoMaterialNumber: '', versionNumber: '' });
                      setShowAddPackagingForm(false);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (!newPackagingEntry.componentType) return;
                      const newEntry: PackagingMaterialEntry = {
                        id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
                        componentType: newPackagingEntry.componentType,
                        cpMaterialNumber: newPackagingEntry.cpMaterialNumber,
                        cmoMaterialNumber: newPackagingEntry.cmoMaterialNumber,
                        versionNumber: newPackagingEntry.versionNumber,
                        source: 'manual',
                      };
                      setPackagingMaterials(prev => [...prev, newEntry]);
                      setNewPackagingEntry({ componentType: '', cpMaterialNumber: '', cmoMaterialNumber: '', versionNumber: '' });
                      setShowAddPackagingForm(false);
                    }}
                    disabled={!newPackagingEntry.componentType}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Add Entry
                  </button>
                </div>
              </div>
            )}

            {/* Packaging Materials List */}
            {packagingMaterials.length === 0 ? (
              <div className="p-3 bg-gray-50 rounded border border-gray-200 text-center text-sm text-gray-500">
                No packaging materials data. Add entries manually or upload packaging documents in Document Validation.
              </div>
            ) : (
              <div className="space-y-2">
                {packagingMaterials.map((material) => {
                  const updateMaterial = (field: keyof PackagingMaterialEntry, value: string) => {
                    setPackagingMaterials(prev =>
                      prev.map(m => m.id === material.id ? { ...m, [field]: value } : m)
                    );
                  };

                  return (
                    <div
                      key={material.id}
                      className={`p-2 rounded border ${material.source === 'manual' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {isReadOnly ? (
                              <span className="font-medium text-gray-900 text-sm">{material.componentType || 'Unknown Type'}</span>
                            ) : (
                              <input
                                type="text"
                                value={material.componentType || ''}
                                onChange={(e) => updateMaterial('componentType', e.target.value)}
                                placeholder="Component Type"
                                className="font-medium text-gray-900 text-sm px-2 py-1 border border-gray-300 rounded w-40"
                              />
                            )}
                            <span className={`px-1.5 py-0.5 text-xs rounded ${material.source === 'manual' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {material.source === 'manual' ? 'Manual' : 'Extracted'}
                            </span>
                            {material.filename && (
                              <span className="text-gray-400 text-xs">({material.filename})</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500">Artworks Mat #:</span>
                              {isReadOnly ? (
                                <span className="text-gray-600">{material.cpMaterialNumber || '-'}</span>
                              ) : (
                                <input
                                  type="text"
                                  value={material.cpMaterialNumber || ''}
                                  onChange={(e) => updateMaterial('cpMaterialNumber', e.target.value)}
                                  placeholder="Material #"
                                  className="text-gray-900 text-xs px-2 py-1 border border-gray-300 rounded w-28"
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500">Artworks Ver #:</span>
                              {isReadOnly ? (
                                <span className="text-gray-600">{material.versionNumber || '-'}</span>
                              ) : (
                                <input
                                  type="text"
                                  value={material.versionNumber || ''}
                                  onChange={(e) => updateMaterial('versionNumber', e.target.value)}
                                  placeholder="Version #"
                                  className="text-gray-900 text-xs px-2 py-1 border border-gray-300 rounded w-20"
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-500">CMO:</span>
                              {isReadOnly ? (
                                <span className="text-gray-600">{material.cmoMaterialNumber || '-'}</span>
                              ) : (
                                <input
                                  type="text"
                                  value={material.cmoMaterialNumber || ''}
                                  onChange={(e) => updateMaterial('cmoMaterialNumber', e.target.value)}
                                  placeholder="CMO Material #"
                                  className="text-gray-900 text-xs px-2 py-1 border border-gray-300 rounded w-28"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                        {!isReadOnly && (
                          <button
                            onClick={() => setPackagingMaterials(prev => prev.filter(p => p.id !== material.id))}
                            className="text-red-500 hover:text-red-700 text-xs ml-2"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sample Documents — multiple Box folder links per subsection */}
      {STANDARD_DOC_SECTIONS.map(({ key, title }) => {
        const sectionId = `sampleDoc_${key}`;
        const st = boxStates[key];
        const updateState = (patch: Partial<BoxSectionState>) =>
          setBoxStates(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

        const startElapsedTimer = () => {
          updateState({ scanElapsed: 0 });
          const id = setInterval(() => {
            setBoxStates(prev => ({ ...prev, [key]: { ...prev[key], scanElapsed: prev[key].scanElapsed + 1 } }));
          }, 1000);
          scanTimerRef.current[key] = id;
        };
        const stopElapsedTimer = () => {
          if (scanTimerRef.current[key]) { clearInterval(scanTimerRef.current[key]); delete scanTimerRef.current[key]; }
        };

        const handleAddNewLink = async () => {
          const folderMatch = st.pendingUrl.match(/box\.com\/folder\/(\d+)/);
          const sharedMatch = st.pendingUrl.match(/box\.com\/s\/[\w]+/);

          if (!folderMatch && !sharedMatch) {
            updateState({ pendingUrlError: 'URL must be a Box folder link (box.com/folder/...) or shared link (box.com/s/...)' });
            return;
          }

          const materialSku = autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value;
          if (!materialSku) { updateState({ pendingUrlError: 'No material SKU available' }); return; }

          let folderId: string;
          if (folderMatch) {
            folderId = folderMatch[1];
          } else {
            try {
              updateState({ loading: true, pendingUrlError: '' });
              const resolved = await apiClient.resolveBoxUrl(st.pendingUrl);
              folderId = resolved.folder_id;
            } catch {
              updateState({ loading: false, pendingUrlError: 'Failed to resolve shared link. Ensure it points to a folder and is accessible.' });
              return;
            }
          }

          updateState({ resolvedFolderId: folderId });

          // Quick size check first
          try {
            const info = await apiClient.getBoxFolderInfo(folderId);
            if (info.total_count > 20) {
              updateState({ folderInfo: info, showLargeWarning: true, loading: false });
              return;
            }
          } catch { /* proceed anyway */ }

          await runScanAndCertifyNewEntry(key, folderId, st.pendingUrl, Number(materialSku), st.pendingVersion, st.pendingFreeText);
        };

        const runScanAndCertifyNewEntry = async (
          subKey: BoxSubsectionKey,
          folderId: string,
          link: string,
          _materialId: number,
          version: string,
          freeText: string
        ) => {
          updateState({ loading: true, pendingUrlError: '', showLargeWarning: false });
          startElapsedTimer();
          try {
            const result = await apiClient.scanBoxFolder(folderId, undefined, 120);
            const newEntryId = crypto.randomUUID();
            const newEntry: BoxLinkEntry = {
              id: newEntryId,
              link,
              folder_id: folderId,
              certified_at: new Date().toISOString(),
              snapshot: result.current_snapshot as Record<string, BoxFileEntry>,
              version: version,
              freeText: freeText,
            };
            // Update local state only - persisted to BRRReview via handleSaveProgress
            updateState({
              entries: [...st.entries, newEntry],
              pendingUrl: '',
              pendingVersion: '',
              pendingFreeText: '',
              showAddForm: false,
              scanResult: result.timed_out ? result : null,
            });
            if (result.timed_out) {
              updateState({ pendingUrlError: `Scan timed out after 2 minutes (${result.items_scanned} items scanned). Folder was saved with partial snapshot.` });
            }
            handleSampleDocsComplianceChange(key, true);
          } catch (err) {
            console.error('Certification failed:', err);
            updateState({ pendingUrlError: 'Failed to certify Box folder. Please try again.' });
          } finally {
            stopElapsedTimer();
            updateState({ loading: false });
          }
        };

        const handleDeleteEntry = (entryId: string) => {
          if (!window.confirm('Are you sure you want to delete this Box folder link?')) return;
          // Update local state only - persisted to BRRReview via handleSaveProgress
          updateState({ entries: st.entries.filter(e => e.id !== entryId) });
        };

        const handleUpdateEntry = (entry: BoxLinkEntry) => {
          // Update local state only - persisted to BRRReview via handleSaveProgress
          updateState({
            entries: st.entries.map(e => e.id === entry.id ? entry : e),
            editingEntryId: null,
            editingUrl: '',
          });
        };

        const handleCheckForChanges = async (entry: BoxLinkEntry) => {
          updateState({ loading: true, scanResult: null, scanningEntryId: entry.id });
          startElapsedTimer();
          try {
            const result = await apiClient.scanBoxFolder(entry.folder_id, entry.snapshot, 120);
            updateState({ scanResult: result, scanningEntryId: entry.id });
          } catch (err) {
            console.error('Box folder scan failed:', err);
          } finally {
            stopElapsedTimer();
            updateState({ loading: false });
          }
        };

        const handleRecertifyEntry = (entry: BoxLinkEntry) => {
          if (!st.scanResult) return;
          const updatedEntry: BoxLinkEntry = {
            ...entry,
            certified_at: new Date().toISOString(),
            snapshot: st.scanResult.current_snapshot as Record<string, BoxFileEntry>,
          };
          // Update local state only - persisted to BRRReview via handleSaveProgress
          updateState({
            entries: st.entries.map(e => e.id === entry.id ? updatedEntry : e),
            scanResult: null,
            scanningEntryId: null,
          });
          handleSampleDocsComplianceChange(key, true);
        };

        return (
          <div key={key} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection(sectionId)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded">
                  <FileText size={18} className="text-indigo-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {st.entries.length > 0 ? (
                  <span className="px-2 py-1 text-xs rounded font-medium bg-green-100 text-green-700">
                    {st.entries.length} Linked
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs rounded font-medium bg-amber-100 text-amber-700">Not linked</span>
                )}
                <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes(sectionId) ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {expandedSections.includes(sectionId) && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4 text-left">
                {/* Existing entries list */}
                {st.entries.map((entry, idx) => (
                  <div key={entry.id} className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                    {st.editingEntryId === entry.id ? (
                      /* Edit mode for this entry */
                      <div className="space-y-3">
                        <p className="text-sm text-amber-600">
                          Changing the URL will require you to re-certify the documents.
                        </p>
                        <div className="w-full">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Box Folder URL</label>
                          <input
                            type="text"
                            value={st.editingUrl}
                            onChange={(e) => updateState({ editingUrl: e.target.value })}
                            placeholder="https://cheplapharm.app.box.com/folder/... or .../s/..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              // Reset entry and mark for re-certification
                              updateState({
                                editingEntryId: null,
                                editingUrl: '',
                                entries: st.entries.filter(e => e.id !== entry.id),
                                pendingUrl: st.editingUrl,
                                showAddForm: true,
                              });
                            }}
                            disabled={!st.editingUrl}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            <CheckCircle size={14} />
                            Update URL
                          </button>
                          <button
                            onClick={() => updateState({ editingEntryId: null, editingUrl: '' })}
                            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display mode for this entry */
                      <>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-gray-700">Box Folder:</span>{' '}
                            <a href={entry.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1 break-all">
                              {entry.link} <ExternalLink size={12} />
                            </a>
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            Last certified: {formatDate(entry.certified_at)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleCheckForChanges(entry)}
                            disabled={st.loading}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {st.loading && st.scanningEntryId === entry.id ? <RefreshCw size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                            Check for Changes
                          </button>
                          {st.scanResult && st.scanningEntryId === entry.id && (
                            <button
                              onClick={() => handleRecertifyEntry(entry)}
                              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
                            >
                              <CheckCircle size={14} />
                              Recertify
                            </button>
                          )}
                          <button
                            onClick={() => {
                              updateState({ editingEntryId: entry.id, editingUrl: entry.link });
                            }}
                            className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded hover:bg-amber-200 flex items-center gap-1"
                          >
                            <FileText size={14} />
                            Edit URL
                          </button>
                          <button
                            onClick={() => handleDeleteEntry(entry.id)}
                            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"
                          >
                            <Trash2 size={14} />
                            Clear
                          </button>
                        </div>
                        {/* Version and Free Text for this entry */}
                        <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Version</label>
                            <input
                              type="text"
                              value={entry.version}
                              onChange={(e) => {
                                const updatedEntry = { ...entry, version: e.target.value };
                                updateState({ entries: st.entries.map(en => en.id === entry.id ? updatedEntry : en) });
                              }}
                              onBlur={() => handleUpdateEntry(st.entries.find(e => e.id === entry.id)!)}
                              placeholder="e.g. V3.1"
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Free Text</label>
                            <input
                              type="text"
                              value={entry.freeText}
                              onChange={(e) => {
                                const updatedEntry = { ...entry, freeText: e.target.value };
                                updateState({ entries: st.entries.map(en => en.id === entry.id ? updatedEntry : en) });
                              }}
                              onBlur={() => handleUpdateEntry(st.entries.find(e => e.id === entry.id)!)}
                              placeholder="Additional notes..."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    {/* Loading progress for this entry's scan */}
                    {st.loading && st.scanningEntryId === entry.id && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700 flex items-center gap-2">
                        <RefreshCw size={14} className="animate-spin" />
                        Scanning folder... {st.scanElapsed}s elapsed (timeout: 2 min)
                      </div>
                    )}
                    {/* Scan Results for this entry */}
                    {st.scanResult && st.scanningEntryId === entry.id && (
                      <div className="mt-2 space-y-2">
                        {st.scanResult.timed_out && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                            Scan timed out — {st.scanResult.items_scanned} items found (partial results shown).
                          </div>
                        )}
                        {st.scanResult.changes.new_files.length === 0 &&
                         st.scanResult.changes.modified_files.length === 0 &&
                         st.scanResult.changes.deleted_files.length === 0 ? (
                          <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                            No changes detected since last certification.
                          </div>
                        ) : (
                          <>
                            {st.scanResult.changes.new_files.length > 0 && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                <h4 className="text-sm font-medium text-blue-800 mb-1">New Files ({st.scanResult.changes.new_files.length})</h4>
                                <ul className="space-y-1">
                                  {st.scanResult.changes.new_files.map((f) => (
                                    <li key={f.id} className="text-sm flex items-center gap-2">
                                      {f.type === 'folder' ? <Package size={12} /> : <File size={12} />}
                                      <a href={f.box_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{f.path}</a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {st.scanResult.changes.modified_files.length > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                                <h4 className="text-sm font-medium text-amber-800 mb-1">Modified Files ({st.scanResult.changes.modified_files.length})</h4>
                                <ul className="space-y-1">
                                  {st.scanResult.changes.modified_files.map((f) => (
                                    <li key={f.id} className="text-sm flex items-center gap-2">
                                      {f.type === 'folder' ? <Package size={12} /> : <File size={12} />}
                                      <a href={f.box_link} target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">{f.path}</a>
                                      <span className="text-xs text-gray-400">{formatDateTime(f.modified_at)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {st.scanResult.changes.deleted_files.length > 0 && (
                              <div className="p-3 bg-red-50 border border-red-200 rounded">
                                <h4 className="text-sm font-medium text-red-800 mb-1">Deleted Files ({st.scanResult.changes.deleted_files.length})</h4>
                                <ul className="space-y-1">
                                  {st.scanResult.changes.deleted_files.map((f) => (
                                    <li key={f.id} className="text-sm text-red-600">{f.path}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Add new link form */}
                {st.showAddForm ? (
                  <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4 space-y-3">
                    <p className="text-sm text-gray-500">
                      Add a new Box folder link for this section.
                    </p>
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Box Folder URL</label>
                      <input
                        type="text"
                        value={st.pendingUrl}
                        onChange={(e) => updateState({ pendingUrl: e.target.value, pendingUrlError: '', showLargeWarning: false, folderInfo: null })}
                        placeholder="https://cheplapharm.app.box.com/folder/... or .../s/..."
                        className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${st.pendingUrlError ? 'border-red-300' : 'border-gray-300'}`}
                      />
                      {st.pendingUrlError && <p className="mt-1 text-xs text-red-600">{st.pendingUrlError}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Version</label>
                        <input
                          type="text"
                          value={st.pendingVersion}
                          onChange={(e) => updateState({ pendingVersion: e.target.value })}
                          placeholder="e.g. V3.1"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Free Text</label>
                        <input
                          type="text"
                          value={st.pendingFreeText}
                          onChange={(e) => updateState({ pendingFreeText: e.target.value })}
                          placeholder="Additional notes..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Large folder warning */}
                    {st.showLargeWarning && st.folderInfo && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                        <p className="font-medium">Large folder detected</p>
                        <p>"{st.folderInfo.name}" contains {st.folderInfo.total_count} items. Scanning may take up to 2 minutes.</p>
                        <button
                          onClick={async () => {
                            const folderId = st.resolvedFolderId;
                            const materialSku = autoPopulatedBRR?.productMarketData?.materialSKUSAP?.value;
                            if (folderId && materialSku) {
                              await runScanAndCertifyNewEntry(key, folderId, st.pendingUrl, Number(materialSku), st.pendingVersion, st.pendingFreeText);
                            }
                          }}
                          className="mt-2 px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                        >
                          Continue anyway
                        </button>
                      </div>
                    )}

                    {/* Loading progress */}
                    {st.loading && !st.scanningEntryId && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700 flex items-center gap-2">
                        <RefreshCw size={14} className="animate-spin" />
                        Scanning folder... {st.scanElapsed}s elapsed (timeout: 2 min)
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={handleAddNewLink}
                        disabled={st.loading || !st.pendingUrl}
                        className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {st.loading && !st.scanningEntryId ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                        Certify Documents Reviewed
                      </button>
                      <button
                        onClick={() => updateState({ showAddForm: false, pendingUrl: '', pendingVersion: '', pendingFreeText: '', pendingUrlError: '' })}
                        className="px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Add link button */
                  <button
                    onClick={() => updateState({ showAddForm: true })}
                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus size={16} />
                    {st.entries.length > 0 ? 'Add Another Link' : 'Add Box Folder Link'}
                  </button>
                )}

                {/* Validation Status (per section, not per entry) */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Validation Status</label>
                  <select
                    value={sampleDocsCompliance[key] === true ? 'compliant' : 'non-compliant'}
                    onChange={(e) => handleSampleDocsComplianceChange(key, e.target.value === 'compliant')}
                    className={`w-full px-3 py-2 border rounded text-sm font-medium focus:outline-none focus:ring-2 bg-white ${
                      sampleDocsCompliance[key] === true
                        ? 'border-green-300 text-green-700 focus:ring-green-200'
                        : 'border-red-300 text-red-700 focus:ring-red-200'
                    }`}
                  >
                    <option value="compliant" className="bg-white text-gray-900">Complies</option>
                    <option value="non-compliant" className="bg-white text-gray-900">Does not correspond</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Custom Document Sections */}
      {customDocSections.map((section, idx) => {
        const sectionId = `customDoc_${section.id}`;
        return (
          <div key={section.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection(sectionId)}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded">
                  <FileText size={18} className="text-purple-600" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900">{section.name}</h3>
                  <span className="text-xs text-gray-500">Custom Section</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes(sectionId) ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {expandedSections.includes(sectionId) && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3 text-left">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Box Folder URL</label>
                  <input
                    type="text"
                    value={section.boxFolderUrl}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomDocSections(prev => prev.map((s, i) => i === idx ? { ...s, boxFolderUrl: val } : s));
                    }}
                    placeholder="https://cheplapharm.app.box.com/folder/..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Version</label>
                  <input
                    type="text"
                    value={section.version}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomDocSections(prev => prev.map((s, i) => i === idx ? { ...s, version: val } : s));
                    }}
                    placeholder="e.g. V1.0"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Free Text</label>
                  <textarea
                    value={section.freeText}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomDocSections(prev => prev.map((s, i) => i === idx ? { ...s, freeText: val } : s));
                    }}
                    rows={2}
                    placeholder="Additional notes..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Validation Status</label>
                  <select
                    value={isCompliantValue(complianceOverrides[section.id]) ? 'compliant' : 'non-compliant'}
                    onChange={(e) => handleComplianceChange(section.id, e.target.value === 'compliant')}
                    className={`w-full px-3 py-2 border rounded text-sm font-medium focus:outline-none focus:ring-2 bg-white ${
                      isCompliantValue(complianceOverrides[section.id])
                        ? 'border-green-300 text-green-700 focus:ring-green-200'
                        : 'border-red-300 text-red-700 focus:ring-red-200'
                    }`}
                  >
                    <option value="compliant" className="bg-white text-gray-900">Complies</option>
                    <option value="non-compliant" className="bg-white text-gray-900">Does not correspond</option>
                  </select>
                </div>
                <button
                  onClick={() => setCustomDocSections(prev => prev.filter((_, i) => i !== idx))}
                  className="mt-2 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded hover:bg-red-50 flex items-center gap-1"
                >
                  <Trash2 size={14} />
                  Remove Section
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Add Custom Document Section button */}
      <button
        onClick={() => setShowAddDocSectionModal(true)}
        className="w-full py-3 border-2 border-dashed border-indigo-300 rounded-lg text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
      >
        <span className="text-lg leading-none">+</span>
        Add Document Section
      </button>

      {showAddDocSectionModal && (
        <AddDocSectionModal
          onAdd={(section) => setCustomDocSections(prev => [...prev, section])}
          onClose={() => setShowAddDocSectionModal(false)}
        />
      )}

      {/* Batch Documents Header */}
      <div className="bg-gradient-to-r from-indigo-50 to-green-50 border-2 border-indigo-200 rounded-lg p-4 mt-6">
        <h2 className="text-xl font-bold text-indigo-900 flex items-center gap-2">
          <FileText size={24} className="text-indigo-600" />
          Batch documents
        </h2>
      </div>

      {/* Batch Date Information Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mt-4">
        <button
          onClick={() => toggleSection('batchDates')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded">
              <Calendar size={18} className="text-indigo-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Batch Date Information</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {batchDatesCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                batchDatesCount.validated === batchDatesCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {batchDatesCount.validated}/{batchDatesCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('batchDates') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('batchDates') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            <ValidatedFieldWithCompliance
              fieldId="batchManufactureDate"
              label="Batch Manufacture Date"
              brrValue={String(fieldOverrides.batchManufactureDate?.value ?? autoPopulatedBRR.sapIdentityBatch?.batchManufactureDate?.value ?? '')}
              sourceValue={autoPopulatedBRR.sapIdentityBatch?.batchManufactureDateSAP?.value || 'No Batch Manufacture Date found'}
              source="SAP"
              brrSourceDoc={fieldOverrides.batchManufactureDate?.source || autoPopulatedBRR.sapIdentityBatch?.batchManufactureDate?.source || 'Extracted from Documents'}
              onComplianceChange={handleBatchDatesComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />

            <ValidatedFieldWithCompliance
              fieldId="batchExpiryDate"
              label="Batch Expiry Date"
              brrValue={String(fieldOverrides.batchExpiryDate?.value ?? autoPopulatedBRR.sapIdentityBatch?.batchExpiryDate?.value ?? '')}
              sourceValue={autoPopulatedBRR.sapIdentityBatch?.batchExpiryDateSAP?.value || 'No Batch Expiry Date found'}
              source="SAP"
              brrSourceDoc={fieldOverrides.batchExpiryDate?.source || autoPopulatedBRR.sapIdentityBatch?.batchExpiryDate?.source || 'Extracted from Documents'}
              onComplianceChange={handleBatchDatesComplianceChange}
              complianceOverrides={complianceOverrides}
              fieldOverrides={fieldOverrides}
              setFieldOverrides={setFieldOverrides}
            />

            {autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryDate?.value && (
              <ValidatedFieldWithCompliance
                fieldId="calculatedExpiryDate"
                label="Calculated Expiry Date (Shelf Life - 1 Month)"
                description={autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryShelfLifeSource?.value
                  ? `Shelf life source: ${autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryShelfLifeSource?.value}`
                  : undefined}
                brrValue={String(fieldOverrides.calculatedExpiryDate?.value ?? autoPopulatedBRR?.sapIdentityBatch?.batchExpiryDate?.value ?? '')}
                sourceValue={autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryDate?.value || ''}
                source="Calculated"
                brrSourceDoc={fieldOverrides.batchExpiryDate?.source || autoPopulatedBRR?.sapIdentityBatch?.batchExpiryDate?.source || 'Extracted from Documents'}
                onComplianceChange={handleBatchDatesComplianceChange}
                complianceOverrides={complianceOverrides}
                fieldOverrides={fieldOverrides}
                setFieldOverrides={setFieldOverrides}
                subtitle={String(autoPopulatedBRR?.sapIdentityBatch?.calculatedExpiryFormula?.value || '') || undefined}
              />
            )}
          </div>
        )}
      </div>

      {/* Quality Events Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('qualityEvents')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded">
              <AlertCircle size={18} className="text-red-600" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Quality Events ({effectiveQEs.length})</h3>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {deviationsOosCount.total > 0 && (
              <span className={`px-2 py-1 text-xs rounded font-medium ${
                deviationsOosCount.validated === deviationsOosCount.total
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                {deviationsOosCount.validated}/{deviationsOosCount.total} validated
              </span>
            )}
            <ChevronDown size={18} className={`text-gray-500 transition-transform ${expandedSections.includes('qualityEvents') ? 'rotate-180' : ''}`} />
          </div>
        </button>
        {expandedSections.includes('qualityEvents') && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
            <div className="flex justify-end">
              <button
                onClick={(e) => { e.stopPropagation(); setShowAddQEModal(true); }}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
              >
                + Add Quality Event
              </button>
            </div>
            {effectiveQEs.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <AlertCircle size={24} className="mx-auto mb-2 text-gray-400" />
                <p>No quality events found for this batch</p>
              </div>
            ) : (
              effectiveQEs.map((qe: any) => (
                <div key={qe.stableId} className="p-4 bg-white rounded-lg border border-gray-200 hover:border-red-300 transition-all">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 overflow-hidden" style={{ minWidth: 0, overflow: 'hidden' }}>
                      <div className="flex items-center gap-2 mb-2 flex-wrap" style={{ flexWrap: 'wrap' }}>
                        <span className="font-semibold text-gray-900">{qe.id}</span>
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                          qe.eventType === 'Deviation' ? 'bg-orange-100 text-orange-700' :
                          qe.eventType === 'OutOfSpecification' ? 'bg-red-100 text-red-700' :
                          qe.eventType === 'Complaint' ? 'bg-yellow-100 text-yellow-700' :
                          qe.eventType === 'MarketComplaint' ? 'bg-pink-100 text-pink-700' :
                          qe.eventType === 'OutOfTrend' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {qe.eventType}
                        </span>
                        {qe.finalApprovalCompleted === false && (
                          <span className="px-2 py-0.5 text-xs rounded font-medium bg-amber-100 text-amber-700">
                            Open
                          </span>
                        )}
                        {qe.isManual && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded-full font-medium bg-blue-100 text-blue-700">
                            Manually Added
                          </span>
                        )}
                      </div>
                      {qe.titleValue && (
                        <p className="text-sm text-gray-500 mb-1">{qe.titleValue}</p>
                      )}
                      <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap break-words" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>{qe.description}</p>
                      <div className="text-xs text-gray-400 space-y-1">
                        {qe.affectedBatchNumber && <p>Affected Batch: {qe.affectedBatchNumber}</p>}
                        {qe.affectedMarket && <p>Affected Market: {qe.affectedMarket}</p>}
                        <p>Final Approval: {qe.finalApprovalDate || '-'}</p>
                        <p>Target Completion: {qe.targetCompletionDate || '-'}</p>
                        <p>Matched via: {qe.matchedVia}</p>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <select
                        className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                        value={deviationsOosCompliance[`qe_${qe.stableId}`] ? 'Complies' : 'Does not correspond'}
                        onChange={(e) => {
                          handleDeviationsOosComplianceChange(`qe_${qe.stableId}`, e.target.value === 'Complies');
                        }}
                      >
                        <option value="Complies">Complies</option>
                        <option value="Does not correspond">Does not correspond</option>
                      </select>
                      <button
                        onClick={() => {
                          if (qe.isManual) {
                            setManuallyAddedQEs(prev => prev.filter(q => q.stableId !== qe.stableId));
                          } else {
                            setRemovedQEIds(prev => new Set([...prev, qe.stableId]));
                          }
                        }}
                        className="p-1.5 hover:bg-red-100 rounded text-gray-400 hover:text-red-600 transition-colors"
                        title="Remove this quality event"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-16 z-50 flex flex-col gap-2">
        {nonValidatedFields.length > 0 && (
          <>
            <button
              onClick={goToPreviousField}
              className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-gray-50 transition-all hover:scale-105"
            >
              <ChevronDown size={18} className="rotate-90 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">Previous</span>
            </button>
            <button
              onClick={goToNextField}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 rounded-full shadow-lg hover:bg-green-700 transition-all hover:scale-105"
            >
              <span className="text-sm font-medium text-white">Next</span>
              <ChevronDown size={18} className="-rotate-90 text-white" />
            </button>
          </>
        )}
        <button
          onClick={scrollToAdditionalEvidence}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 border-2 border-blue-700 rounded-full shadow-lg hover:bg-blue-700 transition-all hover:scale-105"
        >
          <Upload size={18} className="text-white" />
          <span className="text-sm font-medium text-white">Additional Evidence</span>
        </button>
        <button
          onClick={scrollToActions}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 border-2 border-green-700 rounded-full shadow-lg hover:bg-green-700 transition-all hover:scale-105"
        >
          <CheckSquare size={18} className="text-white" />
          <span className="text-sm font-medium text-white">Go to Actions</span>
        </button>
        <button
          onClick={scrollToTop}
          className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 rounded-full shadow-lg hover:bg-gray-50 transition-all hover:scale-105"
        >
          <ChevronDown size={18} className="rotate-180 text-gray-700" />
          <span className="text-sm font-medium text-gray-700">Back to Top</span>
        </button>
        {nonValidatedFields.length > 0 && (
          <div className="text-center mt-1 px-3 py-1.5 bg-white rounded-lg shadow text-xs font-medium text-gray-700">
            {nonValidatedFields.length} Non-compliant Field{nonValidatedFields.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Linked Documents Section */}
      <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <FileText size={18} className="text-blue-600" />
          Linked Documents
        </h3>
        <div className="space-y-2">
          {startBRRDocuments && startBRRDocuments.length > 0 ? (
            startBRRDocuments.map((doc) => (
              <a key={doc.id} href="#" className="flex items-center justify-between p-3 hover:bg-green-50 rounded transition-colors group border border-transparent hover:border-green-200">
                <span className="text-sm text-gray-700 group-hover:text-green-700">{doc.name}</span>
                <ExternalLink size={14} className="text-gray-400 group-hover:text-green-600" />
              </a>
            ))
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No documents linked from Start Batch Record Review
            </div>
          )}
        </div>
      </div>

      {/* Additional Evidence Section */}
      <div id="additional-evidence-section" className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload size={20} className="text-blue-600" />
          Additional Evidence
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Add supporting documents or Box folder references for this Batch Record Review
        </p>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            type="button"
            onClick={() => { setEvidenceTab('pdf'); setItemError(''); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              evidenceTab === 'pdf'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Upload size={16} className="inline mr-2" />
            Upload PDF
          </button>
          <button
            type="button"
            onClick={() => { setEvidenceTab('boxLink'); setItemError(''); setPendingPdfUpload(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              evidenceTab === 'boxLink'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ExternalLink size={16} className="inline mr-2" />
            Box Link
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleEvidenceFileInputChange}
          className="hidden"
        />

        {/* PDF Upload Tab */}
        {evidenceTab === 'pdf' && (
          <div className="space-y-4">
            {!pendingPdfUpload ? (
              <>
                {/* Drag-drop upload area */}
                <div
                  className={`border-2 border-dashed rounded-lg transition-all cursor-pointer ${
                    isDraggingEvidence
                      ? 'border-green-400 bg-green-50'
                      : 'border-gray-300 bg-gray-50 hover:border-green-300 hover:bg-green-50'
                  }`}
                  onDragOver={handleEvidenceDragOver}
                  onDragLeave={handleEvidenceDragLeave}
                  onDrop={handleEvidenceDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="p-6 text-center">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                      isDraggingEvidence ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Upload size={24} className={isDraggingEvidence ? 'text-green-600' : 'text-gray-400'} />
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {isDraggingEvidence ? 'Drop file here' : 'Drag and drop a PDF file'}
                    </p>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition-colors"
                    >
                      Browse Files
                    </button>
                    <p className="text-xs text-gray-400 mt-2">PDF only, max 50MB</p>
                  </div>
                </div>

                {/* Upload Progress */}
                {isUploadingEvidence && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-600">Uploading...</span>
                      <span className="text-sm font-medium text-gray-900">{Math.round(uploadEvidenceProgress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full transition-all" style={{ width: `${uploadEvidenceProgress}%` }} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Pending upload - show file info and title/description form */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                  <FileText size={20} className="text-green-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{pendingPdfUpload.fileName}</div>
                    <div className="text-xs text-gray-500">{pendingPdfUpload.fileSize}</div>
                  </div>
                  <button
                    type="button"
                    onClick={handleCancelPdfUpload}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={newItemTitle}
                    onChange={(e) => setNewItemTitle(e.target.value)}
                    placeholder="e.g., Stability Report"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newItemDescription}
                    onChange={(e) => setNewItemDescription(e.target.value)}
                    placeholder="Brief description of the evidence..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleConfirmPdfUpload}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors text-sm"
                  >
                    {editingItemId ? 'Update Item' : 'Add Item'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelPdfUpload}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Box Link Tab */}
        {evidenceTab === 'boxLink' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                placeholder="e.g., Supplier Certificates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newItemDescription}
                onChange={(e) => setNewItemDescription(e.target.value)}
                placeholder="Brief description of the evidence..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Box Folder Link *</label>
              <input
                type="text"
                value={newItemBoxLink}
                onChange={(e) => { setNewItemBoxLink(e.target.value); setItemError(''); }}
                placeholder="https://app.box.com/folder/12345 or https://app.box.com/s/..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${itemError && evidenceTab === 'boxLink' ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleAddBoxLinkItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm"
              >
                {editingItemId ? 'Update Item' : 'Add Item'}
              </button>
              {editingItemId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}

        {/* Error message */}
        {itemError && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{itemError}</p>
            <button onClick={() => setItemError('')} className="ml-auto text-red-600 hover:text-red-800">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Unified Evidence Items List */}
        {additionalEvidenceItems.length > 0 && (
          <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FileText size={16} className="text-gray-600" />
              Evidence Items ({additionalEvidenceItems.length})
            </h4>
            {additionalEvidenceItems.map((item) => (
              <div
                key={item.id}
                className={`p-4 rounded-lg border transition-colors ${
                  item.type === 'pdf' ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
                } ${editingItemId === item.id ? 'ring-2 ring-offset-1' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded ${item.type === 'pdf' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {item.type === 'pdf' ? (
                        <FileText size={18} className="text-green-600" />
                      ) : (
                        <ExternalLink size={18} className="text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{item.title}</div>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.description}</p>
                      )}
                      {item.type === 'pdf' ? (
                        <div className="text-xs text-gray-500 mt-1">
                          {item.fileName} &bull; {item.fileSize}
                        </div>
                      ) : (
                        <a
                          href={item.boxLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1 break-all"
                        >
                          {item.boxLink} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleEditItem(item)}
                      className={`p-1.5 rounded transition-colors ${item.type === 'pdf' ? 'text-green-600 hover:bg-green-100' : 'text-blue-600 hover:bg-blue-100'}`}
                      title="Edit item"
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      onClick={() => removeEvidenceItem(item.id)}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Remove item"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions Section */}
      <div id="actions-section" className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
          <CheckSquare size={24} className="text-green-600" />
          Actions
        </h3>

        {/* SAP Manual Entry Confirmation */}
        <div className="mb-6">
          <label className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
            <input
              type="checkbox"
              checked={manualSAPEntry}
              onChange={(e) => setManualSAPEntry(e.target.checked)}
              className="mt-1 w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={18} className="text-amber-600" />
                <span className="font-semibold text-gray-900">I confirm manual entry of SAP data</span>
              </div>
              <p className="text-sm text-gray-700">
                Check this box to confirm that SAP data was manually entered because the system was unavailable. This confirmation is required before proceeding to review.
              </p>
            </div>
          </label>
        </div>

        {!isReadOnly && (
          <div className="grid grid-cols-2 gap-4">
            {/* Save Progress Button */}
            {ENABLE_SAVE_FUNCTIONALITY && (
              <button
                onClick={() => { void handleSave(); }}
                disabled={saveStatus === 'saving'}
                className={`py-3 font-medium rounded transition-all flex items-center justify-center gap-2 ${
                  saveStatus === 'saving'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {saveStatus === 'saving' ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FileText size={18} />
                    Save Progress
                  </>
                )}
              </button>
            )}

            <button
              onClick={async () => {
                // Block navigation during save
                blockNavigation?.('Saving Batch Record Review...');

                // Save before proceeding
                if (ENABLE_SAVE_FUNCTIONALITY) {
                  setSaveStatus('saving');
                  const saved = await handleSave();
                  if (saved) {
                    // Mark that button save completed to prevent redundant unmount save
                    buttonSaveCompletedRef.current = true;
                  }
                  if (!saved) {
                    unblockNavigation?.('Save failed. Please try again.');
                    const proceed = window.confirm('Failed to save. Do you want to proceed anyway?');
                    if (!proceed) return;
                  }
                }

                // Unblock navigation before proceeding
                unblockNavigation?.();

                // Call the onNext prop to navigate to next page
                onNext();
                // Scroll to top of page
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={!manualSAPEntry || saveStatus === 'saving' || isNavigationBlocked}
              className={`py-3 font-medium rounded transition-colors flex items-center justify-center gap-2 ${
                manualSAPEntry && saveStatus !== 'saving' && !isNavigationBlocked
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {(saveStatus === 'saving' || isNavigationBlocked) && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              {saveStatus !== 'saving' && !isNavigationBlocked && <CheckCircle size={18} />}
              {saveStatus === 'saving' ? 'Saving...' : isNavigationBlocked ? 'Please wait...' : 'Review prior to QP Submission'}
            </button>
          </div>
        )}

        {!isReadOnly && (
          <div className="flex justify-center mt-4">
            <SaveStatusIndicator isSaving={isSaving} lastSaved={lastSavedTime} />
          </div>
        )}

        {/* Delete BRR Button */}
        {!isReadOnly && onDeleteBRR && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <DeleteBRRButton onDelete={onDeleteBRR} />
          </div>
        )}
      </div>

      {/* Add Change Control Modal */}
      {showAddCCModal && (
        <AddCardModal
          cardType="changeControl"
          onAdd={(card) => setManuallyAddedCCs(prev => [...prev, card])}
          onClose={() => setShowAddCCModal(false)}
        />
      )}

      {/* Add Quality Event Modal */}
      {showAddQEModal && (
        <AddCardModal
          cardType="qualityEvent"
          onAdd={(card) => setManuallyAddedQEs(prev => [...prev, card])}
          onClose={() => setShowAddQEModal(false)}
        />
      )}

    </div>
  );
};

export const FormPage = React.memo(FormPageComponent);
